import {
    AfterViewInit,
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    HostListener,
    Input,
    NgZone,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    QueryList,
    SimpleChanges,
    ViewChild,
    ViewChildren,
} from '@angular/core';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { Router } from '@angular/router';
import { CalendarApi, CalendarOptions, DateSelectArg, DatesSetArg, EventApi, EventInput, getElSeg } from '@fullcalendar/core';
import { FullCalendarComponent } from '@fullcalendar/angular';
import { Draggable } from '@fullcalendar/interaction';
import { AsyncSubject, combineLatest, forkJoin, Observable, of, Subscription } from 'rxjs';
import { catchError, debounceTime, finalize, map, shareReplay, switchMap, timeout } from 'rxjs/operators';

import { ActionService } from '../../action.service';
import { JobAction, JobActionSearchQuery, JobActionTimePeriod } from '../../action.types';
import { LoginService } from '@intranet/modules/login';
import { MatDialog } from '@angular/material/dialog';
import { AlertDialog } from '@intranet/shared/alert/alert-dialog.component';
// Deep-imported, same reasoning as HolidayService below — just the service
// and types, not the users module's own routed components.
import { UserService } from '@intranet/modules/users/user.service';
import { User, UserGroup } from '@intranet/modules/users/user.types';
// Deep-imported (not via the '@intranet/modules/holidays' barrel) so this
// only pulls in the service + types, not holiday.module.ts's own routed
// components — the barrel re-exports HolidayModule too, which an
// unoptimized build bundles in whole rather than tree-shaking.
import { HolidayService } from '@intranet/modules/holidays/holiday.service';
import { Holiday, BankHoliday, HolidayType, NonWorkingDays } from '@intranet/modules/holidays/holiday.types';
import { PlannerColorSet, plannerColorForJobNumber } from './planner-color.util';
import { addDays, formatDurationHM, formatHours, formatTimeRange, getISOWeekNumber, getMondayOf, isSameDay, parseDurationHM } from './planner-date.util';
import { PlannedBlockService } from './planned-block.service';
import { PlannerCacheService } from './planner-cache.service';
import { PlannerLogRange, PlannerLogTimerowComponent } from './planner-log-timerow/planner-log-timerow.component';
import { PlannedActionInfo, PlannedBlockViewModel } from './planned-block.types';
// This one genuinely is shared, cross-feature infrastructure, not Timer
// calendar business logic — TimerToastComponent is mounted once at the app
// layout level (default.component.html/desktop-shell.component.html), and
// TimerToastService is a plain, dependency-free pipe to it designed for
// exactly this kind of reuse ("components that need to surface a toast
// inject this service... they don't need a direct reference to the mounted
// TimerToastComponent" — see its own doc comment). Reusing it here is what
// makes the Planner's delete-undo toast actually the same toast the user
// already sees elsewhere in the app, not a second, separately-styled one.
import { TimerToastService } from '@intranet/modules/timer/timer-toast/timer-toast.service';
// App-wide cross-window sync (core, not Timer's own code) — "Log as time"
// sends the same 'entries-changed' signal the Timer page does after
// creating an entry.
import { TimerSyncService } from '@intranet/core/services/timer-sync.service';

type PlannerViewMode = 'week' | '5days' | 'day';

// One person's section of the planner: whose it is, the name shown in its
// header (several-people view only), and its own calendar options (built
// once per person by buildCalendarOptions, so every callback knows whose
// calendar it came from).
interface PlannerPerson {
    userId: string;
    name: string;
    options: CalendarOptions | null;
}

// Whose a pull-out action is, among the people shown (several-people view).
interface PulloutWhose {
    owners: PlannerPerson[];
    waitingOn: PlannerPerson | null;
    teams: string[];
}

// A person's grey absence days (holidays, bank holidays, non-working days).
interface PersonAbsence {
    holidays: Holiday[];
    bankHolidays: BankHoliday[];
    nonWorkingDays: NonWorkingDays[];
}

// What kind of absence a day (or half day) is, as shown on the calendar.
// 'sick'/'other' only for people with the Calendar role — 'off' otherwise
// (see holidayKind).
type AbsenceKind = 'holiday' | 'sick' | 'other' | 'off' | 'bank' | 'nonworking';

// An action's blocks in some range: when the first one starts, and how many.
interface PlannedSummary {
    nextStart: Date;
    count: number;
}

// One job in the "Add planned block" action picker, with the actions that
// can be planned on it.
// Where "Add planned block" was opened from a click on the calendar.
interface AddBlockAt {
    start: Date;
    durationMinutes: number;
    clientX?: number;
    clientY?: number;
}

// One list in the pull-out's open tab — see pulloutSections.
interface PulloutSection {
    key: string;
    label: string;
    actions: JobAction[];
    reorderable: boolean;
}

interface AddBlockJobGroup {
    jobId: number;
    jobNumber: number | undefined;
    jobTitle: string;
    clientName: string;
    color: string;
    actions: JobAction[];
    // Shown because an action's own title matched the search (not the job's)
    // — those jobs open expanded so the match is visible.
    matchedByAction: boolean;
    // The first job of a section ("Your actions" / "Other people's
    // actions") carries its heading — see actionSectionGroups.
    sectionLabel?: string;
}

// The small padlock beside a past (locked) day's name in its header.
// The server's refusal when an action goes on someone else's planner but
// isn't theirs (PlannedBlockController.NotTheirAction) — shown as it is.
const NOT_THEIR_ACTION = "You can only plan an action on someone else's planner when it's theirs (they or their team own it, or it's waiting on them).";

const PLANNER_DAY_LOCK_ICON = '<svg class="Planner-dayLock" width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="4" y="11" width="16" height="10" rx="2"/><path d="M8 11V7a4 4 0 0 1 8 0v4"/></svg>';

@Component({
    selector: 'planner',
    templateUrl: './planner.component.html',
    styleUrls: ['./planner.component.scss'],
})
export class PlannerComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {
    // One <full-calendar> per person shown (see `people`) — one in the
    // usual one-person view, one per selected person when several are
    // picked in the Owner filter (stacked, spec "Multiple people"). Matched
    // to people by their data-user-id (see apiFor), not by position, since
    // a collapsed person's calendar isn't rendered at all.
    @ViewChildren('calendar') private _calendarList: QueryList<FullCalendarComponent>;
    @ViewChildren('calendar', { read: ElementRef }) private _calendarElList: QueryList<ElementRef<HTMLElement>>;
    /** The top person's calendar — the only one in the one-person view —
     *  used for everything all sections share: the week on screen, the
     *  toolbar label, zoom controls, scrolling. */
    get calendarComponent(): FullCalendarComponent | undefined { return this._calendarList?.first; }
    get calendarElRef(): ElementRef<HTMLElement> | undefined { return this._calendarElList?.first; }
    @ViewChild('pulloutList') pulloutListRef: ElementRef<HTMLElement>;
    @ViewChild('plannerBody') plannerBodyRef: ElementRef<HTMLElement>;
    // Positioning anchor for the context menu (position: absolute relative
    // to this) — see openContextMenu for why not position: fixed.
    @ViewChild('plannerRoot') plannerRootRef: ElementRef<HTMLElement>;
    // "Past days are locked" while dragging over one (updateLockedHover).
    @ViewChild('lockedNote') lockedNoteRef: ElementRef<HTMLElement>;

    // Every action the pull-out lists (both its Unplanned and Planned
    // groups — see pulloutGroups), in the user's own order.
    unplannedActions: JobAction[] = [];
    isLoadingUnplanned = false;

    // Pull-out's own When filter (spec: "with its own When filter, default:
    // Today") — same options and meaning as the Actions page's When filter,
    // applied by the same server-side search.
    pulloutWhen: JobActionTimePeriod = JobActionTimePeriod.Today;
    readonly pulloutWhenOptions = [
        { value: JobActionTimePeriod.Today, label: 'Today' },
        { value: JobActionTimePeriod.TodaysDeadlines, label: "Today's deadlines" },
        { value: JobActionTimePeriod.Tomorrow, label: 'Tomorrow' },
        { value: JobActionTimePeriod.TodayTomorrow, label: 'Today & Tomorrow' },
        { value: JobActionTimePeriod.Week, label: 'This week' },
        { value: JobActionTimePeriod.NextWeek, label: 'Next week' },
        { value: JobActionTimePeriod.All, label: 'All' },
    ];
    // The pull-out's two tabs (spec: "split into Unplanned and Planned
    // groups"), and what the When filter means for each:
    //  - Unplanned: actions SCHEDULED in the When period (the Actions page's
    //    own When filter) with no block from today on, on anyone's planner
    //    (the spec's own definition of Unplanned).
    //  - Planned: actions with a BLOCK in the When period, whatever their
    //    scheduled date — e.g. When: Next week lists what's planned next
    //    week. "All" = any block from today on; "Today's deadlines" = the
    //    deadline-today actions that have a block from today on.
    pulloutTab: 'unplanned' | 'planned' = 'unplanned';
    readonly pulloutTabs: { key: 'unplanned' | 'planned'; label: string; empty: string }[] = [
        { key: 'unplanned', label: 'Unplanned', empty: 'Nothing left to plan for this period.' },
        { key: 'planned', label: 'Planned', empty: 'Nothing planned for this period.' },
    ];
    // Actions the When search returned (Unplanned candidates).
    private _pulloutWhenIds = new Set<number>();
    // Action id → its blocks from today on (any planner).
    private _plannedFromToday = new Map<number, PlannedSummary>();
    // Action id → its blocks in the When period.
    private _plannedInPeriod = new Map<number, PlannedSummary>();
    private _plannedInfoVersion = 0;
    private _plannedInfoToken = 0;
    private _pulloutToken = 0;
    // The pull-out search on its way, if any (see loadUnplannedActions).
    private _pulloutSearchInFlight: string | null = null;
    // The planned check (refreshPlannedInfo) has replied for the list shown —
    // until then the Planned tab shows the spinner instead of its list.
    plannedInfoReady = false;
    // The All search (Planned tab only) still on its way — see
    // loadUnplannedActions.
    pulloutAllPending = false;

    /** A tab's count badge. While the list (or, for Planned, its planned
     *  check) is reloading, the last count stays showing until the new one
     *  is ready — the badge vanishing and coming back changed the tab's
     *  width, so its label jumped. Null only before there's ever been one
     *  (the first load), when no badge shows. */
    pulloutTabCount(key: 'unplanned' | 'planned'): number | null {
        const ready = !this.isLoadingUnplanned && (key === 'unplanned' || this.plannedTabReady);
        if (ready) this._lastTabCounts[key] = this.pulloutGroups[key].length;
        return this._lastTabCounts[key];
    }
    private _lastTabCounts: { unplanned: number | null; planned: number | null } = { unplanned: null, planned: null };

    /** The Planned tab has what it needs to show its list. */
    get plannedTabReady(): boolean {
        return this.plannedInfoReady && !this.pulloutAllPending;
    }
    private _pulloutGroupsMemo: { actions: JobAction[]; version: number; groups: { unplanned: JobAction[]; planned: JobAction[] } } | null = null;

    // Two-way bindable ([(pulloutOpen)]) so the "Unscheduled" toggle can
    // live in the Actions page's own toolbar (to the left of the view
    // icons) instead of this component's — a template reference variable
    // on <planner> can't reach there since it's declared inside a *ngIf's
    // embedded view.
    @Input() pulloutOpen = true;
    // The page's own When filter (its search bar — the Actions page) is the
    // pull-out's When too, rather than a second, identical dropdown in the
    // panel (spec: the page's filters carry over to the pull-out). Off where
    // there's no search bar (e.g. a job's page): the panel's own When then.
    @Input() followPageWhen = false;
    // A job's own page (no search bar, so no Owner filter): the planner picks
    // its own people — this job's (jobPeopleDefault) to start with, changed
    // with the people picker beside the view dropdown. Only affects this
    // page's planner. Off everywhere else (people come from the Owner filter).
    @Input() jobPeoplePicker = false;
    @Output() pulloutOpenChange = new EventEmitter<boolean>();
    @Output() unplannedCountChange = new EventEmitter<number>();
    // Lets the host page show its own existing loading indicator for this
    // instead of Planner having its own separate one — see setBlocksLoading.
    @Output() loadingChange = new EventEmitter<boolean>();
    // "Edit action" (right-click menu, a block's ⋮, a panel card's ⋮): the
    // page opens its own edit-action popup — the same one the List and
    // Board open — then calls refreshAfterActionEdit.
    // propertyKey: the dialog's section to open on (e.g. 'user' — its
    // owners, from a panel card's avatars), as the Actions list does.
    @Output() editAction = new EventEmitter<{ jobActionId: number; propertyKey?: string }>();

    weekLabel = '';
    weekNumber = 0;
    selectedWeekStart: Date;

    // Right-click context menu on a placed block — same pattern as the
    // Timer calendar's own: Duplicate, Go to job, Delete (see
    // contextDuplicate/contextGoToJob/contextDelete). Non-null while open; read by the template for
    // position and by the outside-click handler below to close it.
    // `ready`: placed using its real, drawn size (fitContextMenu) — hidden
    // until then, so it never shows for a moment in the wrong place.
    contextMenu: { plannedBlockId: number; top: number; left: number; clientX: number; clientY: number; ready: boolean } | null = null;

    // Left-click edit popover on a placed block — same look and pattern as
    // the Timer calendar's own event modal (timer-tracker__modal),
    // duplicated rather than shared, same as the context menu above.
    // Non-null while open. Only start time and duration are editable, both
    // in 30-minute steps (see the BLOCK_STEP_MINUTES helpers below).
    blockModal: {
        plannedBlockId: number;
        // Whose planner the block is on (several-people view: anyone's).
        userId: string;
        top: number;
        left: number;
        // The action shown — changed via the action picker (the project
        // pill), saved with Save. originalJobActionId is what it opened with.
        jobActionId: number;
        originalJobActionId: number;
        actionTitle: string;
        jobId: number | null;
        jobNumber: number | null;
        jobTitle: string;
        clientName: string;
        titleColor: string;
        // The action's job is billable — the £ beside the project pill.
        isBillable: boolean;
        // Its action has since been deleted — shown from what was saved then.
        isActionDeleted: boolean;
        // Busy time, not an action: its name box is the block's own title.
        isBusy: boolean;
        // Already turned into a time entry — "Log as time" is then disabled.
        isLogged: boolean;
        // The block's day (midnight) — editing never moves it to another day.
        day: Date;
        // As loaded, to tell whether anything changed.
        originalStartMinutes: number;
        originalDurationMinutes: number;
    } | null = null;
    blockModalShowDropdown = false;
    // The action's name as edited in the popover (blockModalTitleOriginal is
    // what it opened with). Renames the action itself on Save, like editing
    // the name in the Actions list — see renameAction.
    blockModalTitleText = '';
    blockModalTitleOriginal = '';
    // The project pill's action picker — the same picker as "Add planned
    // block", listing the actions in the pull-out for the block's person.
    blockModalPickerOpen = false;
    blockModalPickerSearch = '';
    blockModalPickerExpandedJobIds = new Set<number>();
    private _blockModalGroupsMemo: { actions: JobAction[]; term: string; groups: AddBlockJobGroup[] } | null = null;
    private _blockModalChoicesMemo: { actions: JobAction[]; version: number; userId: string; people: PlannerPerson[]; sections: { own: JobAction[]; others: JobAction[]; all: JobAction[] } } | null = null;
    // The block's day as edited in the popover (blockModal.day is the day it
    // opened with) — changed via the date link beside the start time.
    blockModalDay = new Date();
    blockModalDayPickerOpen = false;
    blockModalDayPickerUpward = false;
    // The "Discard unsaved changes?" confirmation — see requestCloseBlockModal.
    blockModalDiscardOpen = false;
    // Minutes since midnight / length in minutes — always 30-min multiples.
    blockModalStartMinutes = 0;
    blockModalDurationMinutes = 60;
    // What's in the two inputs while typing; committed (parsed + rounded to
    // 30 mins) on blur/Enter, or reset if it can't be parsed.
    blockModalStartText = '';
    blockModalDurationText = '';
    blockModalSaving = false;

    // "Log as time" form inside the popover (spec: a timesheet entry
    // pre-filled from the block, editable before saving). Unlike the
    // block's own times, these are free (minutes and seconds) — logged
    // time doesn't have to follow the plan's 30-minute grid. Edited via
    // <planner-log-timerow>, the Timer-style time row.
    @ViewChild(PlannerLogTimerowComponent) logTimerow?: PlannerLogTimerowComponent;
    logFormOpen = false;
    logDescription = '';
    logStart = new Date();
    logEnd = new Date();
    logError = '';
    logSaving = false;
    // As pre-filled, to tell whether the form has been edited.
    private _logFormInitial = '';

    // "Add planned block" popover (spec: "Add — adds a block manually"),
    // opened from the + beside the week navigation. Same block rules as
    // everywhere else: one action, the person whose planner is showing, a
    // day, a start and a duration — both in 30-minute steps.
    addBlockOpen = false;
    // Whose planner the new block goes on — see toggleAddBlock.
    addBlockUserId: string | null = null;
    addBlockTop = 0;
    addBlockLeft = 0;
    // "Discard unsaved changes?" for this popover — see requestCloseAddBlock.
    addBlockDiscardOpen = false;
    // Day / start / duration as it opened, to tell whether anything changed.
    private _addBlockInitial: { day: string; start: number; duration: number } | null = null;
    addBlockSearch = '';
    addBlockActionId: number | null = null;
    // Busy time instead of an action (the popover's Action | Busy switch) —
    // a block with just a title (optional: shown as "Busy"), for a phone
    // call, a meeting… One-click suggestions fill the title. What the
    // popover opens on (openAddBlock).
    addBlockBusy = true;
    addBlockBusyTitle = '';
    readonly busySuggestions = ['Phone call', 'Meeting', 'Admin', 'Travel'];
    // The action picker — same look and behaviour as the Timer header's job
    // picker (thn__dropdown--picker): jobs with a ⌄ to show their actions.
    addBlockPickerOpen = false;
    addBlockExpandedJobIds = new Set<number>();
    private _addBlockGroupsMemo: { actions: JobAction[]; term: string; groups: AddBlockJobGroup[] } | null = null;
    addBlockDay = new Date();
    addBlockDayPickerOpen = false;
    addBlockDayPickerUpward = false;
    addBlockStartMinutes = 9 * 60;
    addBlockDurationMinutes = 60;
    // START is two segments (HH : MM) like the Timer's inline time editor;
    // DURATION is click-to-edit text, also like it.
    addBlockStartHText = '09';
    addBlockStartMText = '00';
    addBlockDurationText = '1h';
    addBlockEditingDuration = false;
    addBlockError = '';
    addBlockSaving = false;

    // 'today' | 'yesterday' | 'thisWeek' | 'lastWeek' | '' — see
    // planner-nav-picker.component.ts for how this drives its active state.
    selectedQuickOption = '';
    showNavPicker = false;

    // Default: half-hour rows (zoomLevels[1]).
    zoomLevel = 1;
    // Each level pairs slotDuration with the matching slotLabelInterval —
    // changing one without the other is what left the hour labels on the
    // left frozen in place (a separate bug from the remount one below).
    // The rows' height comes from the space on screen (applySlotHeight):
    // `hours` is how many hours that space shows — 08:00–18:00 (10) at
    // every level where the screen allows, a little more zoomed right out —
    // within each level's min/max row height (px).
    readonly zoomLevels = [
        { slot: '01:00:00', label: '01:00:00', slotsPerHour: 1, hours: 14, min: 24, max: 64 },
        { slot: '00:30:00', label: '01:00:00', slotsPerHour: 2, hours: 10, min: 18, max: 40 },
        { slot: '00:15:00', label: '00:30:00', slotsPerHour: 4, hours: 10, min: 12, max: 30 },
    ];
    calendarVisible = true;

    /** Zoomed right out, in every view (Week, 5 days, Day): the whole day
     *  (00:00–24:00) fills the space on screen, rather than leaving it
     *  empty below the last hour, and its hours are labelled every 2. */
    private get fitsWholeDay(): boolean {
        return this.zoomLevel === 0;
    }

    // The rows are sized to the whole day right now (fitsWholeDay, once
    // the calendar's been drawn and measured — set by applySlotHeight).
    // The Actions panel then stops at the calendar's foot
    // (updatePulloutOffset).
    private _wholeDay = false;

    // ─── People shown ───────────────────────────────────────────────────
    // Spec "Multiple people: stacked one below the other, page scrolls.
    // Each person is collapsible". One person (the usual view) renders
    // exactly as before: full height, page scroll, sticky day header. With
    // several, each gets a compact section (header + its own calendar,
    // scrolling inside itself, opening on 08:00–18:00 — the spec's working
    // hours) so everyone can be scanned without a full day's height each.
    // The logged-in user comes first when they're included.
    people: PlannerPerson[] = [];
    // Week (the usual view), 5 days (Mon–Fri — the week without weekends,
    // same as the Timer's "5 days view") or Day. Day with several people
    // lays them out side by side, one column each, so their days can be
    // compared at a glance (see isDayColumns). Remembered per browser, like
    // the zoom. Picked from the same "Week view ▾" dropdown as the Timer's.
    viewMode: PlannerViewMode = 'week';
    viewMenuOpen = false;
    readonly viewModeOptions: { value: PlannerViewMode; label: string }[] = [
        { value: 'week', label: 'Week view' },
        { value: '5days', label: '5 days view' },
        { value: 'day', label: 'Day view' },
    ];
    private static readonly VIEW_MODE_KEY = 'planner_view_mode';
    // Day columns: the shared scroll box has been scrolled to working hours.
    private _columnsScrolled = false;
    // Collapsed sections (several-people view) — kept for the session.
    collapsedPeople = new Set<string>();
    private _allUsers: User[] = [];
    // Everyone who owns actions (for the "All" view) — null until loaded.
    private _actionOwnerIds: string[] | null = null;
    // The week on screen, so a section added later (e.g. another person
    // ticked) opens on the same week as the rest.
    private _currentDate: Date | null = null;
    // Each person's grey absence days, and their planned hours per day
    // (day header totals) — keyed by user id.
    private _absenceByUser = new Map<string, PersonAbsence>();
    private _draftTotalsByUser = new Map<string, Record<string, number>>();
    private draggable: Draggable;
    private subscription = new Subscription();
    private _resizeListener: (() => void) | null = null;
    private _bodyResizeObserver: ResizeObserver | null = null;
    private _bodyResizeFrame: number | null = null;
    // The Actions page's own search bar (client/owner/type/job type/search) —
    // kept in sync via ActionService.filterQuery$ (see ngOnInit) so the
    // Planner reacts to it the same way the list/board views already do.
    private _filterQuery: JobActionSearchQuery | null = null;
    // Bumped on every filter change so a stale holiday/unplanned-actions
    // response that resolves after a newer one (e.g. the Owner filter
    // switched twice in quick succession) gets ignored instead of
    // overwriting fresher data.
    private _requestToken = 0;
    // Set in ngOnDestroy and checked by every async callback below before it
    // touches `loadingChange`/the calendar/an alert. Switching away from
    // Planner destroys this component outright (*ngIf in
    // action-listing.component.html) - any request still in flight at that
    // moment (e.g. the blocks fetch a filter change just kicked off) still
    // resolves afterwards, and without this guard its callback would still
    // run: emitting loadingChange(true) after the host's own reset left the
    // shared loading indicator stuck on with nothing left to ever turn it
    // back off, or popping an alert() for a view the user already left.
    private _destroyed = false;
    // The times of the block being resized, as they were before — what its
    // resize copy shows (see onEventDidMount).
    private _resizingFrom: { start: Date | null; end: Date | null } | null = null;
    // Where the block being dragged started — its drag copy keeps the
    // block's own deadline border (updateDeadlineFlag) whichever day it's
    // over, rather than switching it on and off as it moves.
    private _draggingFrom: Date | null = null;
    // Block ranges already checked against the server during this visit —
    // see the `events` callback. Per component instance on purpose, so
    // every return to the Planner re-checks each range once (quietly).
    private _revalidatedBlockKeys = new Set<string>();
    // In-flight block requests, so FullCalendar asking for the same range
    // again before the first reply lands (e.g. holidays arriving
    // mid-fetch) shares that request rather than sending a duplicate.
    private _pendingBlockFetches = new Map<string, Observable<PlannedBlockViewModel[]>>();

    // ─── Calendar options — one set per person (see `people`) ────────────
    // The same settings for every person's calendar; the callbacks are
    // bound to that person (`userId`), so a fetch, a drop, a move or a
    // click always knows whose planner it happened on. In the one-person
    // view this is exactly the single calendar the Planner always had.
    private buildCalendarOptions(userId: string): CalendarOptions {
        const level = this.zoomLevels[this.zoomLevel];
        return {
            initialView: this.viewMode === 'day' ? 'timeGridDay' : 'timeGridWeek',
            // 5 days view: the week grid without Saturday/Sunday — same as
            // the Timer's own "5 days view".
            weekends: this.viewMode !== '5days',
            // A section added later (another person ticked) opens on the
            // week already on screen.
            initialDate: this._currentDate ?? undefined,
            headerToolbar: false,
            // Only on planners this user may change (canEditPlannerOf): their
            // own, or anyone's with the Planner role. Everyone else's are
            // view-only — no dragging, resizing, dropping or click-to-add.
            editable: this.canEditPlannerOf(userId),
            droppable: this.canEditPlannerOf(userId),
            // Click (or drag down) on an empty part of the day to add a
            // block there — like the Timer calendar's own click-to-add. Opens
            // the "Add planned block" popover at that time; nothing is saved
            // until an action is chosen and Add pressed (onCalendarSelect).
            // One day at a time; 30-minute steps (snapDuration).
            selectable: this.canEditPlannerOf(userId),
            selectMirror: false,
            // One day at a time, and not in the past (blocks are only
            // added for today or later).
            selectAllow: (span) => isSameDay(span.start, new Date(span.end.getTime() - 1))
                && span.start >= this.addBlockMinDay,
            select: (info) => this._ngZone.run(() => this.onCalendarSelect(info, userId)),
            // Past days are locked: nothing moved or dropped onto one — a
            // block dragged there, or an action from the panel. Nor, on
            // someone else's planner, an action that isn't theirs
            // (canDropEventOn).
            // Over the panel (dropping a block there removes it): never a
            // day behind it either — otherwise FullCalendar kept switching
            // between drawing the block in that day (hidden by the panel)
            // and as the copy following the pointer, so it flashed.
            eventAllow: (span, movingEvent) => !this.isPastDay(span.start) && this.canDropEventOn(movingEvent, userId)
                && !this.isPointerOverPanel(),
            eventResizableFromStart: true,
            slotMinTime: '00:00:00',
            slotMaxTime: '24:00:00',
            scrollTime: '08:00:00',
            slotDuration: level.slot,
            // The whole day on screen: a time every 2 hours (fitsWholeDay).
            slotLabelInterval: this.fitsWholeDay ? '02:00:00' : level.label,
            snapDuration: '00:30:00',
            slotLabelFormat: { hour: '2-digit', minute: '2-digit', hour12: false },
            eventTimeFormat: { hour: '2-digit', minute: '2-digit', hour12: false },
            eventMinHeight: 0,
            // Matches the Timer calendar — it hides FC's auto time label too,
            // relying on its own content instead of a redundant "09:00 - 10:30"
            // line above the title.
            displayEventTime: false,
            allDaySlot: false,
            nowIndicator: true,
            slotEventOverlap: true,
            eventOverlap: true,
            // Always the full day at its natural height, as the one-person
            // view has always been: FullCalendar's fixed-height mode lays its
            // grid out with table heights the site's own table styles break
            // (the grid came out blank). With several people, everyone's
            // calendars scroll together in one area instead
            // (.Planner-peopleList--scroll), opened on 08:00
            // (ensureCompactScrolled).
            height: 'auto',
            expandRows: true,
            // The day header sticks: under the page's search bar (one
            // person), or under the person's own header in the people area
            // (several) — see updateStickyHeaderOffset.
            stickyHeaderDates: true,
            firstDay: 1,
            events: (fetchInfo, successCallback) => this.fetchCalendarEvents(userId, fetchInfo, successCallback),
            dayHeaderContent: (arg) => this.renderDayHeader(arg.date, userId),
            datesSet: (arg) => this.onDatesSet(arg, userId),
            eventReceive: (info) => this.onEventReceive(info, userId),
            // While a block is being dragged, planners it can't go on say
            // so (see dragInProgress).
            // A block dragged onto the panel is removed (onBlockDragStop).
            eventDragStart: (info) => this._ngZone.run(() => {
                const props = info.event.extendedProps || {};
                this._draggedBlock = { plannedBlockId: props['plannedBlockId'] as number, saving: !!props['isSaving'] };
                this._dragAction = { jobActionId: (props['jobActionId'] as number) || 0, fromUserId: userId, isBusy: !!props['isBusy'] };
                this._releasedOutside = false;
                this._draggingFrom = info.event.start;
                this.setDragInProgress(true);
            }),
            eventDragStop: (info) => this._ngZone.run(() => {
                this._draggingFrom = null;
                this.onBlockDragStop(info.jsEvent);
                this.setDragInProgress(false);
            }),
            // Deleting a block: right-click (see contextDelete) or the edit
            // popover's ⋮ menu — same as the Timer calendar.
            eventDrop: (info) => this.persistBlockMove(info, userId),
            eventResize: (info) => this.persistBlockMove(info, userId),
            // While a block is being resized, its footer keeps the times it
            // had before (see onEventDidMount) — updated once it's dropped.
            eventResizeStart: (info) => {
                // (Only a moved block can be let go outside the planner.)
                this._releasedOutside = false;
                this._resizingFrom = { start: info.event.start, end: info.event.end };
            },
            eventResizeStop: () => {
                this._resizingFrom = null;
            },
            // Left-click opens the block's edit popover — see openBlockModal.
            // FullCalendar doesn't fire this after a drag/resize, only a plain
            // click, so it never fights those.
            eventClick: (info) => {
                // Someone else's planner without the Planner role: its blocks
                // don't respond at all (see canEditPlannerOf).
                if (!this.canEditPlannerOf(userId)) return;
                const props = info.event.extendedProps || {};
                const plannedBlockId = props['plannedBlockId'] as number | undefined;
                // Not persisted yet (just dropped, still saving) — nothing to edit.
                if (!plannedBlockId || props['isSaving']) return;
                info.jsEvent.preventDefault();
                this._ngZone.run(() =>
                    this.openBlockModal(plannedBlockId, info.jsEvent.clientX, info.jsEvent.clientY)
                );
            },
            eventsSet: (events) => {
                this.recomputeDraftTotals(userId, events);
                this.scheduleHighlightSweep();
                this.scheduleOverflowBadges();
            },
            eventClassNames: (arg) => (this.isOnDeadlineDay(arg.event, arg.isMirror) ? ['Planner-event--deadline'] : []),
            eventDidMount: (info) => this.onEventDidMount(info),
        };
    }

    // A person's blocks for the range FullCalendar asks for, plus their grey
    // absence days.
    private fetchCalendarEvents(
        userId: string,
        fetchInfo: { start: Date; end: Date },
        successCallback: (events: EventInput[]) => void
    ): void {
        const bg = this.backgroundEventsForRange(fetchInfo.start, fetchInfo.end, userId);

        // Already loaded (this visit or a previous one — see
        // PlannerCacheService): shown straight away, no loading
        // indicator, no request. Also what makes the re-renders that
        // holiday/unscheduled-list loads trigger (refetchEvents) free.
        // Checked against the server quietly, once per range per
        // visit, in case anything changed meanwhile.
        const key = this._plannerCache.blocksKey(userId, fetchInfo.start, fetchInfo.end);
        const cached = this._plannerCache.getBlocks(key);
        if (cached) {
            // Blocks remembered, but this person's grey days have never
            // loaded and are on their way (e.g. someone just added to the
            // view)? Draw both together once those arrive — same as below.
            const pendingAbsences = !this._plannerCache.getHolidays(userId) ? this._absenceLoads.get(userId) : undefined;
            if (pendingAbsences) {
                this._awaitingAbsences.add(userId);
                pendingAbsences.subscribe(() => {
                    this._awaitingAbsences.delete(userId);
                    if (this._destroyed) return;
                    const background = this.backgroundEventsForRange(fetchInfo.start, fetchInfo.end, userId);
                    successCallback([...background, ...cached.map((b) => this.toCalendarEvent(b))]);
                });
            } else {
                successCallback([...bg, ...cached.map((b) => this.toCalendarEvent(b))]);
            }
            if (!this._revalidatedBlockKeys.has(key)) {
                this._revalidatedBlockKeys.add(key);
                this.revalidateBlocks(userId, fetchInfo.start, fetchInfo.end, key, cached);
            }
            return;
        }
        this._revalidatedBlockKeys.add(key);

        // Fires on every real fetch FullCalendar makes (first visit to
        // a week, or after a change cleared it from the cache) - lets
        // the host page's own loading indicator cover this instead of
        // Planner showing a second, separate one.
        this.trackLoading(1);
        // The person's grey days still on their way (first visit)? Wait for
        // them too, so the calendar draws its blocks and absences together
        // rather than the absences popping in afterwards. They're asked for
        // at the same moment as the blocks, so this rarely adds any wait.
        const absences = this._absenceLoads.get(userId);
        if (absences) this._awaitingAbsences.add(userId);
        forkJoin([
            this.fetchBlocks(userId, fetchInfo.start, fetchInfo.end, key),
            absences ?? of(undefined),
        ]).subscribe({
            next: ([blocks]) => {
                this._awaitingAbsences.delete(userId);
                if (this._destroyed) return;
                this.trackLoading(-1);
                // Worked out now, with the absences that have just arrived.
                const background = this.backgroundEventsForRange(fetchInfo.start, fetchInfo.end, userId);
                successCallback([...background, ...blocks.map((b) => this.toCalendarEvent(b))]);
            },
            // Keep the background (holiday) events showing even if the
            // planned-block fetch fails, rather than blanking the whole grid.
            error: () => {
                this._awaitingAbsences.delete(userId);
                if (this._destroyed) return;
                this.trackLoading(-1);
                successCallback(this.backgroundEventsForRange(fetchInfo.start, fetchInfo.end, userId));
            },
        });
    }

    // Several people's calendars can be loading at once — the host page's
    // loading indicator (one on/off flag) stays on until the last finishes.
    private _loadingCount = 0;
    private trackLoading(delta: 1 | -1): void {
        const wasLoading = this._loadingCount > 0;
        this._loadingCount = Math.max(0, this._loadingCount + delta);
        const isLoading = this._loadingCount > 0;
        if (isLoading !== wasLoading) this.loadingChange.emit(isLoading);
    }

    private onDatesSet(arg: DatesSetArg, userId: string): void {
        this._currentDate = arg.view.currentStart;
        // Several-people view: open each compact section on working hours
        // (once per calendar — see ensureCompactScrolled).
        if (this.usesScrollArea) setTimeout(() => this.ensureCompactScrolled(), 0);
        // Shared toolbar/zoom/sticky work — once, from the top calendar on
        // screen (every section always shows the same week). Worked out from
        // `people`, not the rendered calendars, as this also runs while the
        // very first calendar is still being created.
        const firstShown = this.people.find((p) => !this.isPersonCollapsed(p.userId))?.userId;
        if (userId === firstShown) {
            this.updateToolbarLabel(arg.start);
            this.updateStickyHeaderOffset();
        }
        // Every calendar has its own zoom buttons (see injectZoomControls).
        this.injectZoomControls();
        // The block the menu/popover refers to may have moved
        // off-screen or not exist in the new range at all.
        this.closeContextMenu();
        this.closeBlockModal();
    }

    // Dropped onto a person's calendar: an action from the pull-out (a new
    // block, placed straight away and saved — removed again if the save
    // fails), or, in the several-people view, a block dragged across from
    // someone else's calendar (moved to this person, keeping its time).
    private onEventReceive(info: { event: EventApi }, userId: string): void {
        const jobActionId = info.event.extendedProps?.['jobActionId'];
        const start = info.event.start;
        const end = info.event.end;
        const movedBlockId = info.event.extendedProps?.['plannedBlockId'] as number | undefined;
        // (A moved block whose action has since been deleted has no action —
        // jobActionId 0 — and keeps it that way; see the server's update.)
        if ((!jobActionId && !movedBlockId) || !userId || !start) {
            info.event.remove();
            return;
        }
        // Not a planner this user may change (calendars that aren't take no
        // drops anyway — this is a safety net; the server checks too).
        const isBusy = !!info.event.extendedProps?.['isBusy'];
        // (A block from another planner let go outside the planner too.)
        if (!this.canEditPlannerOf(userId) || (!isBusy && !this.canPlanActionOn(jobActionId, userId))
            || (movedBlockId && this._releasedOutside)) {
            if (movedBlockId) this.refetchAllCalendars();
            info.event.remove();
            return;
        }
        const durationMinutes = end ? Math.round((end.getTime() - start.getTime()) / 60000) : 60;

        info.event.setExtendedProp('isSaving', true);
        // Every cached copy, not just this person's — a block moved across
        // also leaves the other person's planner.
        this._plannerCache.invalidateAllBlocks(false);
        const save$ = movedBlockId
            ? this._plannedBlockService.updatePlannedBlock(movedBlockId, { jobActionId, userId, start, durationMinutes })
            : this._plannedBlockService.createPlannedBlock({ jobActionId, userId, start, durationMinutes });
        save$.subscribe({
            next: (block) => {
                // Before the _destroyed check — the cache outlives
                // this component, see PlannerCacheService.
                this._plannerCache.invalidateAllBlocks(false);
                if (this._destroyed) return;
                // Swapped for a copy that belongs to the calendar's
                // own event source (addEvent's `true`), rather than
                // just marking the dropped one as saved. A dropped
                // event belongs to no source, so every later
                // refetchEvents (week navigation, duplicate, …)
                // left it in place AND added the same block again
                // from the server — showing it twice.
                info.event.remove();
                this.apiFor(userId)?.addEvent(this.toCalendarEvent(block), true);
            },
            error: (err) => {
                this._plannerCache.invalidateAllBlocks(false);
                if (this._destroyed) return;
                info.event.remove();
                // A failed move: put everyone's calendars back as they are
                // on the server (the block is still with its first person).
                if (movedBlockId) this.refetchAllCalendars();
                alert(this.saveErrorMessage(err, movedBlockId
                    ? 'Could not move this planned block. Please try again.'
                    : 'Could not schedule this action. Please try again.'));
            },
        });
    }

    // Same three-tier content as the Timer calendar's own events
    // (fc-event-description/fc-event-title/fc-event-client, plus a
    // fc-event-footer duration) — built up here the same way Timer's
    // handleEventDidMount does, rather than relying on FC's own title
    // rendering alone.
    private onEventDidMount(info: { event: EventApi; el: HTMLElement; isMirror?: boolean; isDragging?: boolean }): void {
        const props = info.event.extendedProps || {};
        // Shaded absence (see backgroundEventsForRange): what it is on hover.
        if (props['isAbsence']) {
            info.el.title = (props['label'] as string) || '';
            return;
        }
        // Highlighted from the Planned tab (see toggleHighlight).
        if (this.highlightActionId != null && props['jobActionId'] === this.highlightActionId) {
            info.el.setAttribute('data-hl', '');
        }
        const titleColor = props['titleColor'] as string | undefined;

        const main = info.el.querySelector('.fc-event-main');
        if (main) {
            // The action's own name — always available, whichever way
            // the block was created.
            const actionTitle = (props['actionTitle'] as string) || '';
            if (actionTitle) {
                const descEl = document.createElement('div');
                descEl.className = 'fc-event-description';
                descEl.textContent = actionTitle;
                // Kept after its action was deleted (spec, Action Status
                // Changes) — says so, beside the name it was saved with.
                if (props['isActionDeleted']) {
                    const deletedEl = document.createElement('span');
                    deletedEl.className = 'fc-event-deleted';
                    deletedEl.textContent = ' (deleted)';
                    descEl.appendChild(deletedEl);
                }
                main.insertBefore(descEl, main.firstChild);
            }

            // FC's own title (set to the job number + job title where
            // that's known — see toCalendarEvent/setupPulloutDraggable).
            // Hidden rather than left as an empty line when there isn't
            // one, same fallback Timer uses when it has no job info either.
            const titleEl = main.querySelector('.fc-event-title') as HTMLElement | null;
            if (titleEl && !info.event.title) {
                titleEl.style.display = 'none';
            }
            // Setting colour on the root .fc-event isn't enough — our
            // own CSS explicitly sets `.fc-event-title { color: … }` as
            // a fallback (same as Timer's), and an explicit rule on a
            // descendant always beats an inherited value from a
            // parent's inline style.
            if (titleEl && titleColor) {
                titleEl.style.color = titleColor;
            }

            const clientName = props['clientName'] as string | undefined;
            if (titleEl && clientName) {
                const clientDiv = document.createElement('div');
                clientDiv.className = 'fc-event-client';
                clientDiv.textContent = clientName;
                titleEl.parentNode?.insertBefore(clientDiv, titleEl.nextSibling);
            }

            // The copy FullCalendar draws while a block is being resized is
            // mounted one step into the resize and its footer never updates
            // after that — so it shows the block's times from before the
            // resize instead, rather than a length that's neither.
            const resizingFrom = info.isMirror ? this._resizingFrom : null;
            const start = resizingFrom ? resizingFrom.start : info.event.start;
            const end = resizingFrom ? resizingFrom.end : info.event.end;
            if (start && end) {
                const minutes = Math.round((end.getTime() - start.getTime()) / 60000);
                const footer = document.createElement('div');
                footer.className = 'fc-event-footer';
                // Its time on its own line ("09:00 – 12:30"), the length
                // under it, as before.
                footer.innerHTML = `<span class="fc-event-footer__time">${formatTimeRange(start, end)}</span>`
                    + `<span class="fc-event-footer__duration">${formatDurationHM(minutes)}</span>`;
                main.appendChild(footer);
            }

            // The drag copy: as the block was (see _draggingFrom).
            const dayFrom = info.isMirror && this._draggingFrom ? this._draggingFrom : start;
            this.updateDeadlineFlag(info.el, dayFrom, props['deadlineDate'] as string | null);
        }

        // Copying to other days: a handle on each side, dragged sideways
        // (startCopyDrag). Shown on hover, and only where the block can be
        // moved — not on past days or view-only planners, nor in the Day
        // view (see the stylesheet). The block being moved shows them too,
        // as it does the resize handles (just for looks there).
        if (!info.isMirror || info.isDragging) {
            (['start', 'end'] as const).forEach((side) => {
                const handle = document.createElement('div');
                handle.className = `Planner-copyHandle Planner-copyHandle--${side}`;
                if (!info.isMirror) {
                    handle.title = side === 'end' ? 'Drag right to copy to the following days' : 'Drag left to copy to the days before';
                    handle.addEventListener('mousedown', (e) => this.startCopyDrag(e, info.el, side));
                    // Never the block's own click (its popover).
                    handle.addEventListener('click', (e) => e.stopPropagation());
                }
                info.el.appendChild(handle);
            });
        }

        if (props['isLogged']) {
            this.applyLoggedBadge(info.el);
        }

        // Same mechanism as the Timer calendar: the slightly-more-
        // saturated hoverBg is read by a CSS rule on `:hover` via these
        // custom properties, rather than hard-coding a hover colour.
        // Its normal colour too, so a view-only planner's blocks can keep it
        // on hover (.PlannerPerson-calendar--readonly).
        if (info.event.backgroundColor) {
            info.el.style.setProperty('--event-bg', info.event.backgroundColor);
        }
        const hoverBg = (props['hoverBg'] as string) || info.event.backgroundColor;
        if (hoverBg) {
            info.el.style.setProperty('--event-hover-bg', hoverBg);
            info.el.style.setProperty('--event-hover-border', hoverBg);
        }

        // Right-click context menu isn't wired up here — see
        // setupContextMenuDelegation. eventDidMount only runs once per
        // element, so anything decided here would go stale for a block
        // dropped from the pull-out (it mounts mid-save, before it has a
        // plannedBlockId, and doesn't remount once the save completes).
    }

    // ─── Per-person calendar access ──────────────────────────────────────

    /** This person's calendar, if it's rendered (not collapsed). */
    private apiFor(userId: string): CalendarApi | undefined {
        const comps = this._calendarList?.toArray() ?? [];
        const els = this._calendarElList?.toArray() ?? [];
        const index = els.findIndex((el) => el.nativeElement.getAttribute('data-user-id') === userId);
        return index >= 0 ? comps[index]?.getApi() : undefined;
    }

    /** Every rendered calendar. */
    private allApis(): CalendarApi[] {
        return (this._calendarList?.toArray() ?? []).map((c) => c.getApi()).filter((api) => !!api);
    }

    private refetchAllCalendars(): void {
        this.allApis().forEach((api) => api.refetchEvents());
    }

    get isSeveralPeople(): boolean {
        return this.people.length > 1;
    }

    // Below the desktop width the site's page wrapper (.Page) clips sideways
    // overflow, which stops anything inside it staying pinned while the page
    // scrolls — so there the calendar(s) scroll inside their own box (the
    // several-people layout's), even for one person: the day header stays
    // pinned in it, under the site's top bar. Desktop: one person scrolls
    // with the page, as always.
    private static readonly SCROLL_AREA_BELOW = 1076; // $desktop-sm
    isNarrowScreen = typeof window !== 'undefined' && window.innerWidth < PlannerComponent.SCROLL_AREA_BELOW;

    get usesScrollArea(): boolean {
        return this.isSeveralPeople || this.isNarrowScreen;
    }

    // Day columns show everyone picked in the Owner filter — collapsing is a
    // Week / 5 days thing only (it's remembered for when you go back there).
    isPersonCollapsed(userId: string): boolean {
        return !this.isDayColumns && this.collapsedPeople.has(userId);
    }

    togglePersonCollapsed(userId: string): void {
        if (this.collapsedPeople.has(userId)) {
            this.collapsedPeople.delete(userId);
            // Re-opened: a fresh calendar, on the week now on screen (its
            // options carry the week they were built on — initialDate).
            const person = this.people.find((p) => p.userId === userId);
            if (person) person.options = this.buildCalendarOptions(userId);
            // Their grey days are only loaded once their section is open
            // (see loadHolidayData).
            this.loadHolidayDataFor([userId]);
            setTimeout(() => {
                this.injectZoomControls();
                this.updateStickyHeaderOffset();
                this.ensureCompactScrolled();
            }, 0);
        } else {
            this.collapsedPeople.add(userId);
        }
        this._plannerCache.collapsedPeople = new Set(this.collapsedPeople);
    }

    // ── Every planner collapsed (several people, Week / 5 days) ──
    // A note instead of a blank area, with a way back in: the user's own
    // planner when they're one of the people shown, and all of them when
    // there aren't so many that opening every calendar at once would be slow.
    private static readonly EXPAND_ALL_MAX = 8;

    get allPeopleCollapsed(): boolean {
        return this.isSeveralPeople && !this.isDayColumns
            && this.people.every((p) => this.isPersonCollapsed(p.userId));
    }

    get canShowMyPlanner(): boolean {
        const me = this._loginService.userId;
        return !!me && this.people.some((p) => p.userId === me);
    }

    get canExpandAllPeople(): boolean {
        return this.people.length <= PlannerComponent.EXPAND_ALL_MAX;
    }

    showMyPlanner(): void {
        const me = this._loginService.userId;
        if (me && this.isPersonCollapsed(me)) this.togglePersonCollapsed(me);
    }

    expandAllPeople(): void {
        this.people
            .filter((p) => this.isPersonCollapsed(p.userId))
            .forEach((p) => this.togglePersonCollapsed(p.userId));
    }

    // "All" in the Owner filter shows everyone who owns actions (the same
    // people the Owner picker lists), so it starts with only the logged-in
    // user's own section open and everyone else's collapsed — open just the
    // ones you need. Applied each time the All view is entered.
    private _wasAllView = false;
    private applyAllViewDefaults(): void {
        const isAllView = this.isAllView;
        if (isAllView && !this._wasAllView) {
            const me = this._loginService.userId;
            this.collapsedPeople = new Set(this.people.map((p) => p.userId).filter((id) => id !== me));
            this._plannerCache.collapsedPeople = new Set(this.collapsedPeople);
        }
        this._wasAllView = isAllView;
    }

    // ── People on a job's page (jobPeoplePicker) ──

    // The picker's choice, in the Owner filter's format ('' = All, an id,
    // "group-<id>", "many|…") — null until one's made (then the job's
    // people are shown). For this page only; not remembered between jobs.
    private _jobPeopleOwner: string | null = null;
    // Everyone who owns, or is waited on for, a live action on this job.
    private _jobPeopleIds: string[] | null = null;
    private _jobPeopleForJobId: number | null = null;

    /** Whose planners to show, in the Owner filter's format: the page's
     *  Owner filter, or on a job's page its own choice (by default the
     *  user plus the job's people — just the user until those load). */
    private get plannerOwnerFilter(): string {
        if (!this.jobPeoplePicker) return this._filterQuery?.owner || '';
        if (this._jobPeopleOwner !== null) return this._jobPeopleOwner;
        const me = this._loginService.userId;
        const ids = Array.from(new Set([me, ...(this._jobPeopleIds || [])].filter((id) => !!id)));
        return ids.length <= 1 ? ids[0] || '' : 'many|' + ids.join('|');
    }

    // Loads the job's people once per job (the job comes in with the page's
    // query), then shows them unless a choice has already been made.
    private ensureJobPeople(): void {
        if (!this.jobPeoplePicker) return;
        const jobId = this._filterQuery?.jobId;
        if (!jobId || this._jobPeopleForJobId === jobId) return;
        this._jobPeopleForJobId = jobId;
        this._jobPeopleIds = null;
        this._actionService.searchActions({ jobId, page: 1, resultsPerPage: -1 }).subscribe({
            next: (actions) => {
                if (this._destroyed || this._jobPeopleForJobId !== jobId) return;
                const active = new Set(this._allUsers.filter((u) => u.active).map((u) => u.id));
                const ids = new Set<string>();
                (actions || []).forEach((a) => {
                    (a.assignedUsers || []).forEach((id) => ids.add(id));
                    if (a.waitingUserId) ids.add(a.waitingUserId);
                });
                this._jobPeopleIds = Array.from(ids).filter((id) => active.has(id));
                if (this._jobPeopleOwner === null && this.syncPeople()) this.reloadForSubjectUser();
                this._changeRef.markForCheck();
            },
            error: () => {}, // stays on just the user
        });
    }

    /** The picker's value (ids, teams as "group-<id>"; [] = All). Memoised —
     *  a fresh array every check would make it re-write itself. */
    get jobPeopleSelection(): string[] {
        const owner = this.plannerOwnerFilter;
        if (owner !== this._jobPeopleSelectionMemo.owner) {
            const ids = !owner ? [] : owner.startsWith('many|') ? owner.replace('many|', '').split('|').filter((id) => !!id) : [owner];
            this._jobPeopleSelectionMemo = { owner, ids };
        }
        return this._jobPeopleSelectionMemo.ids;
    }
    private _jobPeopleSelectionMemo: { owner: string | null; ids: string[] } = { owner: null, ids: [] };

    /** Everyone the picker offers: active people. Memoised per users list. */
    get jobPeopleUsers(): User[] {
        if (this._jobPeopleUsersMemo.source !== this._allUsers) {
            this._jobPeopleUsersMemo = { source: this._allUsers, users: this._allUsers.filter((u) => u.active) };
        }
        return this._jobPeopleUsersMemo.users;
    }
    private _jobPeopleUsersMemo: { source: User[] | null; users: User[] } = { source: null, users: [] };

    jobPeopleGroups: UserGroup[] = [];

    // "Apply" in the picker (or its ✕ / "All", which apply an empty list).
    onJobPeopleApplied(ids: string[]): void {
        this._jobPeopleOwner = !ids?.length ? '' : ids.length === 1 ? ids[0] : 'many|' + ids.join('|');
        if (this.syncPeople()) this.reloadForSubjectUser();
        this._changeRef.markForCheck();
    }

    /** "All" in the Owner filter (no owner picked) with everyone shown. */
    get isAllView(): boolean {
        return !this.plannerOwnerFilter && this.people.length > 1;
    }

    trackByPerson(_: number, person: PlannerPerson): string {
        return person.userId;
    }

    /** Planned hours this week for a person's section header. */
    personWeekHours(userId: string): number {
        const totals = this._draftTotalsByUser.get(userId) || {};
        return Object.values(totals).reduce((sum, minutes) => sum + minutes, 0) / 60;
    }

    constructor(
        private _actionService: ActionService,
        private _holidayService: HolidayService,
        private _loginService: LoginService,
        private _plannedBlockService: PlannedBlockService,
        private _timerToastService: TimerToastService,
        private _ngZone: NgZone,
        private _changeRef: ChangeDetectorRef,
        private _plannerCache: PlannerCacheService,
        private _router: Router,
        private _timerSyncService: TimerSyncService,
        private _userService: UserService,
        private _dialog: MatDialog
    ) {
        // Restore the last-chosen zoom before <full-calendar> first renders,
        // so the grid opens at that zoom straight away rather than flashing
        // the default first and then remounting (see applyZoom) — the
        // calendar options are built from zoomLevel (buildCalendarOptions).
        const storedZoom = this.readStoredZoomLevel();
        if (storedZoom !== null) {
            this.zoomLevel = storedZoom;
        }
        // Same for the view (Week / 5 days / Day).
        try {
            const stored = localStorage.getItem(PlannerComponent.VIEW_MODE_KEY);
            if (stored === 'day' || stored === '5days') this.viewMode = stored;
        } catch { /* storage unavailable — Week */ }
    }

    get viewModeLabel(): string {
        return this.viewModeOptions.find((o) => o.value === this.viewMode)?.label ?? 'Week view';
    }

    toggleViewMenu(): void {
        this.viewMenuOpen = !this.viewMenuOpen;
    }

    selectViewMode(mode: PlannerViewMode): void {
        this.viewMenuOpen = false;
        this.setViewMode(mode);
    }

    // ─── Week / Day view ─────────────────────────────────────────────────

    /** Day view with several people: one column per person, side by side
     *  (spec's "Multiple people" made comparable — everyone's same day, the
     *  same hours lined up across). */
    get isDayColumns(): boolean {
        return this.viewMode === 'day' && this.isSeveralPeople;
    }


    // Switches every calendar between the views. Day opens on today if
    // it's in the week on screen (otherwise that week's Monday); Week and
    // 5 days open on the week of the day on screen. Rebuilt and remounted,
    // the same way as zoom (see applyZoom).
    setViewMode(mode: PlannerViewMode): void {
        if (mode === this.viewMode) return;
        const shown = this.calendarComponent?.getApi()?.view;
        let target = this._currentDate ?? new Date();
        if (mode === 'day' && shown) {
            const today = new Date();
            target = today >= shown.activeStart && today < shown.activeEnd ? today : new Date(shown.activeStart);
        }
        const wasCollapsed = this.people.filter((p) => this.isPersonCollapsed(p.userId)).map((p) => p.userId);
        this.viewMode = mode;
        try { localStorage.setItem(PlannerComponent.VIEW_MODE_KEY, mode); } catch { /* storage unavailable */ }
        this._currentDate = target;
        // Into day columns: people collapsed in Week view now show, so
        // their grey days are needed (only open sections' are loaded).
        this.loadHolidayDataFor(wasCollapsed.filter((id) => !this.isPersonCollapsed(id)));
        this.remountCalendars();
    }

    // Rebuilds every person's options (view, zoom, layout) and recreates
    // their calendars, on the date in _currentDate.
    private remountCalendars(): void {
        this.people.forEach((p) => (p.options = this.buildCalendarOptions(p.userId)));
        this._columnsScrolled = false;
        this.calendarVisible = false;
        this._changeRef.markForCheck(); // under the OnPush Actions page — see openContextMenu
        setTimeout(() => {
            this.calendarVisible = true;
            this._changeRef.markForCheck();
            setTimeout(() => {
                this.injectZoomControls();
                this.updateStickyHeaderOffset();
                this.ensureCompactScrolled();
            }, 0);
        }, 0);
    }

    ngOnInit(): void {
        this._plannerCache.setViewer(this._loginService.userId);
        this.pulloutWhen = this._plannerCache.pulloutWhen;
        this.pulloutTab = this._plannerCache.pulloutTab;

        // Any block added/moved/removed (here, or e.g. by the Completed
        // prompt) can move an action between the pull-out's Unplanned and
        // Planned groups — re-check once things settle.
        this.subscription.add(
            this._plannerCache.blocksInvalidated$.pipe(debounceTime(400)).subscribe(() => {
                if (!this._destroyed) this.refreshPlannedInfo();
            })
        );

        // Blocks removed from outside the Planner (e.g. "Remove future
        // planned blocks?" after completing an action) — the cache is
        // already cleared, so this re-fetches what's on screen.
        this.subscription.add(
            this._plannerCache.blocksChangedElsewhere$.subscribe(() => {
                if (this._destroyed) return;
                this.refetchAllCalendars();
            })
        );

        this.collapsedPeople = new Set(this._plannerCache.collapsedPeople);

        // Team names, for "whose is this action" on pull-out items owned by
        // a team (see pulloutWhose).
        this.subscription.add(
            this._userService.groups$.subscribe((groups) => {
                this.jobPeopleGroups = (groups || []).filter((g) => g.active !== false);
                this._groupNames = new Map((groups || []).map((g) => [g.userGroupId, g.groupName] as [number, string]));
                this._whoseMemo = { actions: null, people: null, byAction: new Map() };
                this._changeRef.markForCheck();
            })
        );

        // Everyone who owns actions, for the "All" view — the same cached
        // list the Owner picker uses. Re-checks who to show once it lands.
        this.subscription.add(
            this._actionService.getActionOwners().subscribe({
                next: (owners) => {
                    this._actionOwnerIds = owners?.userIds || [];
                    // Only matters once the search bar's filter is known
                    // (the subscription below handles it from then on).
                    if (this._destroyed || !this._filterQuery) return;
                    if (this.syncPeople()) this.reloadForSubjectUser();
                },
                error: () => {},
            })
        );

        // filterQuery$ is a BehaviorSubject, so this fires immediately with
        // whatever the Actions page's search bar (client/owner/type/job
        // type/search) is currently set to - same query the list/board
        // views already use - and again on every later change. Runs before
        // the view (and <full-calendar>) even mounts, so its own first
        // events fetch already sees the right people. The users list is
        // needed for names and for expanding a team into its members.
        this.subscription.add(
            combineLatest([this._actionService.filterQuery$, this._userService.users$]).subscribe(([filterQuery, users]) => {
                this._allUsers = users || [];
                const ownerChanged = !this._filterQuery || this._filterQuery !== filterQuery;
                this._filterQuery = filterQuery;
                this.ensureJobPeople();
                const peopleChanged = this.syncPeople();
                if (ownerChanged || peopleChanged) this.reloadForSubjectUser();
            })
        );
    }

    // Re-loads everything that's scoped to whose planner(s) we're viewing —
    // the Owner filter's selection (see resolvePeopleIds). Also covers
    // Client/Action type/Job type/Search, which only narrow the pull-out
    // list - the calendar's own planned-blocks endpoint only takes a
    // userId + date range, so it has no way to filter the already-placed
    // blocks by those (would need a backend change).
    private reloadForSubjectUser(): void {
        const token = ++this._requestToken;
        this.loadHolidayData();
        this.loadUnplannedActions(token);
        this.refetchAllCalendars();
    }

    /** Whose planners to show, from the Owner filter: one user → them;
     *  several and/or teams ("many|…", "group-…") → each person, a team
     *  expanding to its active members; "All" (no owner) → everyone who
     *  owns actions (the Owner picker's own list — most start collapsed,
     *  see applyAllViewDefaults), or just the logged-in user's own until
     *  that list has loaded. The logged-in user first if included, then
     *  everyone else by name. */
    private resolvePeopleIds(): string[] {
        const me = this._loginService.userId;
        // No filter from the search bar yet (page still loading) — not the
        // same as "All": show the logged-in user's own, as always.
        if (!this._filterQuery) return me ? [me] : [];
        const owner = this.plannerOwnerFilter;
        const ids = new Set<string>();
        if (!owner) {
            if (!this._actionOwnerIds) return me ? [me] : [];
            const activeIds = new Set(this._allUsers.filter((u) => u.active).map((u) => u.id));
            this._actionOwnerIds.filter((id) => activeIds.has(id)).forEach((id) => ids.add(id));
            if (me) ids.add(me);
        } else if (!owner.startsWith('group-') && !owner.startsWith('many|')) {
            return [owner];
        } else {
            const parts = owner.startsWith('many|') ? owner.replace('many|', '').split('|').filter((p) => !!p) : [owner];
            parts.forEach((part) => {
                if (part.startsWith('group-')) {
                    const groupId = +part.replace('group-', '');
                    // The users list gives each person's team as a nested
                    // userGroup (no top-level userGroupId) — same lookup as
                    // the Timer reports' team filter.
                    this._allUsers
                        .filter((u) => u.active && (u.userGroup?.userGroupId ?? u.userGroupId) === groupId)
                        .forEach((u) => ids.add(u.id));
                } else {
                    ids.add(part);
                }
            });
        }
        // An empty team (or members not loaded yet) — fall back to own.
        if (!ids.size) return me ? [me] : [];

        const name = (id: string) => this.personName(id).toLowerCase();
        return Array.from(ids).sort((a, b) => {
            if (a === me) return -1;
            if (b === me) return 1;
            return name(a).localeCompare(name(b));
        });
    }

    /** The person's short name (set on their user profile), else their
     *  first name — for the narrow Day-view column headers. */
    personShortName(userId: string): string {
        const user = this._allUsers.find((u) => u.id === userId);
        if (!user) return '';
        return user.shortName || user.firstName || this.personName(userId);
    }

    /** On a job's own page (see jobPeoplePicker) — the pull-out lists only
     *  that job's actions there. */
    get isJobPage(): boolean {
        return this.jobPeoplePicker && !!this._filterQuery?.jobId;
    }

    personName(userId: string): string {
        const user = this._allUsers.find((u) => u.id === userId);
        return user ? (user.fullName || `${user.firstName} ${user.surname}`) : '';
    }

    /** Rebuilds `people` from the Owner filter; true if it changed. People
     *  who stay keep their calendar (and its options) as they are; going
     *  between one person and several rebuilds every person's options, as
     *  the layout (height, sticky header) differs. */
    private syncPeople(): boolean {
        const ids = this.resolvePeopleIds();
        const current = this.people.map((p) => p.userId);
        if (ids.length === current.length && ids.every((id, i) => id === current[i])) {
            // Same people — names may have only just loaded.
            this.people.forEach((p) => (p.name = this.personName(p.userId)));
            return false;
        }
        const kept = new Map(this.people.map((p) => [p.userId, p] as [string, PlannerPerson]));
        // Day view: going between one person and several switches between
        // a single day and side-by-side columns, whose options differ (the
        // sticky day header) — rebuild everyone's then.
        const dayLayoutChanged = this.viewMode === 'day' && (ids.length > 1) !== (current.length > 1);
        this.people = ids.map((userId) => ({ userId, name: this.personName(userId), options: kept.get(userId)?.options ?? null }));
        this.people.forEach((p) => {
            if (!p.options || dayLayoutChanged) p.options = this.buildCalendarOptions(p.userId);
        });
        // Into the several-people layout (either view): its shared scroll
        // area is new, so it opens on working hours again.
        if ((ids.length > 1) !== (current.length > 1)) this._columnsScrolled = false;
        this.applyAllViewDefaults();
        this._changeRef.markForCheck();
        // Newly shown calendars need the zoom controls/size once rendered,
        // and (several people) the people area opened on working hours.
        setTimeout(() => {
            this.injectZoomControls();
            this.updateStickyHeaderOffset();
            this.allApis().forEach((api) => api.updateSize());
            this.ensureCompactScrolled();
        }, 0);
        return true;
    }

    // Several people: the shared people area opens on 08:00 of the first
    // person shown, just under their pinned name and dates — scrolled once
    // per layout (reset when the view or the one/several layout changes),
    // not again on every week change. After a zoom, on the person who was
    // being looked at instead (_zoomSectionIndex — see topSectionIndex).
    private _zoomSectionIndex = 0;
    private ensureCompactScrolled(): void {
        if (!this.usesScrollArea || this._columnsScrolled) return;
        // The box's size and the rows' height first — this can run as the
        // calendar first draws, before they've been worked out, which would
        // put 08:00 in the wrong place once the rows resize.
        this.updateColumnsHeight();
        this.applySlotHeight();
        const area = this.plannerBodyRef?.nativeElement.querySelector('.Planner-peopleList--scroll') as HTMLElement | null;
        const laneSelector = '.fc-timegrid-slot-lane[data-time="08:00:00"]';
        const target = area?.querySelectorAll('.PlannerPerson')[this._zoomSectionIndex];
        const lane = (target?.querySelector(laneSelector) ?? area?.querySelector(laneSelector)) as HTMLElement | null;
        if (!area || !lane) return;
        this._columnsScrolled = true;
        this._zoomSectionIndex = 0;
        const section = lane.closest('.PlannerPerson') as HTMLElement | null;
        const personHeader = section?.querySelector('.PlannerPerson-header') as HTMLElement | null;
        const dayHeader = section?.querySelector('.fc-scrollgrid-section-header') as HTMLElement | null;
        const pinned = (personHeader?.offsetHeight ?? 0) + (dayHeader?.offsetHeight ?? 0);
        area.scrollTop += lane.getBoundingClientRect().top - area.getBoundingClientRect().top - pinned;
    }

    // Several people: which person's section is at the top of the people
    // area (its name row stuck there) — the last one starting at or above
    // the area's top.
    private topSectionIndex(): number {
        const area = this.plannerBodyRef?.nativeElement.querySelector('.Planner-peopleList--scroll') as HTMLElement | null;
        if (!area) return 0;
        const areaTop = area.getBoundingClientRect().top;
        let index = 0;
        area.querySelectorAll('.PlannerPerson').forEach((section, i) => {
            if (section.getBoundingClientRect().top <= areaTop + 1) index = i;
        });
        return index;
    }

    // Fetches one range of blocks and caches the reply — unless a block was
    // created/moved/deleted while it was in flight (blocksVersion moved
    // on), in which case the reply may predate that change and isn't
    // cached. Shares an identical request that's already in flight.
    private fetchBlocks(userId: string, start: Date, end: Date, key: string): Observable<PlannedBlockViewModel[]> {
        const pending = this._pendingBlockFetches.get(key);
        if (pending) return pending;

        const version = this._plannerCache.blocksVersion(userId);
        const request = this.queueBlockFetch(userId, start, end).pipe(
            map((blocks) => {
                if (this._plannerCache.blocksVersion(userId) === version) {
                    this._plannerCache.setBlocks(key, blocks);
                }
                return blocks;
            }),
            finalize(() => this._pendingBlockFetches.delete(key)),
            shareReplay(1)
        );
        this._pendingBlockFetches.set(key, request);
        return request;
    }

    // Every calendar on screen asks for its own person's blocks, all at the
    // same moment (first load, a new week, a refetch). Rather than one
    // request each — dozens for a team or "All", queued by the browser a
    // few at a time — the asks for the same range made in the same moment
    // go out as one request, and each person gets back just their own
    // blocks, exactly as their own request would have returned them. On
    // its own (one person) it's the same single-person request as before.
    private _blockBatches = new Map<string, { start: Date; end: Date; userIds: Set<string>; result: AsyncSubject<PlannedBlockViewModel[]> }>();
    private queueBlockFetch(userId: string, start: Date, end: Date): Observable<PlannedBlockViewModel[]> {
        const rangeKey = `${start.toISOString()}|${end.toISOString()}`;
        let batch = this._blockBatches.get(rangeKey);
        if (!batch) {
            const created = { start, end, userIds: new Set<string>(), result: new AsyncSubject<PlannedBlockViewModel[]>() };
            this._blockBatches.set(rangeKey, created);
            // Sent once everything asking in this same moment has joined.
            setTimeout(() => {
                this._blockBatches.delete(rangeKey);
                const ids = Array.from(created.userIds);
                const request = ids.length === 1
                    ? this._plannedBlockService.getPlannedBlocks(ids[0], created.start, created.end)
                    : this._plannedBlockService.getPlannedBlocksForUsers(ids, created.start, created.end);
                request.subscribe(created.result);
            }, 0);
            batch = created;
        }
        batch.userIds.add(userId);
        return batch.result.pipe(map((blocks) => blocks.filter((b) => b.userId === userId)));
    }

    // Quiet background check of a range that was shown from the cache: no
    // loading indicator, and the calendar is only re-rendered (from the
    // now-updated cache, so without another request) if something actually
    // changed. A failure just leaves the cached blocks showing.
    private revalidateBlocks(userId: string, start: Date, end: Date, key: string, cached: PlannedBlockViewModel[]): void {
        this.fetchBlocks(userId, start, end, key).subscribe({
            next: (blocks) => {
                if (this._destroyed) return;
                if (this._plannerCache.getBlocks(key) !== blocks) return; // not cached — a change landed meanwhile
                if (JSON.stringify(blocks) === JSON.stringify(cached)) return;
                this.apiFor(userId)?.refetchEvents();
            },
            error: () => {},
        });
    }

    // The top person shown — the only one in the one-person view, exactly
    // as before (the Owner filter's single user, or the logged-in user's own
    // for "All"). Used where one person is needed regardless of how many
    // are shown: the toolbar's "Add a planned block", and the pull-out's
    // saved order. Anything about a particular block uses that block's own
    // person instead (see findBlock).
    private subjectUserId(): string | undefined {
        return this.people[0]?.userId ?? this._loginService.userId;
    }

    // Whose actions the pull-out lists: everyone shown (a team as its
    // members, same as their calendars), in the owner format the action
    // search understands.
    private pulloutOwner(): string | undefined {
        // A job's page: all of this job's actions, whoever's planners are
        // shown (as it always listed) — a people filter here would drop
        // actions owned only by a team.
        if (this.jobPeoplePicker) return '';
        // "All" view: everyone's, the same as the Owner filter's own "All".
        if (this.isAllView) return '';
        const ids = this.people.map((p) => p.userId);
        if (ids.length > 1) return 'many|' + ids.join('|');
        return this.subjectUserId();
    }

    // The pull-out panel (and its list container) is wrapped in
    // *ngIf="pulloutOpen" — closing and reopening it destroys and recreates
    // that DOM element, so a Draggable set up once (e.g. only in
    // ngAfterViewInit) ends up attached to a detached, dead node the moment
    // the panel is reopened. Re-running setup on every open (both the
    // initial one and every later reopen) via this Input's ngOnChanges
    // covers both the internal close/reopen buttons and the parent's
    // [(pulloutOpen)] binding (the "Unscheduled" toolbar toggle) uniformly.
    ngOnChanges(changes: SimpleChanges): void {
        if (changes['pulloutOpen'] && this.pulloutOpen) {
            setTimeout(() => this.setupPulloutDraggable(), 0);
            setTimeout(() => this.updatePulloutOffset(), 0);
        }
        if (changes['pulloutOpen']) {
            // Opening/closing the panel resizes the calendar itself (it now
            // pushes the grid narrower rather than overlapping it — see
            // planner.component.scss). FullCalendar only redraws
            // automatically on a real window resize, not a sibling-driven
            // container resize, so without this nudge its day columns/now
            // indicator can be left rendered at the stale pre-toggle width.
            setTimeout(() => this.allApis().forEach((api) => api.updateSize()), 0);
        }
    }

    ngAfterViewInit(): void {
        setTimeout(() => this.injectZoomControls(), 0);
        setTimeout(() => this.updateStickyHeaderOffset(), 0);
        setTimeout(() => this.updatePulloutOffset(), 0);

        // First load: opens on the working day, 08:00–18:00 (spec).
        setTimeout(() => this.scrollToWorkingHours(), 0);

        this._resizeListener = () => {
            // Crossing the desktop width switches between scrolling with the
            // page and in its own box (usesScrollArea): that box is new, so
            // it opens on 08:00 again.
            const narrow = window.innerWidth < PlannerComponent.SCROLL_AREA_BELOW;
            if (narrow !== this.isNarrowScreen) {
                this.isNarrowScreen = narrow;
                this._columnsScrolled = false;
                this._changeRef.markForCheck();
                setTimeout(() => {
                    this.updateStickyHeaderOffset();
                    this.updatePulloutOffset();
                    this.allApis().forEach((api) => api.updateSize());
                    this.ensureCompactScrolled();
                }, 0);
                return;
            }
            this.updateStickyHeaderOffset();
            this.updatePulloutOffset();
        };
        window.addEventListener('resize', this._resizeListener);

        this.observeBodySize();
        this.setupContextMenuDelegation();
        this.setupDocumentMousedown();
        this.setupPulloutHandlePositioning();
        this.scheduleMidnightLock();
    }

    // FullCalendar measures its time-slot rows once, when it first sizes
    // itself, and only places timed events once that measurement exists —
    // if the calendar has no height at that moment (still hidden / mid-
    // layout on page load), the measurement is skipped and every loaded
    // block stays invisible until something else forces a re-render (e.g.
    // dropping another action). FullCalendar only re-measures by itself on
    // a real window resize, so this re-measures whenever the calendar's
    // container actually changes size, including going from 0 to visible.
    // Outside Angular's zone since it never touches Angular state.
    private observeBodySize(): void {
        const bodyEl = this.plannerBodyRef?.nativeElement;
        if (!bodyEl || typeof ResizeObserver === 'undefined') return;

        let lastWidth = -1;
        let lastHeight = -1;
        this._ngZone.runOutsideAngular(() => {
            this._bodyResizeObserver = new ResizeObserver((entries) => {
                const rect = entries[entries.length - 1]?.contentRect;
                if (!rect || (rect.width === lastWidth && rect.height === lastHeight)) return;
                lastWidth = rect.width;
                lastHeight = rect.height;
                if (!rect.width || !rect.height) return;

                if (this._bodyResizeFrame !== null) cancelAnimationFrame(this._bodyResizeFrame);
                this._bodyResizeFrame = requestAnimationFrame(() => {
                    this._bodyResizeFrame = null;
                    // The rows' height and whether the whole day fills the
                    // space (_wholeDay), and the panel's height with it: the
                    // first pass can run before the calendar or the page
                    // around it has settled on load. Both do nothing when
                    // nothing's changed.
                    this.applySlotHeight();
                    this.updatePulloutOffset();
                    this.allApis().forEach((api) => api.updateSize());
                    this.positionPulloutHandle();
                    this.updateOverflowBadges();
                });
            });
            this._bodyResizeObserver.observe(bodyEl);
        });
    }

    // One listener on the calendar's own container, not one per block —
    // Planner refetches/remounts events far more often than Timer does
    // (job/client enrichment, holiday loads, owner-filter changes all
    // trigger a refetch), so a listener bound to a given block's element
    // could easily end up on one already on its way out. Set up once;
    // .Planner-body itself is never destroyed by a refetch or a zoom
    // remount, only by this whole component going away.
    //
    // The clicked block's data is read from FullCalendar's own live
    // element→segment link (getElSeg — the same lookup its interaction
    // plugin uses for clicks/drags, and kept current on every re-render),
    // not from anything stamped on the element at mount time. That's what
    // makes a block dropped from the pull-out right-clickable as soon as
    // its save completes, without waiting for a refetch.
    private setupContextMenuDelegation(): void {
        const bodyEl = this.plannerBodyRef?.nativeElement;
        if (!bodyEl) return;
        bodyEl.addEventListener('contextmenu', (e: MouseEvent) => {
            const target = e.target as HTMLElement | null;
            const el = target?.closest('.fc-timegrid-event') as HTMLElement | null;
            if (!el) return; // background/holiday shading, empty slots, etc. — let the browser's own menu show
            const props = getElSeg(el)?.eventRange?.def.extendedProps;
            // An action's block, or busy time (Edit / Duplicate / Delete only).
            if (!props?.['jobActionId'] && !props?.['isBusy']) return;
            e.preventDefault();
            e.stopPropagation();
            // Not persisted yet (or still mid-save) — nothing to delete,
            // same guard the old left-click delete used.
            const plannedBlockId = props['plannedBlockId'] as number | undefined;
            if (!plannedBlockId || props['isSaving']) return;
            // Attached outside Angular's template, so a plain call here runs
            // outside the zone — the same class of bug the zoom buttons hit
            // (see injectZoomControls): the contextMenu state change
            // wouldn't be picked up by change detection until some
            // unrelated event happened to trigger it.
            this._ngZone.run(() => this.openContextMenu(plannedBlockId, e.clientX, e.clientY));
        });
    }

    ngOnDestroy(): void {
        clearTimeout(this._overflowTimer);
        this._handlePositionCleanup?.();
        clearTimeout(this._midnightTimer);
        clearTimeout(this._highlightSweepTimer);
        clearTimeout(this._mirrorTimer);
        document.body.classList.remove('PlannerDrag--overPanel', 'PlannerDrag--removed', 'PlannerDrag--overLocked', 'PlannerDrag--outside');
        this._pulloutDragCleanup?.();
        this._dragHoverCleanup?.();
        this._destroyed = true;
        this.draggable?.destroy();
        this.subscription.unsubscribe();
        if (this._resizeListener) {
            window.removeEventListener('resize', this._resizeListener);
        }
        this._bodyResizeObserver?.disconnect();
        document.removeEventListener('mousedown', this._documentMousedownCapture, true);
        if (this._bodyResizeFrame !== null) cancelAnimationFrame(this._bodyResizeFrame);
    }

    private setupPulloutDraggable(): void {
        this.draggable?.destroy();
        if (!this.pulloutListRef) return;
        this.draggable = new Draggable(this.pulloutListRef.nativeElement, {
            // Not the locked ones (canPlanPulloutAction).
            itemSelector: '.PlannerPullout-item:not(.is-locked)',
            eventData: (eventEl: HTMLElement) => {
                // By id, not position — the list is split into the
                // Unplanned/Planned groups, so an index wouldn't map.
                const jobActionId = Number(eventEl.getAttribute('data-action-id'));
                const action = this.unplannedActions.find((a) => a.jobActionId === jobActionId);
                if (!action) return {};
                const color = plannerColorForJobNumber(action.jobNumber);
                // The job's own identifier (number + job title) — the
                // action's name is shown separately above it (the
                // fc-event-description line).
                const jobLabel = action.jobNumber ? `${action.jobNumber} - ${action.jobTitle || ''}` : '';
                return {
                    title: jobLabel,
                    duration: '01:00',
                    backgroundColor: color.bg,
                    borderColor: color.bg,
                    extendedProps: {
                        jobActionId: action.jobActionId,
                        actionTitle: action.title,
                        jobId: action.jobId,
                        jobNumber: action.jobNumber,
                        jobTitle: action.jobTitle,
                        clientName: action.clientName,
                        isBillable: !!action.isBillable,
                        titleColor: color.titleColor,
                        hoverBg: color.hoverBg,
                    },
                };
            },
        });

        // Tablet/mobile only: the panel overlays the calendar there (see
        // .Planner-body in the scss), hiding the days behind it — so it
        // slides out of the way for the duration of a drag. FullCalendar
        // hit-tests drops by coordinates, not by what's on top, so the
        // panel only needs hiding visually. Toggled directly on the element
        // (not via a binding) since these fire from FullCalendar's own
        // pointer handlers mid-drag.
        const panel = this.pulloutListRef.nativeElement.closest('.Planner-pullout') as HTMLElement | null;
        if (!panel) return;
        this.draggable.dragging.emitter.on('dragstart', () => {
            if (panel.parentElement && getComputedStyle(panel.parentElement).display === 'grid') {
                panel.classList.add('Planner-pullout--dragging');
            }
        });
        this.draggable.dragging.emitter.on('dragend', () => {
            panel.classList.remove('Planner-pullout--dragging');
        });
    }

    // First letter only, for display — the rest is left exactly as typed
    // ("content - plan" → "Content - plan"). The action itself isn't changed.
    capitalizeFirst(text: string | null | undefined): string {
        const t = text || '';
        return t.charAt(0).toUpperCase() + t.slice(1);
    }

    setPulloutOpen(value: boolean): void {
        if (!value) this.clearHighlight();
        // Closed: its tab appears — placed once it's drawn.
        if (!value) setTimeout(() => this.positionPulloutHandle(), 0);
        this.pulloutOpen = value;
        this.pulloutOpenChange.emit(value);
    }

    // Week navigation moves every person's calendar together.
    navPrev(): void {
        this.allApis().forEach((api) => api.prev());
    }

    navNext(): void {
        this.allApis().forEach((api) => api.next());
    }

    private gotoDateAll(date: Date): void {
        this.allApis().forEach((api) => api.gotoDate(date));
    }

    toggleNavPicker(): void {
        this.showNavPicker = !this.showNavPicker;
    }

    closeNavPicker(): void {
        this.showNavPicker = false;
    }

    onNavShortcut(key: string): void {
        const today = new Date();
        let target: Date;
        switch (key) {
            case 'today':
                target = today;
                break;
            case 'yesterday':
                target = addDays(today, -1);
                break;
            case 'thisWeek':
                target = getMondayOf(today);
                break;
            case 'lastWeek':
                target = addDays(getMondayOf(today), -7);
                break;
            default:
                target = today;
        }
        this.gotoDateAll(target);
        this.closeNavPicker();
    }

    onNavWeekClick(weekStart: Date): void {
        this.gotoDateAll(weekStart);
        this.closeNavPicker();
    }

    zoomIn(): void {
        if (this.zoomLevel >= this.zoomLevels.length - 1) return;
        this.zoomLevel++;
        this.applyZoom();
    }

    zoomOut(): void {
        if (this.zoomLevel <= 0) return;
        this.zoomLevel--;
        this.applyZoom();
    }

    /** localStorage key for remembering the user's chosen zoom level —
     *  separate from Timer's own (tt_zoom_level), since the two calendars
     *  have different zoom steps. */
    // v2: a zoomed-out level was added in front of the old two, so the
    // numbers moved up by one — an old saved choice (ZOOM_KEY_V1) is read
    // as the same zoom it was.
    private static readonly ZOOM_KEY = 'planner_zoom_level_v2';
    private static readonly ZOOM_KEY_V1 = 'planner_zoom_level';

    /** Reads the saved zoom level, or null if none / out of range. Same
     *  approach as Timer's readStoredZoomLevel. */
    private readStoredZoomLevel(): number | null {
        try {
            let v = localStorage.getItem(PlannerComponent.ZOOM_KEY);
            let shift = 0;
            if (v == null) {
                v = localStorage.getItem(PlannerComponent.ZOOM_KEY_V1);
                shift = 1;
            }
            if (v == null) return null;
            const n = parseInt(v, 10) + shift;
            if (Number.isFinite(n) && n >= 0 && n < this.zoomLevels.length) return n;
            return null;
        } catch {
            return null;
        }
    }

    /** Saves the user's current zoom level so the next visit restores it. */
    private persistZoomLevel(level: number): void {
        try {
            localStorage.setItem(PlannerComponent.ZOOM_KEY, String(level));
        } catch { /* storage full or unavailable */ }
    }

    // FullCalendar's own `setOption()` doesn't reliably re-render a fresh
    // slotDuration/slotLabelInterval grid in place (Timer's calendar hits
    // the same limitation and works around it the same way: destroy and
    // recreate the <full-calendar> itself via *ngIf, rather than mutating
    // the live instance). Every person's options are rebuilt at the new
    // zoom (buildCalendarOptions reads zoomLevel), not just the live API, so
    // the Angular wrapper's own change detection (which diffs against the
    // bound options object, not the live calendar) doesn't fight the change
    // on its next cycle.
    private applyZoom(): void {
        this.persistZoomLevel(this.zoomLevel);
        const currentDate = this.calendarComponent?.getApi()?.getDate();
        // After zooming the calendar starts at 08:00 again (see below) —
        // except on a job's page with one person, where the page itself
        // isn't scrolled: there the first time visible at the top is kept
        // where it was (captureZoomAnchor).
        const anchor = this.captureZoomAnchor();
        // Several people: stays on the person being looked at, rather than
        // going back up to the first.
        this._zoomSectionIndex = this.usesScrollArea ? this.topSectionIndex() : 0;

        this.people.forEach((p) => (p.options = this.buildCalendarOptions(p.userId)));

        this.calendarVisible = false;
        // Under the Actions page (OnPush) — see openContextMenu.
        this._changeRef.markForCheck();
        setTimeout(() => {
            this.calendarVisible = true;
            this._changeRef.markForCheck();
            setTimeout(() => {
                if (currentDate) {
                    this.gotoDateAll(currentDate);
                }
                this.injectZoomControls();
                this.updateStickyHeaderOffset();
                if (this.usesScrollArea) {
                    this._columnsScrolled = false;
                    this.ensureCompactScrolled();
                } else if (!this.jobPeoplePicker) {
                    this.scrollToWorkingHours();
                } else if (anchor) {
                    this.restoreZoomAnchor(anchor);
                }
            }, 0);
        }, 0);
    }

    /** Where to look: the several-people scroll area, else the page's own
     *  scroller (one person) — and the y (viewport) of the top of the hours
     *  that are visible there, just under whatever is pinned above them
     *  (search bar, the person's name row, the day header). */
    private zoomViewport(): { container: HTMLElement; y: number } | null {
        const root = this.plannerBodyRef?.nativeElement;
        const dayHeader = root?.querySelector('.PlannerPerson-calendar .fc-scrollgrid-section-header') as HTMLElement | null;
        const dayHeaderH = dayHeader?.offsetHeight ?? 0;
        const area = root?.querySelector('.Planner-peopleList--scroll') as HTMLElement | null;
        if (area) {
            const personHeader = area.querySelector('.PlannerPerson-header') as HTMLElement | null;
            return { container: area, y: area.getBoundingClientRect().top + (personHeader?.offsetHeight ?? 0) + dayHeaderH };
        }
        const vc = this.getCalendarVisibleCentre();
        return vc ? { container: vc.container, y: vc.visibleTop + dayHeaderH } : null;
    }

    /** A calendar's hours grid (whose person, and where it is on screen). */
    private calendarSlotsRects(): { userId: string; rect: DOMRect }[] {
        const root = this.plannerBodyRef?.nativeElement;
        if (!root) return [];
        return (Array.from(root.querySelectorAll('.PlannerPerson-calendar[data-user-id]')) as HTMLElement[])
            .map((cal) => ({ userId: cal.getAttribute('data-user-id') || '', slots: cal.querySelector('.fc-timegrid-slots') as HTMLElement | null }))
            .filter((c) => !!c.slots)
            .map((c) => ({ userId: c.userId, rect: c.slots.getBoundingClientRect() }))
            .filter((c) => c.rect.height > 0);
    }

    /** The time of day (as a 0–1 share of the day) at the top of the
     *  visible hours, on the calendar there (or the nearest one), before a
     *  zoom. */
    private captureZoomAnchor(): { userId: string; fraction: number; y: number } | null {
        const view = this.zoomViewport();
        const cals = this.calendarSlotsRects();
        if (!view || !cals.length) return null;
        const distance = (r: DOMRect) => (view.y < r.top ? r.top - view.y : view.y > r.bottom ? view.y - r.bottom : 0);
        const best = cals.reduce((a, b) => (distance(b.rect) < distance(a.rect) ? b : a));
        // The calendar starts lower down the screen (not scrolled to yet):
        // its top stays exactly where it is.
        const y = Math.max(view.y, best.rect.top);
        const fraction = Math.max(0, Math.min(1, (y - best.rect.top) / best.rect.height));
        return { userId: best.userId, fraction, y };
    }

    /** After the zoom has redrawn: the same time back in the same place. */
    private restoreZoomAnchor(anchor: { userId: string; fraction: number; y: number }): void {
        const view = this.zoomViewport();
        const cal = this.calendarSlotsRects().find((c) => c.userId === anchor.userId);
        if (!view || !cal) return;
        const targetY = cal.rect.top + anchor.fraction * cal.rect.height;
        this.scrollContainerBy(view.container, targetY - anchor.y);
    }

    // A placed block that's still saving (just dropped, create call not yet
    // back) can't be moved/resized meaningfully — revert and ignore rather
    // than risk a move racing the create call.
    private persistBlockMove(info: {
        event: EventApi;
        oldEvent?: EventApi;
        el?: HTMLElement;
        revert: () => void;
    }, userId: string): void {
        // Let go outside the planner (over the search bar covering the top
        // of the calendar, say): back where it was (isOutsidePlanner).
        if (this._releasedOutside) {
            info.revert();
            return;
        }
        const plannedBlockId = info.event.extendedProps?.['plannedBlockId'];
        // 0 for a block whose action has since been deleted — it keeps that.
        const jobActionId = info.event.extendedProps?.['jobActionId'] ?? 0;
        const start = info.event.start;
        const end = info.event.end;
        if (!plannedBlockId || !userId || !start) {
            info.revert();
            return;
        }
        const durationMinutes = end ? Math.round((end.getTime() - start.getTime()) / 60000) : 60;

        // eventDidMount only fires once, at the block's initial mount, so a
        // resize/move otherwise leaves the footer showing whatever duration
        // the block had when it first appeared, however it's dragged
        // afterwards. Updated optimistically here, then corrected back if
        // the save fails and the move is reverted.
        if (info.el) {
            this.updateEventFooterDuration(info.el, start, end);
            // Moved to another day: on or off its action's deadline.
            this.updateDeadlineFlag(info.el, start, info.event.extendedProps['deadlineDate']);
        }

        this._plannerCache.invalidateBlocks(userId);
        this._plannedBlockService
            .updatePlannedBlock(plannedBlockId, { jobActionId, userId, start, durationMinutes })
            .subscribe({
                next: () => this._plannerCache.invalidateBlocks(userId),
                error: () => {
                    this._plannerCache.invalidateBlocks(userId);
                    if (this._destroyed) return;
                    info.revert();
                    if (info.el) {
                        this.updateEventFooterDuration(info.el, info.oldEvent?.start ?? null, info.oldEvent?.end ?? null);
                        this.updateDeadlineFlag(info.el, info.oldEvent?.start ?? null, info.event.extendedProps['deadlineDate']);
                    }
                    alert('Could not update this planned block. Please try again.');
                },
            });
    }

    // A red border around the blocks of an action that's due today or
    // overdue (its deadline has passed) — every one of them, on any day
    // from today on, so a block planned later still says its action is due
    // now — and around a block on the day its action is due (deadlineDate,
    // see toCalendarEvent). With a hover note saying which (deadlineNote).
    // Given to FullCalendar as one of the block's own classes
    // (eventClassNames), which it works out again each time it redraws the
    // block: added by hand instead, it was lost whenever FullCalendar
    // rewrote the block's classes (as it does while a block is dragged
    // within its day). The drag copy goes by where the block was (see
    // _draggingFrom).
    private isOnDeadlineDay(event: EventApi, isMirror: boolean): boolean {
        const start = isMirror && this._draggingFrom ? this._draggingFrom : event.start;
        return !!this.deadlineNote(start, event.extendedProps['deadlineDate']);
    }

    /** "Deadline overdue (Mon 19 Oct)" (its deadline has passed) or
     *  "Deadline today" (due today), whatever day the block's on from today
     *  on; "Deadline on this day" (due on the block's own day); or null.
     *  Never on a past (locked) day's block: that's done with, so it isn't
     *  flagged. */
    private deadlineNote(start: Date | null, deadlineDate: string | null | undefined): string | null {
        if (!deadlineDate || this.isPastDay(start)) return null;
        const deadline = new Date(deadlineDate);
        const today = new Date();
        if (isSameDay(deadline, today)) return 'Deadline today';
        if (deadline.getTime() < today.getTime()) {
            const day = deadline.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
            return `Deadline overdue (${day})`;
        }
        if (start && isSameDay(deadline, start)) return 'Deadline on this day';
        return null;
    }

    // The border's hover note (a title isn't one of FullCalendar's own, so
    // it stays): set or cleared to match, so it follows a block moved
    // between days. Marked (data-deadline-note) so only a note set here is
    // ever cleared.
    private updateDeadlineFlag(el: HTMLElement, start: Date | null, deadlineDate: string | null | undefined): void {
        const note = this.deadlineNote(start, deadlineDate);
        if (note) {
            el.title = note;
            el.setAttribute('data-deadline-note', '');
        } else if (el.hasAttribute('data-deadline-note')) {
            el.removeAttribute('title');
            el.removeAttribute('data-deadline-note');
        }
    }

    private updateEventFooterDuration(el: HTMLElement, start: Date | null, end: Date | null): void {
        if (!start || !end) return;
        const timeEl = el.querySelector('.fc-event-footer__time');
        if (timeEl) timeEl.textContent = formatTimeRange(start, end);
        const durationEl = el.querySelector('.fc-event-footer__duration');
        if (!durationEl) return;
        const minutes = Math.round((end.getTime() - start.getTime()) / 60000);
        durationEl.textContent = formatDurationHM(minutes);
    }

    // ─── Right-click context menu on a placed block — same pattern as the
    // Timer calendar's own (timer-tracker.component.ts): Duplicate, Go to
    // job, Delete. Duplicated rather than imported, same Timer-isolation
    // reasoning as the rest of this file. ──────────────────────────────────

    // `position: absolute` relative to .Planner's own root rather than
    // `position: fixed` relative to the viewport — this app's page layout
    // has *something* up the ancestor chain that establishes a containing
    // block for fixed-position descendants (the exact cause was never
    // pinned down; .Planner-pullout hit the same thing and is why it's
    // sticky-positioned instead of fixed), which left the menu rendering in
    // the wrong place. Anchoring to a direct, known parent sidesteps that
    // regardless of what the actual culprit further up is.
    private openContextMenu(plannedBlockId: number, clientX: number, clientY: number): void {
        // Someone else's planner without the Planner role: only the
        // action's own items (Edit action, Go to job) — the block's are
        // hidden (contextMenuCanEdit). Nothing to offer at all (its action
        // deleted, on that job's own page): no menu.
        if (!this.canEditPlannerOf(this.findBlock(plannedBlockId)?.userId)) {
            const props = this.findBlockEvent(plannedBlockId)?.extendedProps;
            const jobId = (props?.['jobId'] as number) || null;
            const canGoToJob = !!jobId && !this.isCurrentJobPage(jobId);
            if (!props?.['jobActionId'] && !canGoToJob) return;
        }
        const { top, left } = this.computeContextMenuPosition(clientX, clientY);
        const hostRect = this.plannerRootRef?.nativeElement.getBoundingClientRect();
        this.contextMenu = {
            plannedBlockId,
            top: top - (hostRect?.top ?? 0),
            left: left - (hostRect?.left ?? 0),
            // Where it was opened — "Edit" opens the popover there.
            clientX,
            clientY,
            ready: false,
        };
        // Its height depends on which items it has (two on a view-only
        // planner, six on one that can be changed) — measured once drawn.
        setTimeout(() => this.fitContextMenu(), 0);
        // The host page (ActionListingComponent) is OnPush, so a change
        // made from a plain DOM listener (setupContextMenuDelegation) or a
        // FullCalendar callback (datesSet) never gets this view re-checked
        // on its own — the zone tick runs, but skips the whole OnPush
        // subtree. Without this the state changed but the menu never
        // actually rendered.
        this._changeRef.markForCheck();
    }

    // ── Who can change what (spec, Access and Roles) ──
    // Everyone can view everyone's planner; blocks can only be added, moved,
    // edited, removed or logged on your own — or on anyone's with the
    // Planner role. The server enforces the same rule.
    canEditPlannerOf(userId: string | null | undefined): boolean {
        if (!userId) return false;
        return userId === this._loginService.userId || this._loginService.checkAuth(['Planner']);
    }

    /** Whether a pull-out action can be dragged (onto the calendar, or to
     *  reorder): with the Planner role, any of them; otherwise only the
     *  user's own (owned by them or their team, or waiting on them), and
     *  only while their own planner is on screen to drop it on. The rest
     *  show as locked — no grab cursor, no handle. */
    // Any action can go on the user's own planner — theirs, or a
    // colleague's they're helping with (a block on your own planner is your
    // commitment; it doesn't change the action). Other people's planners
    // stay the Planner role's (canEditPlannerOf — the calendars there refuse
    // the drop and say so). So without the role, a card can be dragged
    // whenever the user's own planner is one of those shown.
    // With the Planner role: whenever one of the planners shown takes it —
    // the user's own, or that of someone it belongs to (canPlanActionOn).
    canPlanPulloutAction(action: JobAction): boolean {
        const me = this._loginService.userId;
        if (this._loginService.checkAuth(['Planner'])) {
            if (this._canPlanMemo.actions !== this.unplannedActions || this._canPlanMemo.people !== this.people) {
                this._canPlanMemo = { actions: this.unplannedActions, people: this.people, byAction: new Map() };
            }
            let can = this._canPlanMemo.byAction.get(action.jobActionId);
            if (can === undefined) {
                can = this.people.some((p) => p.userId === me || this.isActionOf(action, p.userId));
                this._canPlanMemo.byAction.set(action.jobActionId, can);
            }
            return can;
        }
        return !!me && this.people.some((p) => p.userId === me);
    }
    private _canPlanMemo: { actions: JobAction[] | null; people: PlannerPerson[] | null; byAction: Map<number, boolean> } =
        { actions: null, people: null, byAction: new Map() };

    /** Whether an action can go on this person's planner: on the user's
     *  own, any action (as always — helping on a colleague's included); on
     *  someone else's (the Planner role), only one that's theirs — owned by
     *  them or their team, or waiting on them (isActionOf). Not a deleted
     *  action (0) there. An action that isn't in the panel can't be checked
     *  here — the server decides (it checks the same). */
    canPlanActionOn(jobActionId: number | null | undefined, userId: string): boolean {
        if (!this.canEditPlannerOf(userId)) return false;
        if (userId === this._loginService.userId) return true;
        if (!jobActionId) return false;
        const action = this.unplannedActions.find((a) => a.jobActionId === jobActionId);
        return !action || this.isActionOf(action, userId);
    }

    /** eventAllow: a block staying on its own planner can always move there
     *  (only its time changes); anything else — a block from another
     *  planner, or an action from the panel — goes by canPlanActionOn. */
    private canDropEventOn(event: EventApi | null | undefined, userId: string): boolean {
        const props = event?.extendedProps || {};
        const blockId = props['plannedBlockId'] as number | undefined;
        if (blockId != null && this.findBlock(blockId)?.userId === userId) return true;
        // Busy time has no action — any planner the user may change.
        if (props['isBusy']) return this.canEditPlannerOf(userId);
        return this.canPlanActionOn(props['jobActionId'] as number | undefined, userId);
    }

    /** The server's own explanation when it's one worth showing (an action
     *  that isn't the person's), otherwise the usual message. */
    private saveErrorMessage(err: unknown, fallback: string): string {
        return err instanceof Error && err.message === NOT_THEIR_ACTION ? NOT_THEIR_ACTION : fallback;
    }

    /** The toolbar's + (one person shown) — only on a planner this user may change. */
    get canAddToSubject(): boolean {
        return this.canEditPlannerOf(this.subjectUserId());
    }

    /** The block can't be changed here: someone else's planner without the
     *  Planner role (blockModalViewOnly), or a past day (blockModalLocked).
     *  Fields, Save, Duplicate and Delete go by this. */
    get blockModalReadOnly(): boolean {
        return this.blockModalViewOnly || this.blockModalLocked;
    }

    /** Someone else's planner, without the Planner role — not even Log as
     *  time (the entry would go on their timesheet). */
    get blockModalViewOnly(): boolean {
        return !!this.blockModal && !this.canEditPlannerOf(this.blockModal.userId);
    }

    /** On a past day — locked, but can still be logged as time. */
    get blockModalLocked(): boolean {
        return !!this.blockModal && this.isPastDay(this.blockModal.day);
    }

    /** The right-clicked block is on a past day. */
    get contextMenuLocked(): boolean {
        if (!this.contextMenu) return false;
        return this.isPastDay(this.findBlockEvent(this.contextMenu.plannedBlockId)?.start);
    }

    /** The right-clicked block is on a planner this user may change. */
    get contextMenuCanEdit(): boolean {
        if (!this.contextMenu) return false;
        return this.canEditPlannerOf(this.findBlock(this.contextMenu.plannedBlockId)?.userId);
    }

    /** The right-clicked block's action — none (0) once deleted. */
    get contextMenuJobActionId(): number {
        if (!this.contextMenu) return 0;
        return (this.findBlockEvent(this.contextMenu.plannedBlockId)?.extendedProps?.['jobActionId'] as number) || 0;
    }

    /** The right-clicked block's action has since been deleted. */
    get contextMenuActionDeleted(): boolean {
        if (!this.contextMenu) return false;
        return !!this.findBlockEvent(this.contextMenu.plannedBlockId)?.extendedProps?.['isActionDeleted'];
    }

    closeContextMenu(): void {
        // Closed with Escape: no press to clear the block's shade (see
        // _documentMousedownCapture).
        if (this.contextMenu) this.blurFocusedBlock(null);
        this.contextMenu = null;
        this._changeRef.markForCheck(); // see openContextMenu
    }

    // Closes on any outside click/tap - mousedown rather than click so it
    // fires ahead of whatever the click itself triggers (same reasoning as
    // Timer's own: a click handler elsewhere stopping propagation can't
    // prevent mousedown from bubbling here first).
    // Same for the edit popover: a press anywhere outside it closes it —
    // or, if its start/duration have been edited, asks first (see
    // requestCloseBlockModal). Pressing another block with no edits pending
    // closes this one, then that block's own click opens its popover.
    //
    // Listened for in the capture phase (setupDocumentMousedown), ahead of
    // everything else on the page: when the confirmation is about to show,
    // the press is stopped right there, so it can't also start dragging a
    // block (FullCalendar listens for mousedown too), navigate the week, etc.
    private _documentMousedownCapture = (e: MouseEvent) => {
        // A right-clicked (or clicked) block keeps the browser's focus, and
        // FullCalendar darkens a focused block (.fc-event:focus) — a press
        // on an empty part of the page doesn't move focus anywhere, so the
        // shade stayed. Any press outside that block clears it.
        this.blurFocusedBlock(e.target as Node | null);
        // A press that closes an open popover only closes it — it doesn't
        // also open "Add planned block" on the calendar (onCalendarSelect),
        // same as the Timer calendar's own click-to-add.
        this._popoverOpenAtMousedown = !!(this.contextMenu || this.blockModal || this.addBlockOpen);
        // The pull-out card's ⋮ menu closes on any press outside it.
        if (this.pulloutMenuActionId != null && !(e.target as HTMLElement | null)?.closest('.PlannerPullout-menuWrap')) {
            this._ngZone.run(() => this.closePulloutMenu());
        }
        if (!this.contextMenu && !this.blockModal && !this.addBlockOpen && !this.viewMenuOpen) return;
        const target = e.target as HTMLElement | null;
        // Anything inside the confirmation handles itself.
        if (target?.closest('.PlannerDiscard')) return;
        this._ngZone.run(() => {
            // The "Week view ▾" dropdown closes on any press outside it.
            if (this.viewMenuOpen && !target?.closest('.Planner-viewSelector')) {
                this.viewMenuOpen = false;
                this._changeRef.markForCheck();
            }
            if (this.contextMenu && !target?.closest('.Planner-contextMenu')) {
                this.closeContextMenu();
            }
            // "Add planned block" closes on any press outside it, same as
            // the Timer's own add-entry popover. The + button toggles it
            // itself, so it's left alone here.
            // Asks first if anything's been chosen or changed (same as the
            // block popover below, and the Timer's own add-entry modal); the
            // press then stops there, so it can't also drag or select.
            if (this.addBlockOpen && !this.addBlockDiscardOpen
                && !target?.closest('.PlannerAddBlock') && !target?.closest('.Planner-addBtn')) {
                this.requestCloseAddBlock();
                if (this.addBlockDiscardOpen) {
                    e.preventDefault();
                    e.stopPropagation();
                }
            }
            if (this.blockModal && !this.blockModalDiscardOpen && !target?.closest('.PlannerModal')) {
                this.requestCloseBlockModal();
                if (this.blockModalDiscardOpen) {
                    e.preventDefault();
                    e.stopPropagation();
                }
            }
        });
    };

    /** Takes the focus off a focused calendar block (FullCalendar's dark
     *  focus shade) — unless the press is on that block itself. */
    private blurFocusedBlock(pressTarget: Node | null): void {
        const active = document.activeElement as HTMLElement | null;
        if (!active?.classList?.contains('fc-event')) return;
        if (pressTarget && active.contains(pressTarget)) return;
        active.blur();
    }

    // ── The closed panel's tab: middle of the screen, within the calendar ──
    // Fixed to the screen's right edge, at the middle of the screen — but
    // never above the calendar's top or below its bottom (it stays beside
    // the calendar on small screens, where the toolbar and filters can fill
    // the top of the screen). Worked out from the page's position, at most
    // once a frame on any scroll (the page's, or the calendar's own box)
    // and on resize. (Plain CSS can't keep it there: below the desktop
    // width the site's page wrapper stops `position: sticky` working.)
    private _handlePositionCleanup: (() => void) | null = null;

    private setupPulloutHandlePositioning(): void {
        let frame = 0;
        const schedule = () => {
            if (!frame) frame = requestAnimationFrame(() => {
                frame = 0;
                this.positionPulloutHandle();
                this.updateOverflowBadges();
            });
        };
        this._ngZone.runOutsideAngular(() => {
            document.addEventListener('scroll', schedule, true);
            window.addEventListener('resize', schedule);
        });
        this._handlePositionCleanup = () => {
            document.removeEventListener('scroll', schedule, true);
            window.removeEventListener('resize', schedule);
            if (frame) cancelAnimationFrame(frame);
        };
        setTimeout(() => this.positionPulloutHandle(), 0);
    }

    // ── "+N" counters for blocks above / below what's on screen ──
    // As on the Timer calendar (its updateOverflowIndicators): in each day's
    // column, how many blocks are hidden above the visible part (under the
    // pinned day header) and below it, as small "+N" pills at that edge.
    // Per calendar, each with its own visible part: below its day header,
    // and within the screen — or, where the calendars scroll in their own
    // box, within that box. Refreshed on scroll, resize and whenever the
    // blocks change (debounced — many mount at once).
    private _overflowTimer: any = null;

    private scheduleOverflowBadges(): void {
        clearTimeout(this._overflowTimer);
        this._overflowTimer = setTimeout(() => this.updateOverflowBadges(), 200);
    }

    private updateOverflowBadges(): void {
        const root = this.plannerBodyRef?.nativeElement;
        if (!root || this._destroyed) return;
        const area = root.querySelector('.Planner-peopleList--scroll') as HTMLElement | null;
        const areaRect = area?.getBoundingClientRect();
        const screenBottom = document.documentElement.clientHeight;

        root.querySelectorAll('.PlannerPerson-calendar').forEach((calNode) => {
            const cal = calNode as HTMLElement;
            const dayHeader = cal.querySelector('.fc-scrollgrid-section-header.fc-scrollgrid-section-sticky > *') as HTMLElement | null;
            const visibleTop = Math.max(dayHeader ? dayHeader.getBoundingClientRect().bottom : 0, areaRect ? areaRect.top : 0);
            const visibleBottom = Math.min(screenBottom, areaRect ? areaRect.bottom : screenBottom);
            const calRect = cal.getBoundingClientRect();
            // Not (or barely) on screen: no counters.
            const shown = visibleBottom - visibleTop > 40 && calRect.bottom > visibleTop && calRect.top < visibleBottom;

            cal.querySelectorAll('.fc-timegrid-col:not(.fc-timegrid-axis)').forEach((colNode) => {
                const frame = (colNode as HTMLElement).querySelector('.fc-timegrid-col-frame') as HTMLElement | null;
                if (!frame) return;
                let above = 0;
                let below = 0;
                if (shown) {
                    frame.querySelectorAll('.fc-timegrid-event-harness').forEach((h) => {
                        if (h.querySelector('.fc-event-mirror')) return; // the copy being dragged
                        const r = h.getBoundingClientRect();
                        if (r.bottom <= visibleTop) above++;
                        else if (r.top >= visibleBottom) below++;
                    });
                }
                const frameRect = frame.getBoundingClientRect();
                const place = (cls: string, count: number, y: number) => {
                    let badge = frame.querySelector(`:scope > .${cls}`) as HTMLElement | null;
                    if (!count) {
                        if (badge) badge.style.display = 'none';
                        return;
                    }
                    if (!badge) {
                        badge = document.createElement('div');
                        badge.className = `Planner-overflowBadge ${cls}`;
                        frame.appendChild(badge);
                    }
                    badge.textContent = `+${count}`;
                    badge.style.display = '';
                    badge.style.top = `${Math.round(Math.max(4, Math.min(frame.clientHeight - 24, y)))}px`;
                };
                place('Planner-overflowBadge--top', above, visibleTop - frameRect.top + 4);
                place('Planner-overflowBadge--bottom', below, visibleBottom - frameRect.top - 26);
            });
        });
    }

    private positionPulloutHandle(): void {
        const root = this.plannerRootRef?.nativeElement;
        const handle = root?.querySelector('.Planner-pulloutHandle:not(.Planner-pulloutHandle--open)') as HTMLElement | null;
        const calendar = root?.querySelector('.Planner-peopleList') as HTMLElement | null;
        if (!handle || !calendar) return;
        const r = calendar.getBoundingClientRect();
        // At the right edge of the planner area (on a job's page that's
        // beside the calendar, not the far edge of the screen).
        const body = this.plannerBodyRef?.nativeElement.getBoundingClientRect();
        if (body) {
            const fromRight = document.documentElement.clientWidth - body.right;
            handle.style.right = `${Math.max(0, Math.round(fromRight))}px`;
        }
        const half = handle.offsetHeight / 2;
        // The middle of the part of the calendar that's on screen — below
        // the site's pinned bars, above the bottom of the window.
        const visibleTop = Math.max(r.top, this.stickyChromeHeight());
        const visibleBottom = Math.min(r.bottom, document.documentElement.clientHeight);
        const middle = visibleBottom > visibleTop ? (visibleTop + visibleBottom) / 2 : r.top + r.height / 2;
        // Within the calendar — if it's shorter than the tab, its middle.
        const y = r.height < half * 2
            ? r.top + r.height / 2
            : Math.min(Math.max(middle, r.top + half), r.bottom - half);
        handle.style.top = `${Math.round(y)}px`;
    }

    private setupDocumentMousedown(): void {
        document.addEventListener('mousedown', this._documentMousedownCapture, true);
    }

    // Keyboard shortcuts while the block popover or "Add planned block" is
    // open — the same keys as the Timer's edit modal: Ctrl+Q cancels (as
    // its ✕ does — asking first if anything's been changed), Ctrl+Alt+S
    // saves (Save / Add; in the "Log as time" form, Log time). Only while
    // one of them is open, and not over its own "discard changes?" prompt.
    @HostListener('document:keydown', ['$event'])
    onPopoverShortcut(e: KeyboardEvent): void {
        if (!this.blockModal && !this.addBlockOpen) return;
        if (!e.ctrlKey || e.metaKey || typeof e.key !== 'string') return;
        const key = e.key.toLowerCase();
        const cancel = key === 'q' && !e.shiftKey && !e.altKey;
        const save = key === 's' && e.altKey && !e.shiftKey;
        if (!cancel && !save) return;
        e.preventDefault();
        if (this.blockModalDiscardOpen || this.addBlockDiscardOpen) return;

        if (this.blockModal) {
            if (cancel) {
                this.requestCloseBlockModal();
            } else if (this.logFormOpen) {
                this.submitLog();
            } else if (!this.blockModalReadOnly) {
                this.blockModalSave();
            }
        } else if (cancel) {
            this.requestCloseAddBlock();
        } else {
            this.submitAddBlock();
        }
        this._changeRef.markForCheck();
    }

    // Innermost first, same order as the Timer's own Escape handling: the
    // confirmation ("Go back"), the ⋮ dropdown, then the popover itself.
    @HostListener('document:keydown.escape')
    onEscapeKey(): void {
        if (this.pulloutMenuActionId != null) {
            this.closePulloutMenu();
        } else if (this.contextMenu) {
            this.closeContextMenu();
        } else if (this.viewMenuOpen) {
            this.viewMenuOpen = false;
        } else if (this.addBlockDayPickerOpen) {
            this.addBlockDayPickerOpen = false;
        } else if (this.addBlockPickerOpen && this.addBlockActionId) {
            // Back to the chosen action; with none chosen yet, Escape
            // closes the whole popover instead.
            this.addBlockPickerOpen = false;
        } else if (this.addBlockDiscardOpen) {
            this.addBlockDiscardGoBack();
        } else if (this.addBlockOpen) {
            this.requestCloseAddBlock();
        } else if (this.blockModalDiscardOpen) {
            this.blockModalDiscardGoBack();
        } else if (this.blockModalDayPickerOpen) {
            this.blockModalDayPickerOpen = false;
        } else if (this.blockModalPickerOpen) {
            this.blockModalPickerOpen = false;
        } else if (this.logTimerow?.handleEscape()) {
            // The log form's date picker was open — that's all Escape closes.
            this._changeRef.markForCheck();
        } else if (this.blockModalShowDropdown) {
            this.blockModalShowDropdown = false;
        } else if (this.blockModal) {
            this.requestCloseBlockModal();
        } else if (this.highlightActionId != null) {
            this.clearHighlight();
        }
    }

    /** Clamp the menu inside the viewport — flips above the click when
     *  there isn't enough vertical room below, and shifts left when there
     *  isn't enough horizontal room to the right. Mirrors Timer's own
     *  computeContextMenuPosition, sized for this menu's three items
     *  (Duplicate, Go to job, Delete). */
    /** Re-places the open menu from its real, drawn size, then shows it. */
    private fitContextMenu(): void {
        const menu = this.contextMenu;
        if (!menu || this._destroyed) return;
        const el = this.plannerRootRef?.nativeElement.querySelector('.Planner-contextMenu') as HTMLElement | null;
        const { top, left } = this.computeContextMenuPosition(menu.clientX, menu.clientY, el?.offsetWidth, el?.offsetHeight);
        const hostRect = this.plannerRootRef?.nativeElement.getBoundingClientRect();
        this.contextMenu = { ...menu, top: top - (hostRect?.top ?? 0), left: left - (hostRect?.left ?? 0), ready: true };
        this._changeRef.markForCheck();
    }

    private computeContextMenuPosition(clientX: number, clientY: number, width?: number, height?: number): { top: number; left: number } {
        // Its drawn size when known (fitContextMenu); else the largest it
        // gets — six items and two dividers.
        const MENU_W = width || 170;
        const MENU_H = height || 220;
        const PAD = 8;
        const vw = document.documentElement.clientWidth;
        const vh = document.documentElement.clientHeight;
        let left = clientX;
        if (left + MENU_W > vw - PAD) left = vw - MENU_W - PAD;
        left = Math.max(PAD, left);
        let top = clientY;
        if (top + MENU_H > vh - PAD) top = clientY - MENU_H;
        top = Math.max(PAD, top);
        return { top, left };
    }

    // No confirm() prompt — deletes immediately and offers an undo via the
    // toast instead, same UX as the Timer calendar's own delete.
    // "Edit" — the same popover a click on the block opens, where the menu was.
    contextEdit(): void {
        if (!this.contextMenu) return;
        const { plannedBlockId, clientX, clientY } = this.contextMenu;
        this.closeContextMenu();
        this.openBlockModal(plannedBlockId, clientX, clientY);
    }

    // Renames the action itself (as editing its name in the Actions list
    // does). The update replaces the whole action, so the current one is
    // loaded first and only its name changed — nothing else it holds is
    // touched. Every block of it then shows the new name.
    private renameAction(jobActionId: number, title: string): void {
        if (!jobActionId) return;
        this._actionService.get(jobActionId).pipe(
            switchMap((result) => {
                if (!result.success || !result.jobAction) throw new Error(result.error);
                return this._actionService.update(Object.assign(result.jobAction, { title }));
            })
        ).subscribe({
            next: () => {
                this.unplannedActions
                    .filter((a) => a.jobActionId === jobActionId)
                    .forEach((a) => (a.title = title));
                // Its blocks may be on anyone's planner shown.
                this._plannerCache.invalidateAllBlocks(false);
                if (this._destroyed) return;
                this.setUnplannedActions([...this.unplannedActions]);
                this.refetchAllCalendars();
            },
            error: () => alert('Could not rename this action. Please try again.'),
        });
    }

    contextDelete(): void {
        if (!this.contextMenu || this.contextMenuLocked) return;
        const plannedBlockId = this.contextMenu.plannedBlockId;
        this.closeContextMenu();
        this.deleteBlock(plannedBlockId);
    }

    // An exact copy — same day, start and length — like the popover's
    // Duplicate with no edits, and Timer's own right-click Duplicate.
    contextDuplicate(): void {
        if (!this.contextMenu || this.contextMenuLocked) return;
        const plannedBlockId = this.contextMenu.plannedBlockId;
        this.closeContextMenu();
        const event = this.findBlockEvent(plannedBlockId);
        if (!event?.start) return;
        const end = event.end ?? new Date(event.start.getTime() + 60 * 60000);
        const durationMinutes = Math.round((end.getTime() - event.start.getTime()) / 60000);
        this.duplicateBlock(plannedBlockId, new Date(event.start), durationMinutes);
    }

    /** The right-clicked block is busy time (no action or job). */
    get contextMenuIsBusy(): boolean {
        if (!this.contextMenu) return false;
        return !!this.findBlockEvent(this.contextMenu.plannedBlockId)?.extendedProps?.['isBusy'];
    }

    get contextMenuJobId(): number | null {
        if (!this.contextMenu) return null;
        return (this.findBlockEvent(this.contextMenu.plannedBlockId)?.extendedProps?.['jobId'] as number) || null;
    }

    // "Edit action" — the action itself (name, dates, owners, status…), in
    // the page's own edit-action popup; "Edit block" is this block's time.
    contextEditAction(): void {
        const jobActionId = this.contextMenuJobActionId;
        this.closeContextMenu();
        if (jobActionId) this.editAction.emit({ jobActionId });
    }

    blockModalEditAction(e: MouseEvent): void {
        e.stopPropagation();
        // The block's own action (not one picked meanwhile and not saved).
        const jobActionId = this.blockModal?.originalJobActionId;
        this.closeBlockModal();
        if (jobActionId) this.editAction.emit({ jobActionId });
    }

    pulloutEditAction(action: JobAction): void {
        this.closePulloutMenu();
        this.editAction.emit({ jobActionId: action.jobActionId });
    }

    // A panel card's owner avatars: the edit-action dialog on its owners,
    // as clicking the Owner column's avatars on the Actions list does.
    pulloutEditOwners(action: JobAction): void {
        this.closePulloutMenu();
        this.pulloutOwnersOpenId = null;
        this.editAction.emit({ jobActionId: action.jobActionId, propertyKey: 'user' });
    }

    // The card whose owners' "+N" list is open (one at a time) — the avatar
    // component leaves its open/closed state to its parent, as on the
    // Actions list (action-table's onAvatarDropdown).
    pulloutOwnersOpenId: number | null = null;

    onPulloutOwnersDropdown(action: JobAction, isOpening: boolean): void {
        if (isOpening) {
            this.pulloutOwnersOpenId = action.jobActionId;
        } else if (this.pulloutOwnersOpenId === action.jobActionId) {
            this.pulloutOwnersOpenId = null;
        }
    }

    /** After the page's edit-action popup closes: its name, job, dates,
     *  owners or status may have changed. With the saved action at hand,
     *  only what it touches: its own blocks take its new details straight
     *  away (updateActionDetails, see the cache) and only the calendars
     *  showing them redraw, from what's already loaded — no reload of
     *  everyone's blocks, so no wait and no loading spinner (a status,
     *  owner or "Waiting for" change touches no block at all). The panel
     *  re-lists quietly, as it always does. Without it, everything
     *  reloads, as before. (Blocks removed after completing an action are
     *  handled on their own — see blocksChangedElsewhere$.) */
    refreshAfterActionEdit(action?: JobAction | null): void {
        if (this._destroyed) return;
        if (!action?.jobActionId) {
            this._plannerCache.invalidateAllBlocks(false);
            this.refetchAllCalendars();
            this.loadUnplannedActions(this._requestToken);
            return;
        }
        this._plannerCache.updateActionDetails(action);
        this.allApis().forEach((api) => {
            const showsIt = api.getEvents().some((ev) => !ev.extendedProps?.['isBusy']
                && ev.extendedProps?.['jobActionId'] === action.jobActionId);
            if (showsIt) api.refetchEvents();
        });
        this.loadUnplannedActions(this._requestToken);
    }

    contextGoToJob(): void {
        const jobId = this.contextMenuJobId;
        this.closeContextMenu();
        this.goToJob(jobId);
    }

    // Shared by the right-click menu (contextDelete) and the edit popover's
    // ⋮ menu (blockModalDelete).
    private deleteBlock(plannedBlockId: number): void {
        const found = this.findBlock(plannedBlockId);
        if (!found) return;
        const { event, userId } = found;
        if (this.isPastDay(event.start)) return; // locked

        // Snapshot everything needed to put it back — visually if the
        // delete call fails, or via a fresh create if the user hits undo
        // after it succeeds (the old database row is genuinely gone by
        // then, so undo can't just resurrect it by id).
        const snapshot: EventInput = {
            id: event.id,
            title: event.title,
            start: event.start,
            end: event.end,
            backgroundColor: event.backgroundColor,
            borderColor: event.borderColor,
            extendedProps: { ...event.extendedProps },
        };
        const jobActionId = event.extendedProps?.['jobActionId'];
        const start = event.start;
        const end = event.end;
        const durationMinutes = (start && end) ? Math.round((end.getTime() - start.getTime()) / 60000) : 60;

        // Optimistic removal — same as the Timer calendar's own delete.
        event.remove();

        if (userId) this._plannerCache.invalidateBlocks(userId);
        this._plannedBlockService.deletePlannedBlock(plannedBlockId).subscribe({
            next: () => {
                if (userId) this._plannerCache.invalidateBlocks(userId);
                if (this._destroyed) return;
                this._timerToastService.show('The planned block was deleted.', () => {
                    this.restoreDeletedBlock(snapshot, jobActionId, userId, durationMinutes);
                });
            },
            error: () => {
                if (userId) this._plannerCache.invalidateBlocks(userId);
                if (this._destroyed) return;
                // Delete didn't actually happen server-side — put it back
                // (into the event source, see eventReceive for why).
                this.apiFor(userId)?.addEvent(snapshot, true);
                alert('Could not remove this planned block. Please try again.');
            },
        });
    }

    // Re-creates the block server-side (the deleted row is gone for good,
    // so this gets a fresh id, same as the Timer calendar's own undo) and
    // re-adds it to the grid with the snapshot's visual properties.
    private restoreDeletedBlock(
        snapshot: EventInput,
        jobActionId: number,
        userId: string | undefined,
        durationMinutes: number
    ): void {
        if (!userId || !snapshot.start) {
            this.calendarComponent?.getApi()?.addEvent(snapshot); // best-effort UI-only restore (no person known)
            return;
        }
        this._plannerCache.invalidateBlocks(userId);
        const busy: { isBusy?: boolean; busyTitle?: string } = snapshot.extendedProps?.['isBusy']
            ? { isBusy: true, busyTitle: (snapshot.extendedProps?.['busyTitle'] as string) || '' }
            : {};
        this._plannedBlockService
            .createPlannedBlock({ jobActionId: busy.isBusy ? 0 : jobActionId, userId, start: snapshot.start as Date, durationMinutes, ...busy })
            .subscribe({
                next: (block) => {
                    this._plannerCache.invalidateBlocks(userId);
                    if (this._destroyed) return;
                    this.apiFor(userId)?.addEvent({
                        ...snapshot,
                        id: `planned-block-${block.plannedBlockId}`,
                        extendedProps: { ...snapshot.extendedProps, plannedBlockId: block.plannedBlockId },
                    }, true); // into the event source, see eventReceive
                },
                error: (err) => {
                    this._plannerCache.invalidateBlocks(userId);
                    if (this._destroyed) return;
                    alert(this.saveErrorMessage(err, 'Could not undo the delete. Please try again.'));
                },
            });
    }

    // ─── Block edit popover — same look and pattern as the Timer
    // calendar's own event modal (timer-tracker.component: handleEventClick
    // / positionModalNear / duplicateEvent / goToJob), trimmed to what a
    // planned block has: Duplicate, ⋮ (Go to job / Delete), and start time
    // + duration in 30-minute steps. Duplicated rather than shared, same
    // Timer-isolation reasoning as the rest of this file. ─────────────────

    private static readonly BLOCK_STEP_MINUTES = 30;
    private static readonly LAST_START_MINUTES = 24 * 60 - PlannerComponent.BLOCK_STEP_MINUTES; // 23:30

    /** A block on screen and whose planner it's on (in the several-people
     *  view it could be anyone's). */
    private findBlock(plannedBlockId: number): { event: EventApi; userId: string } | undefined {
        for (const person of this.people) {
            const event = this.apiFor(person.userId)
                ?.getEvents()
                .find((ev) => ev.extendedProps?.['plannedBlockId'] === plannedBlockId);
            if (event) return { event, userId: person.userId };
        }
        return undefined;
    }

    private findBlockEvent(plannedBlockId: number): EventApi | undefined {
        return this.findBlock(plannedBlockId)?.event;
    }

    private openBlockModal(plannedBlockId: number, clientX: number, clientY: number): void {
        const found = this.findBlock(plannedBlockId);
        const event = found?.event;
        if (!event?.start) return;
        this.closeContextMenu();
        this.addBlockOpen = false;
        this.addBlockDayPickerOpen = false;

        const props = event.extendedProps || {};
        const start = event.start;
        const end = event.end ?? new Date(start.getTime() + 60 * 60000);
        const day = new Date(start);
        day.setHours(0, 0, 0, 0);
        const startMinutes = start.getHours() * 60 + start.getMinutes();
        const durationMinutes = Math.round((end.getTime() - start.getTime()) / 60000);

        const { top, left } = this.computeBlockModalPosition(clientX, clientY);
        const hostRect = this.plannerRootRef?.nativeElement.getBoundingClientRect();
        this.blockModal = {
            plannedBlockId,
            userId: found.userId,
            top: top - (hostRect?.top ?? 0),
            left: left - (hostRect?.left ?? 0),
            jobActionId: props['jobActionId'] as number,
            originalJobActionId: props['jobActionId'] as number,
            actionTitle: (props['actionTitle'] as string) || '',
            jobId: (props['jobId'] as number) || null,
            jobNumber: (props['jobNumber'] as number) || null,
            jobTitle: (props['jobTitle'] as string) || '',
            clientName: (props['clientName'] as string) || '',
            titleColor: (props['titleColor'] as string) || '',
            isBillable: !!props['isBillable'],
            isActionDeleted: !!props['isActionDeleted'],
            isBusy: !!props['isBusy'],
            isLogged: !!props['isLogged'],
            day,
            originalStartMinutes: startMinutes,
            originalDurationMinutes: durationMinutes,
        };
        this.blockModalShowDropdown = false;
        this.blockModalTitleText = this.blockModal.isBusy
            ? ((props['busyTitle'] as string) || '')
            : this.capitalizeFirst(this.blockModal.actionTitle);
        this.blockModalTitleOriginal = this.blockModalTitleText;
        this.blockModalPickerOpen = false;
        this.blockModalPickerSearch = '';
        this.blockModalPickerExpandedJobIds = new Set<number>();
        this.blockModalDiscardOpen = false;
        this.blockModalSaving = false;
        this.blockModalDay = new Date(day);
        this.blockModalDayPickerOpen = false;
        this.resetLogForm();
        this.setBlockModalStart(startMinutes);
        this.setBlockModalDuration(durationMinutes);
        // "Changed" is measured against what the fields show on opening
        // (already rounded to 30 mins), not the raw stored times — a block
        // that isn't on a half-hour (e.g. created outside the planner)
        // would otherwise count as edited the moment it opens.
        this.blockModal.originalStartMinutes = this.blockModalStartMinutes;
        this.blockModal.originalDurationMinutes = this.blockModalDurationMinutes;
        this._changeRef.markForCheck(); // see openContextMenu
    }

    /** Closes unconditionally — after Save/Duplicate/Delete/Go to job, a
     *  week change, or "Discard" in the confirmation. */
    closeBlockModal(): void {
        this.blockModal = null;
        this.blockModalShowDropdown = false;
        this.blockModalPickerOpen = false;
        this.blockModalDiscardOpen = false;
        this.blockModalDayPickerOpen = false;
        this.resetLogForm();
        this._changeRef.markForCheck(); // see openContextMenu
    }

    /** User-initiated close (✕, Escape, clicking outside) — asks first if
     *  the start/duration (or an open Log-as-time form) have been edited,
     *  same as the Timer modal's
     *  closeEditModal → "Discard unsaved changes?". */
    requestCloseBlockModal(): void {
        if (!this.blockModal) return;
        this.commitBlockModalStart();
        this.commitBlockModalDuration();
        if (this.blockModalHasChanges || this.logFormHasChanges) {
            this.blockModalShowDropdown = false;
            this.blockModalDiscardOpen = true;
            this._changeRef.markForCheck();
            return;
        }
        this.closeBlockModal();
    }

    // "Go back" — keep editing.
    blockModalDiscardGoBack(): void {
        this.blockModalDiscardOpen = false;
        this._changeRef.markForCheck();
    }

    // Nothing was saved yet (Save is the only thing that changes the
    // block), so discarding is just closing.
    blockModalDiscardConfirm(): void {
        this.closeBlockModal();
    }

    // Rendered inside this component's template but moved to <body> on
    // open, so the dimmed backdrop covers the whole window like Timer's
    // does (position: fixed) — inside .Planner, some ancestor in this
    // page's layout confines fixed-position elements (see
    // .Planner-contextMenu). Angular still removes it from <body> when the
    // *ngIf closes it, or when this component is destroyed.
    @ViewChild('discardOverlay') set discardOverlayRef(ref: ElementRef<HTMLElement> | undefined) {
        if (ref && ref.nativeElement.parentElement !== document.body) {
            document.body.appendChild(ref.nativeElement);
        }
    }

    /** Same placement rules as Timer's positionModalNear (opens just below
     *  the click, flips above it near the bottom, stays inside the
     *  viewport), returning viewport coords — openBlockModal then makes
     *  them relative to .Planner, same as the context menu. */
    private computeBlockModalPosition(clientX: number, clientY: number): { top: number; left: number } {
        const MODAL_W = 460;
        const MODAL_H = 200;
        const PAD = 12;
        const GAP = 6;
        const vw = document.documentElement.clientWidth;
        const vh = document.documentElement.clientHeight;
        let left = clientX;
        if (left + MODAL_W > vw - PAD) left = vw - MODAL_W - PAD;
        left = Math.max(PAD, left);
        let top = clientY + GAP;
        if (top + MODAL_H > vh - PAD) top = clientY - MODAL_H + 80;
        top = Math.max(PAD, Math.min(top, vh - MODAL_H - PAD));
        return { top, left };
    }

    get blockModalHasChanges(): boolean {
        return !!this.blockModal
            && (this.blockModalStartMinutes !== this.blockModal.originalStartMinutes
                || this.blockModalDurationMinutes !== this.blockModal.originalDurationMinutes
                || this.blockModalDay.toDateString() !== this.blockModal.day.toDateString()
                || this.blockModal.jobActionId !== this.blockModal.originalJobActionId
                || this.blockModalTitleChanged);
    }

    /** The action's name has been edited. */
    get blockModalTitleChanged(): boolean {
        return !!this.blockModal && this.blockModalTitleText.trim() !== this.blockModalTitleOriginal.trim();
    }

    /** The name box is empty — Save is disabled, with a message. */
    get blockModalTitleEmpty(): boolean {
        // (Busy time can go without one — it's then shown as "Busy".)
        return !!this.blockModal && !this.blockModal.isActionDeleted && !this.blockModal.isBusy && !this.blockModalTitleText.trim();
    }

    onBlockModalTitleKeydown(e: KeyboardEvent): void {
        if (e.key === 'Enter') {
            e.preventDefault();
            this.blockModalSave();
        }
    }

    // ── Action picker (the project pill) ──
    // To correct a block's action without deleting it and dragging another
    // one out. Lists what the pull-out lists (both its tabs) for the
    // block's person: with several people shown the pull-out has everyone's,
    // so just the ones that are theirs — owned by them or their team, or
    // waiting on them (the same ways the Owner filter matches an action).

    /** The block popover's "Owner: …" line — shown when the block's action
     *  isn't that person's own (e.g. helping on a colleague's action): its
     *  owners, people and teams. Only for actions in the panel (the rest
     *  aren't loaded here); none for a deleted action. */
    get blockModalOwnerNote(): string {
        const modal = this.blockModal;
        if (!modal || modal.isActionDeleted || !modal.jobActionId) return '';
        const action = this.unplannedActions.find((a) => a.jobActionId === modal.jobActionId);
        if (!action || this.isActionOf(action, modal.userId)) return '';
        const owners = this.pulloutOwners(action);
        const names = [...owners.users.map((u) => u.name), ...owners.teams].filter((n) => !!n);
        if (!names.length) return '';
        return `${names.length > 1 ? 'Owners' : 'Owner'}: ${names.join(', ')}`;
    }

    get blockModalActionChoices(): JobAction[] {
        return this.blockModal ? this.pulloutActionsFor(this.blockModal.userId) : [];
    }

    /** The pull-out's actions (both its tabs) for one person: with one
     *  person shown, all of them; with several, just the ones that are
     *  theirs — owned by them or their team, or waiting on them (the same
     *  ways the Owner filter matches an action). In the same order as the
     *  "Add planned block" picker. Memoised (one person at a time). */
    pulloutActionsFor(userId: string): JobAction[] {
        return this.pulloutActionSectionsFor(userId).all;
    }

    /** The same, as two sections: the person's own actions (owned by them
     *  or their team, or waiting on them), then — on the user's own planner
     *  only — every other action in the panel, which can go there too
     *  (helping on a colleague's action; see canPlanActionOn). The pickers
     *  only open on planners the user may change. Memoised (one person at
     *  a time). */
    private pulloutActionSectionsFor(userId: string): { own: JobAction[]; others: JobAction[]; all: JobAction[] } {
        const memo = this._blockModalChoicesMemo;
        if (memo && memo.actions === this.unplannedActions && memo.version === this._plannedInfoVersion
            && memo.userId === userId && memo.people === this.people) {
            return memo.sections;
        }
        const { unplanned, planned } = this.pulloutGroups;
        const inPullout = [...unplanned, ...planned.filter((a) => !unplanned.includes(a))];
        const own = this.sortAddBlockActions(inPullout.filter((a) => this.isActionOf(a, userId)));
        // Everyone else's actions only on the user's own planner — someone
        // else's takes just theirs (canPlanActionOn).
        const others = userId === this._loginService.userId
            ? this.sortAddBlockActions(inPullout.filter((a) => !this.isActionOf(a, userId)))
            : [];
        const sections = { own, others, all: [...own, ...others] };
        this._blockModalChoicesMemo = { actions: this.unplannedActions, version: this._plannedInfoVersion, userId, people: this.people, sections };
        return sections;
    }

    /** A picker's rows (groupActionsByJob), in its two sections — headed
     *  "Your actions" (or "<Name>'s actions") and "Other people's actions"
     *  when there are both. */
    private actionSectionGroups(userId: string, term: string): AddBlockJobGroup[] {
        const { own, others } = this.pulloutActionSectionsFor(userId);
        const ownGroups = this.groupActionsByJob(own, term);
        const otherGroups = this.groupActionsByJob(others, term);
        if (ownGroups.length && otherGroups.length) {
            const name = this.personName(userId).split(' ')[0];
            const ownLabel = userId === this._loginService.userId ? 'Your actions' : `${name || 'Their'}’s actions`;
            ownGroups[0] = { ...ownGroups[0], sectionLabel: ownLabel };
            otherGroups[0] = { ...otherGroups[0], sectionLabel: 'Other people’s actions' };
        }
        return [...ownGroups, ...otherGroups];
    }

    /** The picker's rows — see groupActionsByJob. Memoised. */
    get blockModalActionGroups(): AddBlockJobGroup[] {
        const choices = this.blockModalActionChoices;
        const term = this.blockModalPickerSearch.trim().toLowerCase();
        const memo = this._blockModalGroupsMemo;
        if (memo && memo.actions === choices && memo.term === term) return memo.groups;
        const groups = this.blockModal ? this.actionSectionGroups(this.blockModal.userId, term) : [];
        this._blockModalGroupsMemo = { actions: choices, term, groups };
        return groups;
    }

    toggleBlockModalPicker(): void {
        this.blockModalPickerOpen = !this.blockModalPickerOpen;
        if (!this.blockModalPickerOpen) return;
        this.blockModalShowDropdown = false;
        this.blockModalDayPickerOpen = false;
        this.blockModalPickerSearch = '';
        this.placePicker('modal');
        setTimeout(() => {
            const input = this.plannerRootRef?.nativeElement
                .querySelector('.PlannerModal-pickerSearchInput') as HTMLInputElement | null;
            input?.focus();
        }, 0);
    }

    isBlockModalJobExpanded(group: AddBlockJobGroup): boolean {
        return group.matchedByAction || this.blockModalPickerExpandedJobIds.has(group.jobId);
    }

    toggleBlockModalJob(jobId: number, e?: MouseEvent): void {
        e?.stopPropagation();
        if (this.blockModalPickerExpandedJobIds.has(jobId)) this.blockModalPickerExpandedJobIds.delete(jobId);
        else this.blockModalPickerExpandedJobIds.add(jobId);
    }

    // As in "Add planned block": a job with one action picks it straight
    // away, otherwise it opens to choose.
    onBlockModalJobClick(group: AddBlockJobGroup): void {
        if (group.actions.length === 1) {
            this.selectBlockModalAction(group.actions[0]);
        } else {
            this.toggleBlockModalJob(group.jobId);
        }
    }

    // Shown straight away; saved (with the rest) on Save.
    selectBlockModalAction(action: JobAction): void {
        if (!this.blockModal) return;
        this.blockModal.jobActionId = action.jobActionId;
        this.blockModal.actionTitle = action.title || '';
        this.blockModal.jobId = action.jobId ?? null;
        this.blockModal.jobNumber = action.jobNumber ?? null;
        this.blockModal.jobTitle = action.jobTitle || '';
        this.blockModal.clientName = action.clientName || '';
        this.blockModal.titleColor = plannerColorForJobNumber(action.jobNumber).titleColor;
        this.blockModal.isBillable = !!action.isBillable;
        // Its own name — shown as it is, not counted as a rename.
        this.blockModalTitleText = this.capitalizeFirst(action.title);
        this.blockModalTitleOriginal = this.blockModalTitleText;
        this.blockModalPickerOpen = false;
    }

    // Day — the date link beside the start time. Opens below the time row,
    // or above it when there isn't room below (same as the log form's).
    toggleBlockModalDayPicker(e: MouseEvent): void {
        this.blockModalDayPickerOpen = !this.blockModalDayPickerOpen;
        if (!this.blockModalDayPickerOpen) return;
        this.blockModalShowDropdown = false;
        const PICKER_H = 230;
        const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
        this.blockModalDayPickerUpward = rect.bottom + PICKER_H > document.documentElement.clientHeight - 20;
    }

    onBlockModalDayPicked(day: Date): void {
        this.blockModalDay = day;
        this.blockModalDayPickerOpen = false;
    }

    toggleBlockModalDropdown(): void {
        this.blockModalShowDropdown = !this.blockModalShowDropdown;
    }

    // ── Start time / duration — 30-minute steps only ──

    private roundToStep(minutes: number): number {
        const step = PlannerComponent.BLOCK_STEP_MINUTES;
        return Math.round(minutes / step) * step;
    }

    private setBlockModalStart(minutes: number): void {
        this.blockModalStartMinutes = Math.min(
            PlannerComponent.LAST_START_MINUTES,
            Math.max(0, this.roundToStep(minutes))
        );
        const h = Math.floor(this.blockModalStartMinutes / 60);
        const m = this.blockModalStartMinutes % 60;
        this.blockModalStartText = `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
    }

    // Minimum 30 mins, no maximum (per the spec) — a block may run past
    // midnight and still belongs to its start day.
    private setBlockModalDuration(minutes: number): void {
        this.blockModalDurationMinutes = Math.max(PlannerComponent.BLOCK_STEP_MINUTES, this.roundToStep(minutes));
        // "1h 30m", not "1:30" — which could be read as a minute and a half.
        this.blockModalDurationText = formatDurationHM(this.blockModalDurationMinutes);
    }

    /** "9", "930", "0930", "9:30", "9.30" → minutes since midnight, or
     *  null if it isn't a valid time. */
    private parseStartText(text: string): number | null {
        const t = (text || '').trim();
        let h: number;
        let m: number;
        const parts = t.split(/[:.]/);
        if (parts.length === 2) {
            h = parseInt(parts[0], 10);
            m = parseInt(parts[1] || '0', 10);
        } else if (/^\d{1,4}$/.test(t)) {
            if (t.length <= 2) { h = parseInt(t, 10); m = 0; }
            else { h = parseInt(t.slice(0, t.length - 2), 10); m = parseInt(t.slice(-2), 10); }
        } else {
            return null;
        }
        if (!Number.isFinite(h) || !Number.isFinite(m) || h < 0 || h > 23 || m < 0 || m > 59) return null;
        return h * 60 + m;
    }

    /** "1h 30m", "1h", "30m", or the older "1:30" / "1.5" / "2" (hours) →
     *  minutes, or null if it isn't a valid duration (parseDurationHM). */
    private parseDurationText(text: string): number | null {
        return parseDurationHM(text);
    }

    // Parse what was typed, round to the nearest 30 mins, and show the
    // tidied value — or put the last good value back if it can't be read.
    commitBlockModalStart(): void {
        const parsed = this.parseStartText(this.blockModalStartText);
        this.setBlockModalStart(parsed ?? this.blockModalStartMinutes);
    }

    commitBlockModalDuration(): void {
        const parsed = this.parseDurationText(this.blockModalDurationText);
        this.setBlockModalDuration(parsed ?? this.blockModalDurationMinutes);
    }

    // ↑/↓ step by 30 mins, Enter saves.
    onBlockModalStartKeydown(e: KeyboardEvent): void {
        if (this.blockModalReadOnly) return; // view-only: ↑/↓ don't change it
        const step = PlannerComponent.BLOCK_STEP_MINUTES;
        if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
            e.preventDefault();
            this.commitBlockModalStart();
            this.setBlockModalStart(this.blockModalStartMinutes + (e.key === 'ArrowUp' ? step : -step));
        } else if (e.key === 'Enter') {
            e.preventDefault();
            this.blockModalSave();
        }
    }

    onBlockModalDurationKeydown(e: KeyboardEvent): void {
        if (this.blockModalReadOnly) return; // view-only: ↑/↓ don't change it
        const step = PlannerComponent.BLOCK_STEP_MINUTES;
        if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
            e.preventDefault();
            this.commitBlockModalDuration();
            this.setBlockModalDuration(this.blockModalDurationMinutes + (e.key === 'ArrowUp' ? step : -step));
        } else if (e.key === 'Enter') {
            e.preventDefault();
            this.blockModalSave();
        }
    }

    // ── Actions ──

    // Optimistic, same as a drag/resize (persistBlockMove): the block moves
    // straight away and is put back if the save fails. Its day, start and
    // length can all change (the day via the date link — onto another week
    // too, in which case it just leaves the week on screen).
    blockModalSave(): void {
        if (!this.blockModal || this.blockModalSaving) return;
        if (this.blockModalReadOnly) {
            this.closeBlockModal();
            return;
        }
        if (this.blockModalTitleEmpty) return; // Save is disabled — see the message
        this.commitBlockModalStart();
        this.commitBlockModalDuration();
        if (!this.blockModalHasChanges) {
            this.closeBlockModal();
            return;
        }

        // Renaming the action (its name box) — saved on its own, after
        // which every block of it shows the new name. Busy time: the name
        // box is the block's own title, saved with the block below.
        const isBusy = this.blockModal.isBusy;
        const busyTitleChanged = isBusy && this.blockModalTitleChanged;
        if (this.blockModalTitleChanged && !isBusy) {
            this.renameAction(this.blockModal.jobActionId, this.blockModalTitleText.trim());
        }
        const blockChanged = this.blockModalStartMinutes !== this.blockModal.originalStartMinutes
            || this.blockModalDurationMinutes !== this.blockModal.originalDurationMinutes
            || this.blockModalDay.toDateString() !== this.blockModal.day.toDateString()
            || this.blockModal.jobActionId !== this.blockModal.originalJobActionId
            || busyTitleChanged;
        if (!blockChanged) {
            this.closeBlockModal();
            return;
        }

        const plannedBlockId = this.blockModal.plannedBlockId;
        const event = this.findBlockEvent(plannedBlockId);
        // The action as shown — another one if changed via the picker.
        const jobActionId = this.blockModal.jobActionId;
        const actionChanged = jobActionId !== this.blockModal.originalJobActionId;
        // The block's own person — stays on their planner.
        const userId = this.blockModal.userId;
        if (!event?.start || (!jobActionId && !this.blockModal.isActionDeleted && !isBusy) || !userId) {
            this.closeBlockModal();
            return;
        }

        const oldStart = event.start;
        const oldEnd = event.end;
        const start = new Date(this.blockModalDay);
        start.setHours(0, this.blockModalStartMinutes, 0, 0);
        const durationMinutes = this.blockModalDurationMinutes;
        const end = new Date(start.getTime() + durationMinutes * 60000);
        const busyTitle = this.blockModalTitleText.trim();

        event.setDates(start, end);
        this.refreshBlockFooter(plannedBlockId, start, end);
        this.closeBlockModal();

        this._plannerCache.invalidateBlocks(userId);
        this._plannedBlockService
            .updatePlannedBlock(plannedBlockId, isBusy
                ? { jobActionId: 0, userId, start, durationMinutes, isBusy: true, busyTitle }
                : { jobActionId, userId, start, durationMinutes })
            .subscribe({
                next: () => {
                    this._plannerCache.invalidateBlocks(userId);
                    // Another action (or busy time's new title): redrawn
                    // from the server, with its own name, job and colour.
                    if ((actionChanged || busyTitleChanged) && !this._destroyed) this.apiFor(userId)?.refetchEvents();
                },
                error: () => {
                    this._plannerCache.invalidateBlocks(userId);
                    if (this._destroyed) return;
                    event.setDates(oldStart, oldEnd);
                    this.refreshBlockFooter(plannedBlockId, oldStart, oldEnd);
                    alert('Could not update this planned block. Please try again.');
                },
            });
    }

    // eventDidMount builds the "1h 30m" footer only when a block's element
    // first mounts, and FullCalendar may keep the same element when a
    // block's times change — so it's updated directly, same as
    // persistBlockMove does after a drag/resize. Runs after FullCalendar's
    // re-render; finds the element via the same live lookup the right-click
    // menu uses (getElSeg).
    private refreshBlockFooter(plannedBlockId: number, start: Date | null, end: Date | null): void {
        setTimeout(() => {
            const root = this.plannerBodyRef?.nativeElement; // every person's calendar
            if (!root) return;
            root.querySelectorAll('.fc-timegrid-event').forEach((el) => {
                const props = getElSeg(el as HTMLElement)?.eventRange?.def.extendedProps;
                if (props?.['plannedBlockId'] === plannedBlockId) {
                    this.updateEventFooterDuration(el as HTMLElement, start, end);
                    this.updateDeadlineFlag(el as HTMLElement, start, props['deadlineDate']);
                }
            });
        }, 0);
    }

    // Like Timer's duplicateEvent, but always available: copies the block
    // as the popover currently shows it. With no edits that's an exact copy
    // (same day, start and duration); with edited start/duration the copy
    // takes the edited values and the original block stays as it was
    // saved. Either way it's a fresh block on the same action and day.
    blockModalDuplicate(): void {
        if (!this.blockModal) return;
        this.commitBlockModalStart();
        this.commitBlockModalDuration();

        const plannedBlockId = this.blockModal.plannedBlockId;
        const jobActionId = this.blockModal.jobActionId; // as shown, even if changed
        // Busy time: its title as shown, too.
        const busyTitle = this.blockModal.isBusy ? this.blockModalTitleText.trim() : undefined;
        const start = new Date(this.blockModalDay);
        start.setHours(0, this.blockModalStartMinutes, 0, 0);
        const durationMinutes = this.blockModalDurationMinutes;
        this.closeBlockModal();
        this.duplicateBlock(plannedBlockId, start, durationMinutes, jobActionId, busyTitle);
    }

    // Shared by the popover's Duplicate (blockModalDuplicate) and the
    // right-click menu's (contextDuplicate): a fresh block on the same
    // action, at the given start and length.
    private duplicateBlock(plannedBlockId: number, start: Date, durationMinutes: number, actionId?: number, busyTitle?: string): void {
        if (this.isPastDay(start)) return; // never onto a locked day
        // The copy goes on the same person's planner as the original.
        const found = this.findBlock(plannedBlockId);
        const props = found?.event.extendedProps || {};
        // Busy time: another busy block with the same title (no action).
        const isBusy = !!props['isBusy'];
        const jobActionId = isBusy ? 0 : actionId ?? props['jobActionId'];
        const userId = found?.userId;
        if ((!jobActionId && !isBusy) || !userId) return;
        const request = isBusy
            ? { jobActionId: 0, userId, start, durationMinutes, isBusy: true, busyTitle: busyTitle ?? ((props['busyTitle'] as string) || '') }
            : { jobActionId, userId, start, durationMinutes };

        this._plannerCache.invalidateBlocks(userId);
        this._plannedBlockService.createPlannedBlock(request).subscribe({
            next: () => {
                this._plannerCache.invalidateBlocks(userId);
                if (this._destroyed) return;
                this.apiFor(userId)?.refetchEvents();
            },
            error: (err) => {
                this._plannerCache.invalidateBlocks(userId);
                if (this._destroyed) return;
                alert(this.saveErrorMessage(err, 'Could not duplicate this planned block. Please try again.'));
            },
        });
    }

    // ── Copying a block to other days: drag a side handle sideways ──
    // From its right side to the following days, its left side to the days
    // before — every day between the block and the pointer gets a copy at
    // the same time and length (same action, or busy time with the same
    // title), on the same person's planner. Past days are skipped. While
    // dragging, a faded copy shows in each of those days; nothing is saved
    // until the mouse is let go (then one toast, with Undo).
    private startCopyDrag(e: MouseEvent, el: HTMLElement, side: 'start' | 'end'): void {
        if (e.button !== 0) return;
        // The handle's own — not FullCalendar's move of the block.
        e.preventDefault();
        e.stopPropagation();
        const plannedBlockId = getElSeg(el)?.eventRange?.def.extendedProps?.['plannedBlockId'] as number | undefined;
        const calendarEl = el.closest('.PlannerPerson-calendar[data-user-id]') as HTMLElement | null;
        const userId = calendarEl?.getAttribute('data-user-id');
        if (!plannedBlockId || !calendarEl || !userId || !this.canEditPlannerOf(userId)) return;
        const event = this.findBlock(plannedBlockId)?.event;
        if (!event?.start || !event.end || this.isPastDay(event.start)) return;
        const srcCol = el.closest('.fc-timegrid-col[data-date]') as HTMLElement | null;
        const cols = Array.from(calendarEl.querySelectorAll('.fc-timegrid-col[data-date]')) as HTMLElement[];
        const srcIdx = srcCol ? cols.indexOf(srcCol) : -1;
        const srcFrame = srcCol?.querySelector('.fc-timegrid-col-frame') as HTMLElement | null;
        if (srcIdx < 0 || !srcFrame) return;
        this.closeContextMenu();

        // Where the copies go in each day's column: the same spot as the block.
        const blockRect = el.getBoundingClientRect();
        const top = blockRect.top - srcFrame.getBoundingClientRect().top;
        const height = blockRect.height;
        const color = event.backgroundColor || '#dbdbdb';
        const dayOf = (col: HTMLElement) => {
            const [y, m, d] = (col.dataset['date'] || '').split('-').map(Number);
            return new Date(y, m - 1, d);
        };

        let targets: HTMLElement[] = [];
        let ghosts: HTMLElement[] = [];
        const showGhosts = () => {
            ghosts.forEach((g) => g.remove());
            ghosts = targets.map((col) => {
                const ghost = document.createElement('div');
                ghost.className = 'Planner-copyGhost';
                ghost.style.top = `${top}px`;
                ghost.style.height = `${height}px`;
                ghost.style.setProperty('--copy-bg', color);
                ghost.textContent = 'Copy';
                col.querySelector('.fc-timegrid-col-frame')?.appendChild(ghost);
                return ghost;
            });
        };
        const onMove = (ev: MouseEvent) => {
            // The day under the pointer — or the first/last one past the ends.
            let hoverIdx = cols.findIndex((c) => {
                const r = c.getBoundingClientRect();
                return ev.clientX >= r.left && ev.clientX < r.right;
            });
            if (hoverIdx < 0) {
                hoverIdx = ev.clientX < cols[0].getBoundingClientRect().left ? 0 : cols.length - 1;
            }
            const next = cols.filter((c, i) => (side === 'end' ? i > srcIdx && i <= hoverIdx : i < srcIdx && i >= hoverIdx)
                && !this.isPastDay(dayOf(c)));
            if (next.length !== targets.length || next.some((c, i) => c !== targets[i])) {
                targets = next;
                showGhosts();
            }
            this.updateLockedHover(ev.clientX, ev.clientY);
        };
        const onUp = () => {
            document.removeEventListener('mousemove', onMove, true);
            document.removeEventListener('mouseup', onUp, true);
            document.body.classList.remove('PlannerCopyDrag');
            root?.classList.remove('Planner--copying');
            this.markLockedColumns(false);
            ghosts.forEach((g) => g.remove());
            // The click that follows the release isn't one on the block.
            const swallowClick = (ce: Event) => ce.stopPropagation();
            document.addEventListener('click', swallowClick, true);
            setTimeout(() => document.removeEventListener('click', swallowClick, true), 0);
            if (targets.length) {
                const days = targets.map(dayOf);
                this._ngZone.run(() => this.copyBlockToDays(event, userId, days));
            }
        };
        document.body.classList.add('PlannerCopyDrag');
        // Past days: the same grey veil and "locked" note as moving a block
        // there, while the pointer is over one (markLockedColumns /
        // updateLockedHover).
        const root = this.plannerRootRef?.nativeElement;
        root?.classList.add('Planner--copying');
        this.markLockedColumns(true);
        this._ngZone.runOutsideAngular(() => {
            document.addEventListener('mousemove', onMove, true);
            document.addEventListener('mouseup', onUp, true);
        });
    }

    /** Copies of the block on these days (same time and length), saved
     *  together; Undo removes them again. */
    private copyBlockToDays(event: EventApi, userId: string, days: Date[]): void {
        if (!event.start || !event.end) return;
        const props = event.extendedProps || {};
        const isBusy = !!props['isBusy'];
        const jobActionId = (props['jobActionId'] as number) || 0;
        if (!isBusy && !jobActionId) return;
        const startMinutes = event.start.getHours() * 60 + event.start.getMinutes();
        const durationMinutes = Math.round((event.end.getTime() - event.start.getTime()) / 60000);
        const requests = days.map((day) => {
            const start = new Date(day);
            start.setHours(0, startMinutes, 0, 0);
            return isBusy
                ? { jobActionId: 0, userId, start, durationMinutes, isBusy: true, busyTitle: (props['busyTitle'] as string) || '' }
                : { jobActionId, userId, start, durationMinutes };
        });

        this._plannerCache.invalidateBlocks(userId);
        forkJoin(requests.map((r) => this._plannedBlockService.createPlannedBlock(r).pipe(
            map((block) => ({ block, err: null as unknown })),
            catchError((err) => of({ block: null as PlannedBlockViewModel | null, err })),
        ))).subscribe((results) => {
            this._plannerCache.invalidateBlocks(userId);
            if (this._destroyed) return;
            const created = results.filter((r) => !!r.block).map((r) => r.block as PlannedBlockViewModel);
            // Into the event source, see eventReceive.
            created.forEach((block) => this.apiFor(userId)?.addEvent(this.toCalendarEvent(block), true));
            const failed = results.find((r) => !r.block);
            if (failed) {
                alert(this.saveErrorMessage(failed.err, created.length
                    ? 'Some of the copies could not be added. Please try again.'
                    : 'Could not copy this planned block. Please try again.'));
            }
            if (created.length) {
                const n = created.length;
                this._timerToastService.show(`Copied to ${n} more day${n === 1 ? '' : 's'}.`, () => this.undoBlockCopies(created, userId));
            }
        });
    }

    private undoBlockCopies(created: PlannedBlockViewModel[], userId: string): void {
        this._plannerCache.invalidateBlocks(userId);
        forkJoin(created.map((b) => this._plannedBlockService.deletePlannedBlock(b.plannedBlockId).pipe(
            map(() => true),
            catchError(() => of(false)),
        ))).subscribe((results) => {
            this._plannerCache.invalidateBlocks(userId);
            if (this._destroyed) return;
            created.forEach((b, i) => {
                if (results[i]) this.findBlockEvent(b.plannedBlockId)?.remove();
            });
            if (results.some((ok) => !ok)) alert('Could not remove all of the copies. Please try again.');
        });
    }

    // Same route as Timer's goToJob.
    blockModalGoToJob(e: MouseEvent): void {
        e.stopPropagation();
        const jobId = this.blockModal?.jobId;
        this.closeBlockModal();
        this.goToJob(jobId);
    }

    private goToJob(jobId: number | null | undefined): void {
        if (!jobId) return;
        this._router.navigate([`/jobs/detail/${jobId}`]);
    }

    /** True when this job's own detail page is the one already open (the
     *  Planner embedded there) — "Go to job" would go nowhere, so it isn't
     *  offered for that job's blocks. Blocks of any other job keep it. */
    isCurrentJobPage(jobId: number | null | undefined): boolean {
        if (!jobId) return false;
        const match = /^\/jobs\/detail\/(\d+)(?:[/?#]|$)/.exec(this._router.url);
        return !!match && Number(match[1]) === jobId;
    }

    blockModalDelete(): void {
        if (!this.blockModal) return;
        const plannedBlockId = this.blockModal.plannedBlockId;
        this.closeBlockModal();
        this.deleteBlock(plannedBlockId);
    }

    // ── Log as time ──
    // Spec: "Creates a timesheet entry pre-filled from the block (client,
    // job, action, day, start, duration). The user can edit it before
    // saving. The block then shows a Logged state, to stop it being logged
    // twice. The block itself is not changed. Available on past blocks too."
    // The job (and so the client) is the block's own; the description, day
    // and start/end times can be edited.

    private resetLogForm(): void {
        this.logFormOpen = false;
        this.logError = '';
        this.logSaving = false;
        this._logFormInitial = '';
    }

    private logFormSnapshot(): string {
        return JSON.stringify([this.logDescription, this.logStart.getTime(), this.logEnd.getTime()]);
    }

    get logFormHasChanges(): boolean {
        return this.logFormOpen && this.logFormSnapshot() !== this._logFormInitial;
    }

    // Pre-filled from the popover as it currently stands (so from the
    // block, or from its edited-but-unsaved start/duration if changed).
    openLogForm(): void {
        if (!this.blockModal || this.blockModal.isLogged) return;
        this.commitBlockModalStart();
        this.commitBlockModalDuration();
        const start = new Date(this.blockModalDay);
        start.setHours(0, this.blockModalStartMinutes, 0, 0);
        this.logDescription = this.capitalizeFirst(this.blockModal.actionTitle);
        this.logStart = start;
        this.logEnd = new Date(start.getTime() + this.blockModalDurationMinutes * 60000);
        this.logError = '';
        this.logSaving = false;
        this.blockModalShowDropdown = false;
        this.logFormOpen = true;
        this._logFormInitial = this.logFormSnapshot();
    }

    // Back to the block view — anything typed in the form is dropped.
    cancelLogForm(): void {
        this.resetLogForm();
    }

    // From <planner-log-timerow> — it already keeps the end after the start
    // (overnight → next day), same as the Timer's own time row.
    onLogRangeChange(range: PlannerLogRange): void {
        this.logStart = range.start;
        this.logEnd = range.end;
        this.logError = '';
    }

    submitLog(): void {
        if (!this.blockModal || this.logSaving) return;
        const range = { start: this.logStart, end: this.logEnd };
        if (range.end.getTime() <= range.start.getTime()) {
            this.logError = 'End time must be after start time.';
            return;
        }

        const plannedBlockId = this.blockModal.plannedBlockId;
        const userId = this.blockModal.userId;
        this.logError = '';
        this.logSaving = true;
        this._plannedBlockService
            .logPlannedBlock(plannedBlockId, {
                startTime: range.start,
                endTime: range.end,
                description: this.capitalizeFirst(this.logDescription.trim()),
            })
            .subscribe({
                next: () => {
                    // The block's Logged flag changed — cached copies are stale.
                    if (userId) this._plannerCache.invalidateBlocks(userId);
                    // Same signal the Timer page sends after creating an
                    // entry, so an open Timer tab / the desktop app picks
                    // the new entry up.
                    this._timerSyncService.notify('entries-changed');
                    if (this._destroyed) return;
                    this.findBlockEvent(plannedBlockId)?.setExtendedProp('isLogged', true);
                    this.refreshLoggedBadge(plannedBlockId);
                    if (this.blockModal?.plannedBlockId === plannedBlockId) {
                        this.blockModal.isLogged = true;
                    }
                    this.resetLogForm();
                    this._timerToastService.show('Time logged.');
                    this._changeRef.markForCheck();
                },
                error: (err) => {
                    if (this._destroyed) return;
                    this.logSaving = false;
                    // The server's own message where there is one (e.g.
                    // "already logged" if it was logged elsewhere meanwhile).
                    this.logError = (err instanceof Error && err.message) || 'Could not log this time. Please try again.';
                    this._changeRef.markForCheck();
                },
            });
    }

    // ─── Add a block manually ────────────────────────────────────────────

    // From the toolbar's + (the top person — the only one in the one-person
    // view), or a person's own + in their section header (several-people
    // view). The same + again closes it; another person's + switches to them.
    toggleAddBlock(e: MouseEvent, forUserId?: string): void {
        const userId = forUserId ?? this.subjectUserId();
        if (this.addBlockOpen && this.addBlockUserId === userId) {
            this.requestCloseAddBlock();
            return;
        }
        this.openAddBlock(e.currentTarget as HTMLElement, userId);
    }

    private openAddBlock(anchor: HTMLElement | null, userId: string | undefined, at?: AddBlockAt): void {
        if (!userId) return;
        this.removeAddPlaceholder();
        this.addBlockUserId = userId;
        this.closeContextMenu();
        this.closeBlockModal();

        // Just below the + button — or, from a click on the calendar, just
        // beside the click — kept inside the viewport, then made relative to
        // .Planner, same approach as the context menu.
        const POPOVER_W = 400;
        // Its action list opens on demand (above the pill when there's no
        // room below — placePicker), so just the popover itself (with its
        // Action | Busy switch).
        const POPOVER_H = 320;
        const PAD = 12;
        const hostRect = this.plannerRootRef?.nativeElement.getBoundingClientRect();
        const vw = document.documentElement.clientWidth;
        const vh = document.documentElement.clientHeight;
        let left: number;
        let top: number;
        if (at && at.clientX != null && at.clientY != null) {
            left = at.clientX + 12;
            top = at.clientY + 6;
            if (top + POPOVER_H > vh - PAD) top = Math.max(PAD, vh - POPOVER_H - PAD);
        } else if (anchor) {
            const rect = anchor.getBoundingClientRect();
            // A person's own + (at the right end of their name row — in the
            // Week view, or their column in the Day view) and any + on the
            // right half of the screen open to its bottom-left — right edges
            // lined up — rather than being pushed back from the screen's
            // edge over the panel; the toolbar's + on the left, to its
            // bottom-right, as before. (Kept on screen below either way.)
            const opensLeft = anchor.classList.contains('PlannerPerson-addBtn')
                || rect.left + rect.width / 2 > vw / 2;
            left = opensLeft ? rect.right - POPOVER_W : rect.left;
            top = rect.bottom + 6;
        } else {
            left = (vw - POPOVER_W) / 2;
            top = 120;
        }
        if (left + POPOVER_W > vw - PAD) left = vw - POPOVER_W - PAD;
        left = Math.max(PAD, left);
        this.addBlockTop = top - (hostRect?.top ?? 0);
        this.addBlockLeft = left - (hostRect?.left ?? 0);

        if (at) {
            // Where the calendar was clicked (or dragged).
            const day = new Date(at.start);
            day.setHours(0, 0, 0, 0);
            this.addBlockDay = day;
            this.setAddBlockStart(at.start.getHours() * 60 + at.start.getMinutes());
            this.setAddBlockDuration(at.durationMinutes);
        } else {
            // Defaults: today if it's in the week on screen (otherwise that
            // week's first day), 09:00, 1 hour — the spec's default duration.
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            const view = this.calendarComponent?.getApi()?.view;
            const inView = !!view && today >= view.activeStart && today < view.activeEnd;
            // Never a past day — blocks are only added for today or later.
            this.addBlockDay = inView || !view || view.activeStart < today ? today : new Date(view.activeStart);
            this.setAddBlockStart(9 * 60);
            this.setAddBlockDuration(60);
        }
        this.addBlockActionId = null;
        // Opens on Busy (the switch's first option); Action is one click.
        this.addBlockBusy = true;
        this.addBlockBusyTitle = '';
        this.addBlockSearch = '';
        this.addBlockError = '';
        this.addBlockSaving = false;
        this.addBlockDayPickerOpen = false;
        this.addBlockEditingDuration = false;
        this.addBlockExpandedJobIds = new Set<number>();
        // The action list stays closed until the "Select an action" pill is
        // clicked (it took over the popover when it opened by itself) — the
        // pill makes it clear that's the next step, and Add waits for it.
        this.addBlockPickerOpen = false;
        this.addBlockOpen = true;
        // From the calendar: a placeholder shows where it'll go. Either way
        // the picker lists the pull-out's actions for this person (already
        // loaded — see addBlockSourceActions).
        this.addBlockDiscardOpen = false;
        this._addBlockInitial = this.addBlockFieldsSnapshot();
        if (at) this.showAddPlaceholder();
        this._changeRef.markForCheck(); // see openContextMenu
    }

    // A click (or drag) on an empty part of a person's calendar. Opens "Add
    // planned block" there, for that person, with its action picker open.
    // Unlike the Timer's, no empty entry is ever made: the block is only
    // added once an action is chosen and Add pressed (submitAddBlock).
    private onCalendarSelect(info: DateSelectArg, userId: string): void {
        info.view.calendar.unselect();
        if (this._popoverOpenAtMousedown) {
            this._popoverOpenAtMousedown = false;
            return;
        }
        const step = PlannerComponent.BLOCK_STEP_MINUTES;
        const selected = Math.round((info.end.getTime() - info.start.getTime()) / 60000);
        // A plain click selects a single 30-minute step: the usual 1 hour
        // then; a drag keeps the length dragged.
        const durationMinutes = selected <= step ? 60 : selected;
        const mouse = info.jsEvent as MouseEvent | null;
        this.openAddBlock(null, userId, {
            start: info.start,
            durationMinutes,
            clientX: mouse?.clientX,
            clientY: mouse?.clientY,
        });
    }

    // ── Placeholder for a block being added from the calendar ──
    // A grey outline where it'll go, kept in step with the popover's day,
    // start and duration — never saved; removed when the popover closes.
    private _addPlaceholder: EventApi | null = null;
    private _popoverOpenAtMousedown = false;

    private showAddPlaceholder(): void {
        this.removeAddPlaceholder();
        const api = this.addBlockUserId ? this.apiFor(this.addBlockUserId) : undefined;
        if (!api) return;
        const { start, end } = this.addBlockRange();
        this._addPlaceholder = api.addEvent({
            id: 'planner-add-placeholder',
            start,
            end,
            backgroundColor: 'transparent',
            borderColor: 'transparent',
            startEditable: false,
            durationEditable: false,
            classNames: ['Planner-addPlaceholder'],
            extendedProps: { isPlaceholder: true },
        });
    }

    private syncAddPlaceholder(): void {
        if (!this._addPlaceholder) return;
        const { start, end } = this.addBlockRange();
        this._addPlaceholder.setDates(start, end);
    }

    private removeAddPlaceholder(): void {
        this._addPlaceholder?.remove();
        this._addPlaceholder = null;
    }

    private addBlockRange(): { start: Date; end: Date } {
        const start = new Date(this.addBlockDay);
        start.setHours(0, this.addBlockStartMinutes, 0, 0);
        return { start, end: new Date(start.getTime() + this.addBlockDurationMinutes * 60000) };
    }

    /** Day / start / duration as shown, for spotting changes. */
    private addBlockFieldsSnapshot(): { day: string; start: number; duration: number } {
        return { day: this.addBlockDay.toDateString(), start: this.addBlockStartMinutes, duration: this.addBlockDurationMinutes };
    }

    /** An action chosen, or the day / start / duration changed. */
    get addBlockHasChanges(): boolean {
        if (!this.addBlockOpen) return false;
        if (this.addBlockActionId) return true;
        if (this.addBlockBusy && this.addBlockBusyTitle.trim()) return true;
        const now = this.addBlockFieldsSnapshot();
        const was = this._addBlockInitial;
        return !!was && (now.day !== was.day || now.start !== was.start || now.duration !== was.duration);
    }

    /** User-initiated close (✕, Cancel, Escape, a press outside, the + again)
     *  — asks first if anything's been chosen or changed, same as the block
     *  popover's requestCloseBlockModal and the Timer's own add-entry modal. */
    requestCloseAddBlock(): void {
        if (!this.addBlockOpen) return;
        this.commitAddBlockStart();
        this.commitAddBlockDuration();
        if (this.addBlockHasChanges) {
            this.addBlockDayPickerOpen = false;
            this.addBlockDiscardOpen = true;
            this._changeRef.markForCheck();
            return;
        }
        this.closeAddBlock();
    }

    // "Go back" — keep editing.
    addBlockDiscardGoBack(): void {
        this.addBlockDiscardOpen = false;
        this._changeRef.markForCheck();
    }

    // Nothing's been added yet (only Add does that), so discarding is just
    // closing — the placeholder goes with it.
    addBlockDiscardConfirm(): void {
        this.closeAddBlock();
    }

    // Shared by the one confirmation, whichever popover asked.
    onDiscardGoBack(): void {
        if (this.addBlockDiscardOpen) this.addBlockDiscardGoBack();
        else this.blockModalDiscardGoBack();
    }

    onDiscardConfirm(): void {
        if (this.addBlockDiscardOpen) this.addBlockDiscardConfirm();
        else this.blockModalDiscardConfirm();
    }

    /** Earliest day a block can be added for (the day picker greys out
     *  earlier ones) — today. */
    get addBlockMinDay(): Date {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        return today;
    }

    /** A day before today — locked (spec, Day Transition and Locking): its
     *  blocks can't be moved, resized, edited or removed by anyone; they can
     *  still be logged as time. A block belongs to the day it starts on. */
    isPastDay(date: Date | null | undefined): boolean {
        return !!date && date.getTime() < this.addBlockMinDay.getTime();
    }

    // At midnight the day just ended locks — on a page left open, too: its
    // blocks are redrawn (toCalendarEvent marks them uneditable), and an
    // open menu/popover for one of them goes. Set again for the next night.
    private _midnightTimer: any = null;

    private scheduleMidnightLock(): void {
        clearTimeout(this._midnightTimer);
        const next = new Date();
        next.setHours(24, 0, 1, 0); // just after midnight
        this._midnightTimer = setTimeout(() => {
            if (this._destroyed) return;
            this._ngZone.run(() => {
                this.closeContextMenu();
                this.closeBlockModal();
                this.refetchAllCalendars();
                this._changeRef.markForCheck();
            });
            this.scheduleMidnightLock();
        }, next.getTime() - Date.now());
    }

    closeAddBlock(): void {
        this.removeAddPlaceholder();
        this.addBlockDiscardOpen = false;
        this.addBlockOpen = false;
        this.addBlockDayPickerOpen = false;
        this.addBlockPickerOpen = false;
        this._changeRef.markForCheck();
    }

    private sortAddBlockActions(actions: JobAction[]): JobAction[] {
        return [...actions].sort(
            (a, b) => (a.jobNumber || 0) - (b.jobNumber || 0) || (a.title || '').localeCompare(b.title || '')
        );
    }

    /** The picker's rows: one per job (in job-number order), each with its
     *  actions. Search matches the job number, job title or client (the
     *  whole job shows) or an action's title (just those actions show, with
     *  the job opened). Memoised — this is read on every change detection. */
    get addBlockJobGroups(): AddBlockJobGroup[] {
        const term = this.addBlockSearch.trim().toLowerCase();
        const memo = this._addBlockGroupsMemo;
        const source = this.addBlockSourceActions;
        if (memo && memo.actions === source && memo.term === term) return memo.groups;

        const groups = this.addBlockUserId ? this.actionSectionGroups(this.addBlockUserId, term) : [];
        this._addBlockGroupsMemo = { actions: source, term, groups };
        return groups;
    }

    /** What the "Add planned block" picker lists — opened from the + or a
     *  click on the calendar: the pull-out's actions, that person's own
     *  first (pulloutActionsFor). */
    get addBlockSourceActions(): JobAction[] {
        return this.addBlockUserId ? this.pulloutActionsFor(this.addBlockUserId) : [];
    }

    /** Still loading what the picker lists (the pull-out's list). */
    get addBlockListLoading(): boolean {
        return this.isLoadingUnplanned;
    }

    /** Actions as the pickers' rows (see addBlockJobGroups and
     *  blockModalActionGroups): one per job, each with its actions, the
     *  search term (lower case) matching a job (the whole job shows) or an
     *  action's title (just those actions, with the job opened). */
    private groupActionsByJob(actions: JobAction[], term: string): AddBlockJobGroup[] {
        const byJob = new Map<number, AddBlockJobGroup>();
        actions.forEach((a) => {
            const jobId = a.jobId ?? 0;
            let group = byJob.get(jobId);
            if (!group) {
                group = {
                    jobId,
                    jobNumber: a.jobNumber,
                    jobTitle: a.jobTitle || '',
                    clientName: a.clientName || '',
                    color: plannerColorForJobNumber(a.jobNumber).titleColor,
                    actions: [],
                    matchedByAction: false,
                };
                byJob.set(jobId, group);
            }
            group.actions.push(a);
        });

        const has = (v: string | number | undefined | null) => String(v ?? '').toLowerCase().includes(term);
        const groups: AddBlockJobGroup[] = [];
        byJob.forEach((g) => {
            if (!term || has(g.jobNumber) || has(g.jobTitle) || has(g.clientName)) {
                groups.push(g);
                return;
            }
            const matching = g.actions.filter((a) => has(a.title));
            if (matching.length) groups.push({ ...g, actions: matching, matchedByAction: true });
        });

        return groups;
    }

    isAddBlockJobExpanded(group: AddBlockJobGroup): boolean {
        return group.matchedByAction || this.addBlockExpandedJobIds.has(group.jobId);
    }

    // Same dot colour as the picker rows and the Timer header's picker.
    addBlockJobDotColor(action: JobAction): string {
        return plannerColorForJobNumber(action.jobNumber).titleColor;
    }

    get addBlockSelectedAction(): JobAction | undefined {
        return this.addBlockSourceActions.find((a) => a.jobActionId === this.addBlockActionId);
    }

    toggleAddBlockPicker(): void {
        this.addBlockPickerOpen = !this.addBlockPickerOpen;
        if (this.addBlockPickerOpen) {
            this.addBlockDayPickerOpen = false;
            this.focusAddBlockSearch();
            this.placePicker('add');
        }
    }

    // ── The action pickers: below their pill, or above it when there isn't
    // room below on screen (and there's more above). Measured once drawn,
    // each time one opens — hidden until then, so it never shows for a
    // moment in the wrong place. ──
    addBlockPickerUp = false;
    addBlockPickerPlaced = false;
    blockModalPickerUp = false;
    blockModalPickerPlaced = false;

    private placePicker(which: 'add' | 'modal'): void {
        if (which === 'add') {
            this.addBlockPickerUp = false;
            this.addBlockPickerPlaced = false;
        } else {
            this.blockModalPickerUp = false;
            this.blockModalPickerPlaced = false;
        }
        setTimeout(() => {
            if (this._destroyed) return;
            const root = this.plannerRootRef?.nativeElement;
            const picker = root?.querySelector(which === 'add'
                ? '.PlannerPicker:not(.PlannerModal-picker)'
                : '.PlannerModal-picker') as HTMLElement | null;
            let up = false;
            const wrap = picker?.parentElement;
            if (picker && wrap) {
                const PAD = 8;
                const r = wrap.getBoundingClientRect();
                const needed = picker.offsetHeight + 4;
                const below = document.documentElement.clientHeight - r.bottom - PAD;
                const above = r.top - PAD;
                up = needed > below && above > below;
            }
            if (which === 'add') {
                this.addBlockPickerUp = up;
                this.addBlockPickerPlaced = true;
            } else {
                this.blockModalPickerUp = up;
                this.blockModalPickerPlaced = true;
            }
            this._changeRef.markForCheck();
        }, 0);
    }

    /** The Action | Busy switch. Busy: the title box gets the focus. */
    setAddBlockBusy(busy: boolean): void {
        if (this.addBlockBusy === busy) return;
        this.addBlockBusy = busy;
        this.addBlockPickerOpen = false;
        this.addBlockError = '';
        if (busy) {
            setTimeout(() => {
                const input = this.plannerRootRef?.nativeElement
                    .querySelector('.PlannerAddBlock-busyInput') as HTMLInputElement | null;
                input?.focus();
            }, 0);
        }
    }

    pickBusySuggestion(title: string): void {
        this.addBlockBusyTitle = title;
        this.addBlockError = '';
    }

    private focusAddBlockSearch(): void {
        setTimeout(() => {
            const input = this.plannerRootRef?.nativeElement
                .querySelector('.PlannerAddBlock-pickerSearchInput') as HTMLInputElement | null;
            input?.focus();
        }, 0);
    }

    toggleAddBlockJob(jobId: number, e?: MouseEvent): void {
        e?.stopPropagation();
        if (this.addBlockExpandedJobIds.has(jobId)) this.addBlockExpandedJobIds.delete(jobId);
        else this.addBlockExpandedJobIds.add(jobId);
    }

    // A block is always for one action, so picking a job either takes its
    // only action straight away or opens it to choose one.
    onAddBlockJobClick(group: AddBlockJobGroup): void {
        if (group.actions.length === 1) {
            this.selectAddBlockAction(group.actions[0]);
        } else {
            this.toggleAddBlockJob(group.jobId);
        }
    }

    selectAddBlockAction(action: JobAction): void {
        this.addBlockActionId = action.jobActionId;
        this.addBlockError = '';
        this.addBlockPickerOpen = false;
    }

    // Opens below the Day link, or above it when there isn't room below —
    // same rule as the log form's date picker (PlannerLogTimerowComponent).
    toggleAddBlockDayPicker(e: MouseEvent): void {
        this.addBlockDayPickerOpen = !this.addBlockDayPickerOpen;
        if (!this.addBlockDayPickerOpen) return;
        this.addBlockPickerOpen = false;
        const PICKER_H = 230;
        const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
        this.addBlockDayPickerUpward = rect.bottom + PICKER_H > document.documentElement.clientHeight - 20;
    }

    onAddBlockDayPicked(day: Date): void {
        this.addBlockDay = day;
        this.addBlockDayPickerOpen = false;
        this.syncAddPlaceholder();
    }

    // Same 30-minute rules as the block popover's own fields.
    private setAddBlockStart(minutes: number): void {
        this.addBlockStartMinutes = Math.min(PlannerComponent.LAST_START_MINUTES, Math.max(0, this.roundToStep(minutes)));
        this.addBlockStartHText = String(Math.floor(this.addBlockStartMinutes / 60)).padStart(2, '0');
        this.addBlockStartMText = String(this.addBlockStartMinutes % 60).padStart(2, '0');
        this.syncAddPlaceholder();
    }

    private setAddBlockDuration(minutes: number): void {
        this.addBlockDurationMinutes = Math.max(PlannerComponent.BLOCK_STEP_MINUTES, this.roundToStep(minutes));
        this.addBlockDurationText = formatDurationHM(this.addBlockDurationMinutes); // "1h 30m"
        this.syncAddPlaceholder();
    }

    // HH and MM read together, then rounded to the nearest half hour
    // (e.g. 09:40 → 09:30, 09:50 → 10:00). Anything unreadable falls back
    // to the last good start.
    commitAddBlockStart(): void {
        const h = parseInt(this.addBlockStartHText, 10);
        const m = parseInt(this.addBlockStartMText || '0', 10);
        if (isNaN(h) || isNaN(m) || h < 0 || h > 23 || m < 0 || m > 59) {
            this.setAddBlockStart(this.addBlockStartMinutes);
            return;
        }
        this.setAddBlockStart(h * 60 + m);
    }

    commitAddBlockDuration(): void {
        this.setAddBlockDuration(this.parseDurationText(this.addBlockDurationText) ?? this.addBlockDurationMinutes);
    }

    // Digits only, two per segment; a full hour moves on to the minutes,
    // like the Timer's own segments.
    onAddBlockSegInput(e: Event, seg: 'h' | 'm'): void {
        const input = e.target as HTMLInputElement;
        const digits = (input.value || '').replace(/\D/g, '').slice(0, 2);
        input.value = digits;
        if (seg === 'h') {
            this.addBlockStartHText = digits;
            if (digits.length === 2) this.focusAddBlockSeg('m');
        } else {
            this.addBlockStartMText = digits;
        }
    }

    // ↑/↓: an hour on HH, half an hour on MM. Enter adds the block.
    onAddBlockSegKeydown(e: KeyboardEvent, seg: 'h' | 'm'): void {
        if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
            e.preventDefault();
            this.commitAddBlockStart();
            const step = seg === 'h' ? 60 : PlannerComponent.BLOCK_STEP_MINUTES;
            this.setAddBlockStart(this.addBlockStartMinutes + (e.key === 'ArrowUp' ? step : -step));
        } else if (e.key === 'Enter') {
            e.preventDefault();
            this.submitAddBlock();
        }
    }

    // Once focus has left both segments, tidy and round what was typed.
    onAddBlockSegBlur(): void {
        setTimeout(() => {
            const active = document.activeElement as HTMLElement | null;
            if (active?.classList.contains('PlannerAddBlock-seg')) return;
            this.commitAddBlockStart();
            this._changeRef.markForCheck();
        }, 0);
    }

    private focusAddBlockSeg(seg: 'h' | 'm'): void {
        const el = this.plannerRootRef?.nativeElement
            .querySelector(`.PlannerAddBlock-seg[data-seg="${seg}"]`) as HTMLInputElement | null;
        el?.focus();
        el?.select();
    }

    // DURATION: click (or Tab in) to edit, like the Timer's own.
    startEditingAddBlockDuration(): void {
        this.addBlockEditingDuration = true;
        setTimeout(() => {
            const el = this.plannerRootRef?.nativeElement
                .querySelector('.PlannerAddBlock-durInput') as HTMLInputElement | null;
            el?.focus();
            el?.select();
        }, 0);
    }

    stopEditingAddBlockDuration(): void {
        this.commitAddBlockDuration();
        this.addBlockEditingDuration = false;
    }

    // ↑/↓ step by 30 mins, Enter adds — same as the block popover.
    onAddBlockDurationKeydown(e: KeyboardEvent): void {
        const step = PlannerComponent.BLOCK_STEP_MINUTES;
        if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
            e.preventDefault();
            this.commitAddBlockDuration();
            this.setAddBlockDuration(this.addBlockDurationMinutes + (e.key === 'ArrowUp' ? step : -step));
        } else if (e.key === 'Enter') {
            e.preventDefault();
            this.submitAddBlock();
        }
    }

    submitAddBlock(): void {
        if (this.addBlockSaving) return;
        this.commitAddBlockStart();
        this.commitAddBlockDuration();
        const isBusy = this.addBlockBusy;
        const jobActionId = isBusy ? 0 : this.addBlockActionId;
        const userId = this.addBlockUserId;
        if (!jobActionId && !isBusy) {
            this.addBlockError = 'Please choose an action.';
            return;
        }
        if (!userId) return;
        if (this.addBlockDay < this.addBlockMinDay) {
            this.addBlockError = 'Please choose today or a later day.';
            return;
        }

        const start = new Date(this.addBlockDay);
        start.setHours(0, this.addBlockStartMinutes, 0, 0);
        const durationMinutes = this.addBlockDurationMinutes;

        this.addBlockSaving = true;
        this.addBlockError = '';
        this._plannerCache.invalidateBlocks(userId);
        const request = isBusy
            ? { jobActionId: 0, userId, start, durationMinutes, isBusy: true, busyTitle: this.addBlockBusyTitle.trim() }
            : { jobActionId: jobActionId as number, userId, start, durationMinutes };
        this._plannedBlockService.createPlannedBlock(request).subscribe({
            next: (block) => {
                this._plannerCache.invalidateBlocks(userId);
                if (this._destroyed) return;
                this.addBlockSaving = false;
                this.closeAddBlock();
                // Shown straight away on its person's calendar if its day is
                // on screen (into the event source, see eventReceive);
                // otherwise every calendar goes to its week, which loads it
                // along with the rest. (A collapsed person's calendar loads
                // it when expanded.)
                const view = this.calendarComponent?.getApi()?.view;
                if (view && start >= view.activeStart && start < view.activeEnd) {
                    this.apiFor(userId)?.addEvent(this.toCalendarEvent(block), true);
                } else {
                    this.gotoDateAll(start);
                }
            },
            error: (err) => {
                this._plannerCache.invalidateBlocks(userId);
                if (this._destroyed) return;
                this.addBlockSaving = false;
                this.addBlockError = this.saveErrorMessage(err, 'Could not add this block. Please try again.');
                this._changeRef.markForCheck();
            },
        });
    }

    // The block's "✓ Logged" marker, in its footer next to the duration.
    // Idempotent — safe to call from eventDidMount and after logging.
    private applyLoggedBadge(el: HTMLElement): void {
        el.classList.add('Planner-event--logged');
        const footer = el.querySelector('.fc-event-footer');
        if (!footer || footer.querySelector('.fc-event-footer__logged')) return;
        const badge = document.createElement('span');
        badge.className = 'fc-event-footer__logged';
        badge.textContent = '✓ Logged';
        footer.appendChild(badge);
    }

    // After logging, FullCalendar may keep the block's existing element (so
    // eventDidMount won't run again) — badge it directly, found the same
    // way refreshBlockFooter does.
    private refreshLoggedBadge(plannedBlockId: number): void {
        setTimeout(() => {
            const root = this.plannerBodyRef?.nativeElement; // every person's calendar
            if (!root) return;
            root.querySelectorAll('.fc-timegrid-event').forEach((el) => {
                const props = getElSeg(el as HTMLElement)?.eventRange?.def.extendedProps;
                if (props?.['plannedBlockId'] === plannedBlockId) {
                    this.applyLoggedBadge(el as HTMLElement);
                }
            });
        }, 0);
    }

    // The API returns the block's job number/title and client name itself,
    // so every block gets its final colour (hashed on the job number, same
    // as Timer and the pull-out) on first paint. unplannedActions is only a
    // fallback should those ever come back empty. Never hashes on jobId —
    // that's the job's database id, not its number, so it would always give
    // a different colour from Timer's.
    private toCalendarEvent(block: PlannedBlockViewModel): EventInput {
        const start = new Date(block.start);
        const end = new Date(start.getTime() + block.durationMinutes * 60000);
        const isBusy = !!block.isBusy;
        // Busy time: no action or job — the grey of "no job" (as on the
        // Timer), its title where an action's name goes.
        const known = isBusy ? undefined : this.unplannedActions.find((a) => a.jobActionId === block.jobActionId);
        const jobNumber = block.jobNumber || known?.jobNumber;
        const jobTitle = block.jobTitle ?? known?.jobTitle;
        const clientName = block.clientName ?? known?.clientName;
        const color = plannerColorForJobNumber(jobNumber);
        const jobLabel = jobNumber ? `${jobNumber} - ${jobTitle || ''}` : '';
        return {
            id: `planned-block-${block.plannedBlockId}`,
            title: jobLabel,
            start,
            end,
            // Past day (spec, Day Transition and Locking): can't be moved or
            // resized by anyone. Only ever false here — an explicit true
            // would override a view-only calendar's own editable: false.
            ...(this.isPastDay(start) ? { editable: false } : {}),
            backgroundColor: color.bg,
            borderColor: color.bg,
            extendedProps: {
                plannedBlockId: block.plannedBlockId,
                jobActionId: block.jobActionId,
                actionTitle: isBusy ? (block.busyTitle || 'Busy') : block.jobActionTitle,
                isBusy,
                busyTitle: isBusy ? (block.busyTitle || '') : '',
                jobId: block.jobId || known?.jobId,
                jobNumber,
                jobTitle,
                clientName,
                isLogged: block.isLogged,
                isBillable: block.isBillable ?? known?.isBillable ?? false,
                isActionDeleted: !!block.isActionDeleted,
                deadlineDate: block.deadlineDate ?? null,
                titleColor: color.titleColor,
                hoverBg: color.hoverBg,
            },
        };
    }

    // Injected directly into FullCalendar's own header axis cell (the
    // narrow top-left corner above the hour labels) rather than overlaid
    // with guessed pixel positioning — FullCalendar computes that cell's
    // real width/position itself, so this is the only way to guarantee the
    // buttons actually sit inside it instead of spilling into the first
    // day column. Mirrors the Timer calendar's own zoom-control technique.
    // Several people: in every person's calendar, so they're still at hand
    // once scrolled down to someone else's section (zoom applies to all).
    private injectZoomControls(): void {
        this._calendarElList?.forEach((ref) => {
            const axisCell = ref.nativeElement.querySelector(
                '.fc-scrollgrid-section-header .fc-timegrid-axis'
            ) as HTMLElement | null;
            if (axisCell) this.injectZoomControlsInto(axisCell);
        });
    }

    private injectZoomControlsInto(axisCell: HTMLElement): void {
        axisCell.querySelectorAll('.Planner-zoomControls').forEach((el) => el.remove());
        // FullCalendar renders this corner cell aria-hidden (it's normally
        // empty) — with real buttons inside it, a clicked button ends up
        // focused inside an aria-hidden ancestor, which the browser blocks
        // and warns about, and screen readers can't reach the buttons at
        // all. FC only sets the attribute when it creates the cell, and a
        // fresh cell also means fresh buttons via this same method, so
        // removing it here sticks for as long as the buttons exist.
        axisCell.removeAttribute('aria-hidden');

        const wrap = document.createElement('div');
        wrap.className = 'Planner-zoomControls';

        const zoomInBtn = document.createElement('button');
        zoomInBtn.type = 'button';
        zoomInBtn.className = 'Planner-zoomBtn';
        zoomInBtn.title = 'Zoom in';
        zoomInBtn.textContent = '+';
        zoomInBtn.disabled = this.zoomLevel >= this.zoomLevels.length - 1;
        zoomInBtn.addEventListener('click', (e) => {
            e.stopPropagation();
            // This element is injected straight into FullCalendar's DOM via
            // document.createElement, outside Angular's template — a plain
            // call here can run outside Angular's zone, so the state change
            // (calendarVisible/calendarOptions in applyZoom) doesn't trigger
            // change detection until some unrelated event happens to. Forcing
            // it into the zone is what made the button need a second,
            // unrelated click elsewhere before anything visibly updated.
            this._ngZone.run(() => this.zoomIn());
        });

        const zoomOutBtn = document.createElement('button');
        zoomOutBtn.type = 'button';
        zoomOutBtn.className = 'Planner-zoomBtn';
        zoomOutBtn.title = 'Zoom out';
        zoomOutBtn.textContent = '−';
        zoomOutBtn.disabled = this.zoomLevel <= 0;
        zoomOutBtn.addEventListener('click', (e) => {
            e.stopPropagation();
            this._ngZone.run(() => this.zoomOut());
        });

        wrap.appendChild(zoomInBtn);
        wrap.appendChild(zoomOutBtn);
        axisCell.appendChild(wrap);
    }

    // ─── Sticky day header + pull-out panel + centre-on-now scroll — same
    // technique as the Timer calendar (timer-tracker.component.ts),
    // duplicated rather than imported so Planner has zero dependency on
    // Timer's internals. The offset calculation itself differs from Timer's
    // since Planner is hosted on the Actions page, not Timer's own page, so
    // what's actually sticky/fixed above it is different: the global top nav
    // bar when the window scrolls (narrow widths), or the Actions page's
    // sticky search bar (action-search.component — sticky from $desktop-sm
    // up) when the page itself scrolls. In practice only one of the two is
    // ever active at a given width, since both flip at the same breakpoint,
    // so summing them is safe. ────────────────────────────────────────────

    // Sets the day-header's sticky `top` inline, rather than a hardcoded CSS
    // breakpoint value like Timer uses — the Actions page's search bar can
    // change height responsively (wrapping at narrow widths), so the offset
    // has to be measured, not assumed. Native CSS `position: sticky` handles
    // the ongoing stuck/unstuck transition itself once given this value, so
    // this only needs recomputing on resize/navigation, not on every scroll.
    private updateStickyHeaderOffset(): void {
        // Floors and pulls in an extra 1px — sub-pixel rounding between this
        // sticky header and the search bar's own stuck position can
        // otherwise leave a hairline gap where the grid behind it peeks
        // through while scrolling; a tiny 1px overlap is invisible against
        // the matching grey backgrounds, a gap isn't.
        const offset = Math.max(0, Math.floor(this.stickyChromeHeight()) - 1);

        // Several-people view: everything scrolls inside the people area
        // (.Planner-peopleList--scroll), so its sticky rows stick to that
        // area's top rather than under the page's bars: each person's header
        // at the very top, and their calendar's day header just under it
        // (overlapping its bottom border by 1px, the same hairline-gap
        // guard as above).
        this.updateColumnsHeight();
        this.applySlotHeight();
        // The panel's height can follow the calendar's (_wholeDay).
        this.updatePulloutOffset();

        if (this.usesScrollArea) {
            this.plannerBodyRef?.nativeElement.querySelectorAll('.PlannerPerson').forEach((section) => {
                const header = section.querySelector('.PlannerPerson-header') as HTMLElement | null;
                if (header) header.style.top = '0px';
                const dayHeaderTop = header ? Math.max(0, Math.floor(header.getBoundingClientRect().height) - 1) : 0;
                section.querySelectorAll('.fc-scrollgrid-section-header.fc-scrollgrid-section-sticky > *')
                    .forEach((el) => ((el as HTMLElement).style.top = `${dayHeaderTop}px`));
            });
            return;
        }

        const rootEl = this.calendarElRef?.nativeElement;
        if (!rootEl) return;
        const headerEl = rootEl.querySelector(
            '.fc-scrollgrid-section-header.fc-scrollgrid-section-sticky > *'
        ) as HTMLElement | null;
        if (!headerEl) return;
        headerEl.style.top = `${offset}px`;
    }

    // Several people (Week or Day view): everyone's calendars scroll inside
    // one area. It gets the same height as the pull-out panel beside it
    // (the screen height minus the sticky bars above, see
    // updatePulloutOffset), so once the page is scrolled down it fills the
    // screen to the bottom, and its bottom edge lines up with the panel's.
    private updateColumnsHeight(): void {
        const box = this.plannerBodyRef?.nativeElement
            .querySelector('.Planner-peopleList--scroll') as HTMLElement | null;
        // Back to one person: the class goes, so drop the inline height too.
        this.plannerBodyRef?.nativeElement
            .querySelectorAll('.Planner-peopleList:not(.Planner-peopleList--scroll)')
            .forEach((el) => ((el as HTMLElement).style.height = ''));
        if (!box) return;

        const container = this.findScrollContainer(box);
        const isWindowScroller = container === document.scrollingElement
            || container === document.documentElement
            || container === document.body;
        const visibleHeight = isWindowScroller ? window.innerHeight : container.clientHeight;
        const top = Math.max(0, Math.floor(this.stickyChromeHeight()));
        // Smaller screens (the window scrolls): also leave out the page's own
        // spacing below the box, so scrolling the page right down brings the
        // box's top exactly under the site's top bar rather than behind it.
        let below = 0;
        if (isWindowScroller) {
            const docBottom = document.documentElement.scrollHeight;
            const boxBottom = box.getBoundingClientRect().bottom + window.scrollY;
            below = Math.max(0, Math.floor(docBottom - boxBottom));
            // More than the page's own spacing (e.g. the Job detail page's
            // sections below the planner): the page scrolls on past the box
            // anyway, so it keeps the full screen height instead of shrinking
            // by everything below it.
            if (below > 120) below = 0;
        }
        box.style.height = `${Math.max(420, visibleHeight - top - below)}px`;
    }

    // Sets the pull-out panel's `top`/`height` inline. Like the day-header,
    // the panel is native `position: sticky` (see planner.component.scss),
    // so this is self-correcting as the page scrolls and only needs
    // recomputing on resize/navigation, same as updateStickyHeaderOffset
    // above — not on every scroll.
    private updatePulloutOffset(): void {
        const panel = document.querySelector('.Planner-pullout') as HTMLElement | null;
        if (!panel) return;

        // Where sticking can't work, the panel stretches to the calendar's
        // full height instead — see .Planner-pullout--stretch.
        const stickyTrapped = this.isStickyTrapped(panel);
        panel.classList.toggle('Planner-pullout--stretch', stickyTrapped);

        // Narrow widths (.Planner-body is a grid there): the panel stretches
        // to the calendar's height instead of sticking — see the scss.
        if (stickyTrapped || (panel.parentElement && getComputedStyle(panel.parentElement).display === 'grid')) {
            panel.style.top = '';
            panel.style.height = '';
            return;
        }

        const top =Math.max(0, Math.floor(this.stickyChromeHeight()));
        panel.style.top = `${top}px`;

        // Height is the space the panel gets once stuck: the scroll
        // container's visible height minus the sticky chrome above it. Not
        // measured from the panel's current on-screen position — that
        // depends on how far the page happens to be scrolled when this runs
        // (e.g. mid-resize), so an unscrolled page produced a panel that
        // then stopped short of the bottom once scrolled and stuck.
        const container = this.findScrollContainer(panel);
        const isWindowScroller = container === document.scrollingElement
            || container === document.documentElement
            || container === document.body;
        const visibleHeight = isWindowScroller ? window.innerHeight : container.clientHeight;
        let height = Math.max(0, visibleHeight - top);
        // The whole day on screen (_wholeDay): no taller than the calendar
        // beside it, so neither leaves empty space under the other.
        const people = this._wholeDay
            ? panel.parentElement?.querySelector('.Planner-people') as HTMLElement | null
            : null;
        if (people?.offsetHeight) height = Math.min(height, people.offsetHeight);
        panel.style.height = `${height}px`;
    }

    // `position: sticky` only follows the nearest ancestor that clips its
    // overflow (hidden/auto/scroll). If that isn't the element that actually
    // scrolls, the panel can never stick — it just sits at the top at one
    // viewport's height, stopping partway down the calendar. That's the
    // case on the Job detail page, where .Job-body has `overflow: hidden`
    // between the Planner and the scrolling .Page.
    private isStickyTrapped(panel: HTMLElement): boolean {
        const scroller = this.findScrollContainer(panel);
        let p: HTMLElement | null = panel.parentElement;
        while (p && p !== scroller && p !== document.body && p !== document.documentElement) {
            const cs = getComputedStyle(p);
            if (/hidden|auto|scroll/.test(`${cs.overflowX} ${cs.overflowY}`)) return true;
            p = p.parentElement;
        }
        return false;
    }

    // Height (px) that the Actions page's sticky search bar will cover once
    // fully stuck, plus the global nav bar's height when it's rendered as a
    // fixed top bar (narrow widths) rather than a side bar (desktop).
    private stickyChromeHeight(): number {
        let height = 0;

        const nav = document.querySelector('.nav') as HTMLElement | null;
        if (nav && getComputedStyle(nav).position === 'fixed') {
            const rect = nav.getBoundingClientRect();
            // Skip the desktop layout where .nav is a tall narrow side bar —
            // only a wide-and-short top bar at the very top counts.
            if (rect.top <= 0 && rect.width > rect.height * 2) {
                height += rect.bottom;
            }
        }

        const searchBar = document.querySelector('.ActionSearch') as HTMLElement | null;
        if (searchBar && getComputedStyle(searchBar).position === 'sticky') {
            height += searchBar.getBoundingClientRect().height;
        }

        return height;
    }

    // Walks up from `el` to find whichever ancestor actually has a
    // scrollable overflow at the current viewport size — the page body on
    // most widths, or the window itself below it, depending on layout.
    private findScrollContainer(el: HTMLElement): HTMLElement {
        let p: HTMLElement | null = el.parentElement;
        while (p && p !== document.body && p !== document.documentElement) {
            const cs = getComputedStyle(p);
            if ((cs.overflowY === 'auto' || cs.overflowY === 'scroll') && p.scrollHeight > p.clientHeight) {
                return p;
            }
            p = p.parentElement;
        }
        return (document.scrollingElement || document.documentElement) as HTMLElement;
    }

    /** Vertical extent of the time-grid (viewport coords): `top` is the y of
     *  00:00 and `height` spans the full 24h. */
    private getCalendarGridGeometry(): { top: number; height: number } | null {
        const calEl = this.calendarElRef?.nativeElement;
        if (!calEl) return null;

        const slotsEl = calEl.querySelector('.fc-timegrid-slots') as HTMLElement | null;
        if (slotsEl) {
            const r = slotsEl.getBoundingClientRect();
            if (r.height > 0) return { top: r.top, height: r.height };
        }
        const lanes = calEl.querySelectorAll('.fc-timegrid-slot-lane[data-time]') as NodeListOf<HTMLElement>;
        if (lanes.length >= 2) {
            const firstR = lanes[0].getBoundingClientRect();
            const lastR = lanes[lanes.length - 1].getBoundingClientRect();
            const height = (lastR.top + lastR.height) - firstR.top;
            if (height > 0) return { top: firstR.top, height };
        }
        return null;
    }

    /** The scroll container and the y (viewport coords) of the vertical
     *  centre of its *visible* region — i.e. below the sticky search bar. */
    private getCalendarVisibleCentre(): { container: HTMLElement; centreY: number; visibleTop: number; visibleBottom: number } | null {
        const calEl = this.calendarElRef?.nativeElement;
        if (!calEl) return null;
        const anchor = (calEl.querySelector('.fc-timegrid-slots')
            || calEl.querySelector('[data-time]')) as HTMLElement | null;
        if (!anchor) return null;

        const scrollContainer = this.findScrollContainer(anchor);
        if (!scrollContainer) return null;
        const isWindowScroller = scrollContainer === document.scrollingElement
            || scrollContainer === document.documentElement
            || scrollContainer === document.body;
        const stickyHeight = this.stickyChromeHeight();
        const containerTop = isWindowScroller
            ? 0
            : (scrollContainer.getBoundingClientRect?.()?.top ?? 0);
        const visibleTop = containerTop + stickyHeight;
        const visibleBottom = isWindowScroller
            ? window.innerHeight
            : containerTop + scrollContainer.clientHeight;
        return { container: scrollContainer, centreY: (visibleTop + visibleBottom) / 2, visibleTop, visibleBottom };
    }

    /** First load (spec: "All 24 hours scrollable, opening focused on
     *  08:00–18:00"): the one-person view on desktop scrolls the page so
     *  08:00 sits at the top, just under the pinned day header — the rows
     *  are sized so the hours to 18:00 fill what's below (applySlotHeight).
     *  Where the calendar scrolls in its own box (several people, smaller
     *  screens), that box opens on 08:00 the same way (ensureCompactScrolled). */
    private scrollToWorkingHours(): void {
        if (this.usesScrollArea) return;
        // A job's page: the planner is one part of a longer page — opening
        // it shouldn't scroll the whole page down to the calendar.
        if (this.jobPeoplePicker) return;
        // The whole day's on screen — nothing to scroll to (checked before
        // anything's measured: on load the calendar may not be drawn yet).
        if (this.fitsWholeDay) return;
        this.applySlotHeight(); // the rows' final height before measuring
        const geom = this.getCalendarGridGeometry();
        const vc = this.getCalendarVisibleCentre();
        if (!geom || !vc) return;
        const hourPx = geom.height / 24;
        // The calendar's own day header sticks under the search bar too.
        const dayHeader = this.calendarElRef?.nativeElement.querySelector('.fc-scrollgrid-section-header') as HTMLElement | null;
        const visibleTop = vc.visibleTop + (dayHeader?.offsetHeight ?? 0);
        const PAD = 4;
        this.scrollContainerBy(vc.container, geom.top + 8 * hourPx - visibleTop - PAD);
    }

    // ── Row height: the zoom level's hours (08:00–18:00) fit on screen ──
    // From the space the hours have below whatever's pinned above them
    // (several people / smaller screens: the scroll box, under the name row
    // and day header; one person on desktop: the screen under the search bar
    // and day header), within the level's min/max (zoomLevels). Set as a CSS
    // variable on .Planner (--planner-slot-h, read by .fc-timegrid-slot);
    // the calendars re-measure only when it actually changes.
    private _slotHeight = 0;

    private applySlotHeight(): void {
        const root = this.plannerRootRef?.nativeElement;
        if (!root) return;
        const level = this.zoomLevels[this.zoomLevel];
        const dayHeader = root.querySelector('.PlannerPerson-calendar .fc-scrollgrid-section-header') as HTMLElement | null;
        const dayHeaderH = dayHeader?.offsetHeight || 70;
        let available: number;
        const area = root.querySelector('.Planner-peopleList--scroll') as HTMLElement | null;
        if (area) {
            const personHeader = area.querySelector('.PlannerPerson-header') as HTMLElement | null;
            available = area.clientHeight - (personHeader?.offsetHeight ?? 0) - dayHeaderH;
        } else {
            available = document.documentElement.clientHeight - this.stickyChromeHeight() - dayHeaderH;
        }
        const MARGIN = 8;
        const fit = Math.floor((available - MARGIN) / (level.hours * level.slotsPerHour));
        let height = Math.max(level.min, Math.min(level.max, fit || level.max));
        // Zoomed right out: the whole day fills the space (fitsWholeDay) —
        // once the calendar's drawn, as that's measured from it.
        let wholeDay = false;
        if (this.fitsWholeDay) {
            const whole = this.wholeDaySlotHeight(root, area);
            if (whole != null) {
                height = whole;
                wholeDay = true;
            }
        }
        this._wholeDay = wholeDay;
        // Only the whole day's rows take this height for certain — see
        // .Planner--wholeDay in the stylesheet.
        const wholeDayChanged = root.classList.contains('Planner--wholeDay') !== wholeDay;
        root.classList.toggle('Planner--wholeDay', wholeDay);
        if (height === this._slotHeight && !wholeDayChanged) return;
        this._slotHeight = height;
        root.style.setProperty('--planner-slot-h', `${height}px`);
        this.allApis().forEach((api) => api.updateSize());
    }

    // The whole day on screen (_wholeDay): the 24 hours exactly as tall
    // as the space the calendar has — the Actions panel's own height (see
    // updatePulloutOffset), or the people's scroll box less a name row — so
    // nothing's left empty under the last hour. Worked out from the
    // calendar as drawn (its day header and borders), not estimated; in
    // quarter pixels, so it fills to within a few. Null until it's drawn.
    private wholeDaySlotHeight(root: HTMLElement, area: HTMLElement | null): number | null {
        const cal = root.querySelector('.PlannerPerson-calendar') as HTMLElement | null;
        const body = cal?.querySelector('.fc-timegrid-body') as HTMLElement | null;
        if (!cal || !body || !body.offsetHeight) return null;
        let space: number;
        if (area) {
            const personHeader = area.querySelector('.PlannerPerson-header') as HTMLElement | null;
            space = area.clientHeight - (personHeader?.offsetHeight ?? 0);
        } else {
            space = this.visibleHeightBelowChrome(cal);
        }
        const aroundRows = cal.offsetHeight - body.offsetHeight;
        const MARGIN = 2;
        const fit = Math.floor(((space - aroundRows - MARGIN) / 24) * 4) / 4;
        return fit > 0 ? Math.max(14, fit) : null;
    }

    // The height left for the planner once the page is scrolled down to it:
    // the scrolling area's visible height under the pinned bars above. The
    // Actions panel is exactly this tall (updatePulloutOffset).
    private visibleHeightBelowChrome(el: HTMLElement): number {
        const container = this.findScrollContainer(el);
        const isWindowScroller = container === document.scrollingElement
            || container === document.documentElement
            || container === document.body;
        const visibleHeight = isWindowScroller ? window.innerHeight : container.clientHeight;
        return Math.max(0, visibleHeight - Math.max(0, Math.floor(this.stickyChromeHeight())));
    }

    // Instant (no smooth animation), whatever the container's own setting.
    private scrollContainerBy(container: HTMLElement, delta: number): void {
        const prevBehavior = container.style.scrollBehavior;
        container.style.scrollBehavior = 'auto';
        container.scrollTop += delta;
        container.style.scrollBehavior = prevBehavior;
    }

    // Pulls through Client/Action type/Job type/Search from the Actions
    // page's own filter bar, plus the pull-out's OWN When filter (spec: the
    // pull-out has its own, default Today) in place of the filter bar's.
    // Owner is always overridden to the resolved subject user, never the
    // filter bar's raw value, since that can be a group or multi-user
    // selection that doesn't map to a single planner.
    //
    // Two searches (see pulloutTabs for why): the When period's own
    // (Unplanned candidates, same meaning as the Actions page's When), and —
    // unless When is All or Today's deadlines, where that one already
    // covers it — every live action, from which the Planned tab picks those
    // with a block in the period.
    private loadUnplannedActions(token: number): void {
        // Everyone shown (several-people view), else the one person.
        const owner = this.pulloutOwner();
        if (owner === undefined) return; // '' = everyone ("All" view)
        // Changing the When filter re-loads just this list, so it has its
        // own token too (a newer When choice wins over an older reply).
        // Empty filters as "none" (undefined), whichever way they arrive —
        // the page opening searches before the search bar's filter has
        // loaded, then again once it has, with '' / [] where there was
        // nothing before; the same search either way, so the same key.
        const blank = <T>(v: T): T | undefined =>
            ((v as unknown) === '' || v === null || (Array.isArray(v) && !v.length) ? undefined : v);
        const baseQuery: JobActionSearchQuery = {
            clientId: blank(this._filterQuery?.clientId),
            jobId: blank(this._filterQuery?.jobId),
            type: blank(this._filterQuery?.type),
            jobTypeIds: blank(this._filterQuery?.jobTypeIds),
            searchTerm: blank(this._filterQuery?.searchTerm),
            owner,
            page: 1,
            resultsPerPage: -1,
        };
        const when = this.effectivePulloutWhen;
        const whenQuery: JobActionSearchQuery = { ...baseQuery, timePeriod: when };
        const needsAll = when !== JobActionTimePeriod.All
            && when !== JobActionTimePeriod.TodaysDeadlines;
        const allQuery: JobActionSearchQuery | null = needsAll ? { ...baseQuery, timePeriod: JobActionTimePeriod.All } : null;

        const whenKey = JSON.stringify(whenQuery);
        const allKey = allQuery ? JSON.stringify(allQuery) : null;
        // That exact search is already on its way: its reply serves —
        // asking again would only show "Loading…" a second time and throw
        // the first reply away.
        const searchKey = `${whenKey}|${allKey}`;
        if (searchKey === this._pulloutSearchInFlight) return;

        // Changing the When filter (or whose actions) re-loads just this
        // list, with its own token (a newer search wins over an older reply).
        const pulloutToken = ++this._pulloutToken;
        const isCurrent = () => !this._destroyed && pulloutToken === this._pulloutToken;

        // Cached from an earlier visit with these same filters: shown
        // straight away (no "Loading…"), then quietly refreshed below.
        const cachedWhen = this._plannerCache.getUnplanned(whenKey);
        const cachedAll = allKey ? this._plannerCache.getUnplanned(allKey) : null;
        const haveCache = !!cachedWhen && (!allKey || !!cachedAll);
        if (haveCache) {
            this.applyPulloutLists(cachedWhen, cachedAll);
            this.isLoadingUnplanned = false;
        } else {
            this.applyPulloutLists([], null);
            this.isLoadingUnplanned = true;
        }
        this._changeRef.markForCheck(); // see openContextMenu

        this._pulloutSearchInFlight = searchKey;
        // Each sent once, shared by both subscriptions below.
        const when$ = this._actionService.searchActions(whenQuery).pipe(shareReplay(1));
        const all$ = allQuery ? this._actionService.searchActions(allQuery).pipe(shareReplay(1)) : of(null);

        // Nothing to show yet: the Unplanned tab only needs the When search
        // (which are already planned comes with it — hasPlannedBlock), so
        // it's shown as soon as that's back, without waiting for the larger
        // All search, which only the Planned tab uses (that tab keeps its
        // spinner meanwhile — pulloutAllPending).
        if (!haveCache) {
            this.pulloutAllPending = !!allQuery;
            this.subscription.add(
                when$.subscribe({
                    next: (whenActions) => {
                        if (!isCurrent()) return;
                        this.isLoadingUnplanned = false;
                        this.applyPulloutLists(whenActions || [], null, false);
                        this._changeRef.markForCheck();
                    },
                    error: () => {}, // handled below
                })
            );
        }

        this.subscription.add(
            forkJoin([when$, all$]).pipe(
                finalize(() => {
                    if (this._pulloutSearchInFlight === searchKey) this._pulloutSearchInFlight = null;
                })
            ).subscribe({
                next: ([whenActions, allActions]) => {
                    const whenList = whenActions || [];
                    const allList = allQuery ? allActions || [] : null;
                    this._plannerCache.setUnplanned(whenKey, whenList);
                    if (allKey && allList) this._plannerCache.setUnplanned(allKey, allList);
                    if (!isCurrent()) return;
                    this.isLoadingUnplanned = false;
                    this.pulloutAllPending = false;
                    this._changeRef.markForCheck();
                    // Nothing changed since the cached copy shown above.
                    if (haveCache
                        && JSON.stringify(whenList) === JSON.stringify(cachedWhen)
                        && JSON.stringify(allList) === JSON.stringify(cachedAll)) return;
                    this.applyPulloutLists(whenList, allList);
                    // toCalendarEvent falls back to unplannedActions for a
                    // block's job/client should the API ever not supply
                    // them — re-render so those pick it up. Served from the
                    // block cache, so this doesn't re-request anything.
                    this.refetchAllCalendars();
                },
                error: () => {
                    if (!isCurrent()) return;
                    this.isLoadingUnplanned = false;
                    this.pulloutAllPending = false;
                    this.plannedInfoReady = true; // never a spinner for good
                    this._changeRef.markForCheck();
                },
            })
        );
    }

    // Everything either tab might list, in one ordered list (so drag-to-
    // calendar and reordering work the same on both tabs); then which are
    // Planned is checked against their blocks (checkPlanned — not for the
    // early, When-only list, which the full one replaces moments later).
    private applyPulloutLists(whenList: JobAction[], allList: JobAction[] | null, checkPlanned = true): void {
        // Exactly what the When search returns — the same actions the
        // Actions page lists for that When, so overdue ones that are still
        // live are included (e.g. "Today" = today or earlier): they're the
        // ones most worth planning in. Their cards say they're overdue
        // (isOverdue). Planning onto a past day stays locked regardless.
        const seen = new Set<number>();
        const union = [...whenList, ...(allList || [])].filter((a) => {
            if (seen.has(a.jobActionId)) return false;
            seen.add(a.jobActionId);
            return true;
        });
        this._pulloutWhenIds = new Set(whenList.map((a) => a.jobActionId));
        // Until the planned check below replies, which actions are already
        // planned (a block today or later) comes from the search's own flag
        // (hasPlannedBlock) — so the Unplanned tab is right from the start,
        // rather than listing everything and then moving the planned ones
        // out. The Planned tab (which needs each block's time) waits for
        // the check (plannedInfoReady).
        this._plannedFromToday = new Map(
            union.filter((a) => a.hasPlannedBlock).map((a) => [a.jobActionId, { nextStart: new Date(0), count: 0 }] as [number, PlannedSummary])
        );
        this._plannedInPeriod = new Map();
        this.plannedInfoReady = false;
        this._plannedInfoVersion++;
        this.setUnplannedActions(union);
        if (checkPlanned) this.refreshPlannedInfo();
    }

    /** Scheduled before today and still live — shown in the panel, flagged
     *  "Overdue" on its card. */
    isOverdue(action: JobAction): boolean {
        if (!action.scheduleDate) return false;
        const startOfToday = new Date();
        startOfToday.setHours(0, 0, 0, 0);
        return new Date(action.scheduleDate) < startOfToday;
    }

    /** The When the pull-out uses: the page's (followPageWhen) — "All"
     *  when it has none set, as the page's list treats it; until the page's
     *  filter has loaded, the panel's remembered one (Today by default, as
     *  the page's) rather than searching everything meanwhile — or, with no
     *  page When, the panel's own. */
    get effectivePulloutWhen(): JobActionTimePeriod {
        if (!this.followPageWhen || !this._filterQuery) return this.pulloutWhen;
        return this._filterQuery.timePeriod || JobActionTimePeriod.All;
    }

    /** The action pickers' note: the period they list (the When filter in
     *  use — the page's, or the panel's own). None for All. */
    get pickerWhenLabel(): string {
        const when = this.effectivePulloutWhen;
        if (when === JobActionTimePeriod.All) return '';
        return this.pulloutWhenOptions.find((o) => o.value === when)?.label || '';
    }

    onPulloutWhenChange(value: string): void {
        this.clearHighlight();
        this.pulloutWhen = value as JobActionTimePeriod;
        this._plannerCache.pulloutWhen = this.pulloutWhen;
        this.loadUnplannedActions(this._requestToken);
    }

    setPulloutTab(tab: 'unplanned' | 'planned'): void {
        this.pulloutTab = tab;
        this._plannerCache.pulloutTab = tab;
        if (tab !== 'planned') this.clearHighlight();
    }

    /** The When period's block range for the Planned tab — `to` null means
     *  open-ended. Blocks before today never count (spec: Planned means
     *  today or later), so a period that has already started begins today. */
    private pulloutPeriodRange(): { from: Date; to: Date | null } {
        const day = (offset: number) => {
            const d = new Date();
            d.setHours(0, 0, 0, 0);
            d.setDate(d.getDate() + offset);
            return d;
        };
        const today = day(0);
        const nextMonday = getMondayOf(addDays(today, 7));
        switch (this.effectivePulloutWhen) {
            case JobActionTimePeriod.Today: return { from: today, to: day(1) };
            case JobActionTimePeriod.Tomorrow: return { from: day(1), to: day(2) };
            case JobActionTimePeriod.TodayTomorrow: return { from: today, to: day(2) };
            case JobActionTimePeriod.Week: return { from: today, to: nextMonday };
            case JobActionTimePeriod.NextWeek: return { from: nextMonday, to: addDays(nextMonday, 7) };
            default: return { from: today, to: null }; // All, Today's deadlines
        }
    }

    // Re-checks blocks for everything listed: any from today on (decides
    // Unplanned) and any in the When period (decides Planned, and its "Next
    // block" line). After the list loads, and after any block change (see
    // the blocksInvalidated$ subscription in ngOnInit).
    private refreshPlannedInfo(): void {
        const ids = this.unplannedActions.map((a) => a.jobActionId);
        const token = ++this._plannedInfoToken;
        if (!ids.length) {
            this.setPlannedInfo(new Map(), new Map());
            return;
        }
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const period = this.pulloutPeriodRange();
        const samePeriod = period.to === null && period.from.getTime() === today.getTime();
        forkJoin([
            this._plannedBlockService.getPlannedActions(ids, today),
            samePeriod ? of(null) : this._plannedBlockService.getPlannedActions(ids, period.from, period.to),
        ]).subscribe({
            next: ([fromToday, inPeriod]) => {
                if (this._destroyed || token !== this._plannedInfoToken) return;
                const toMap = (list: PlannedActionInfo[]) => new Map(
                    list.map((p) => [p.jobActionId, { nextStart: new Date(p.nextStart), count: p.blockCount }] as [number, PlannedSummary])
                );
                const fromTodayMap = toMap(fromToday);
                this.setPlannedInfo(fromTodayMap, inPeriod ? toMap(inPeriod) : fromTodayMap);
            },
            // Leaves the tabs as they were — never blanks the list.
            error: () => {
                if (this._destroyed || token !== this._plannedInfoToken) return;
                this.plannedInfoReady = true;
                this._changeRef.markForCheck();
            },
        });
    }

    private setPlannedInfo(fromToday: Map<number, PlannedSummary>, inPeriod: Map<number, PlannedSummary>): void {
        this._plannedFromToday = fromToday;
        this._plannedInPeriod = inPeriod;
        this.plannedInfoReady = true;
        this._plannedInfoVersion++;
        this._changeRef.markForCheck();
    }

    /** Both tabs' lists: Unplanned in the user's own order, Planned
     *  chronologically by when each action's first block in the period
     *  starts (earliest at the top). Memoised — read on every change
     *  detection. */
    get pulloutGroups(): { unplanned: JobAction[]; planned: JobAction[] } {
        const memo = this._pulloutGroupsMemo;
        if (memo && memo.actions === this.unplannedActions && memo.version === this._plannedInfoVersion) {
            return memo.groups;
        }
        const groups = {
            unplanned: this.unplannedActions.filter(
                (a) => this._pulloutWhenIds.has(a.jobActionId) && !this._plannedFromToday.has(a.jobActionId)
            ),
            planned: this.unplannedActions
                .filter((a) => this._plannedInPeriod.has(a.jobActionId))
                .sort((a, b) =>
                    this._plannedInPeriod.get(a.jobActionId).nextStart.getTime()
                    - this._plannedInPeriod.get(b.jobActionId).nextStart.getTime()
                ),
        };
        this._pulloutGroupsMemo = { actions: this.unplannedActions, version: this._plannedInfoVersion, groups };
        return groups;
    }

    /** The open tab's list, as the section(s) it's shown in.
     *  - One person shown: one list. Unplanned can be reordered; Planned
     *    is in time order (not reorderable).
     *  - Several people (either tab): one group per person, collapsible
     *    (isPulloutSectionOpen), so a long list of everyone's isn't one long
     *    scroll. The user's own first when they're shown ("Your actions":
     *    owned by them or their team, or waiting on them), then each other
     *    person's in the planner's people order (headed with their name),
     *    then anything that's no one's on screen ("Other actions").
     *    Separate lists, so they never mix. On Unplanned the user's own can
     *    be reordered among themselves, or, with them not shown, the first
     *    person's (whose order the panel saves, see pulloutOrderKey).
     *    Headings only when there's more than one group.
     *  Memoised. */
    get pulloutSections(): PulloutSection[] {
        const groups = this.pulloutGroups;
        const me = this._loginService.userId;
        const several = this.people.length > 1;
        const memo = this._pulloutSectionsMemo;
        if (memo && memo.groups === groups && memo.tab === this.pulloutTab && memo.several === several
            && memo.people === this.people) {
            return memo.sections;
        }

        const unplanned = this.pulloutTab === 'unplanned';
        const list = unplanned ? groups.unplanned : groups.planned;
        let sections: PulloutSection[];
        if (!several) {
            sections = [{ key: this.pulloutTab, label: '', actions: list, reorderable: unplanned }];
        } else {
            const meShown = !!me && this.people.some((p) => p.userId === me);
            const others = this.people.filter((p) => !meShown || p.userId !== me);
            const mine: JobAction[] = [];
            const byPerson = new Map<string, JobAction[]>(others.map((p) => [p.userId, []] as [string, JobAction[]]));
            const rest: JobAction[] = [];
            list.forEach((a) => {
                if (meShown && this.isMyAction(a)) {
                    mine.push(a);
                    return;
                }
                // The first person shown it belongs to (people order).
                const owner = others.find((p) => this.isActionOf(a, p.userId));
                if (owner) byPerson.get(owner.userId).push(a);
                else rest.push(a);
            });
            sections = [
                ...(meShown ? [{ key: 'mine', label: 'Your actions', actions: mine, reorderable: unplanned }] : []),
                ...others.map((p, i) => ({
                    key: `person-${p.userId}`,
                    label: p.name || '',
                    actions: byPerson.get(p.userId),
                    reorderable: unplanned && !meShown && i === 0,
                })),
                { key: 'rest', label: 'Other actions', actions: rest, reorderable: false },
            ].filter((sec) => sec.actions.length);
            // A single group needs no heading.
            if (sections.length === 1) sections = [{ ...sections[0], label: '' }];
        }
        this._pulloutSectionsMemo = { groups, tab: this.pulloutTab, several, people: this.people, sections };
        return sections;
    }
    private _pulloutSectionsMemo: { groups: { unplanned: JobAction[]; planned: JobAction[] }; tab: string; several: boolean; people: PlannerPerson[]; sections: PulloutSection[] } | null = null;

    trackBySection(_: number, section: PulloutSection): string {
        return section.key;
    }

    // ─── The panel's groups: open or closed ─────────────────────────────
    // Several people shown (pulloutSections): the user's own group starts
    // open and everyone else's closed (or, with the user not shown, just
    // the first open), so the list is short until a person's opened. Each
    // group remembers being opened or closed, per browser.
    private static readonly PULLOUT_OPEN_KEY = 'planner_pullout_open_groups';
    private _pulloutOpen: Record<string, boolean> = PlannerComponent.readPulloutOpen();

    private static readPulloutOpen(): Record<string, boolean> {
        try {
            const parsed = JSON.parse(localStorage.getItem(PlannerComponent.PULLOUT_OPEN_KEY) || '{}');
            return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
        } catch {
            return {};
        }
    }

    isPulloutSectionOpen(section: PulloutSection, index: number): boolean {
        // No heading (a single list): nothing to open or close.
        if (!section.label) return true;
        const stored = this._pulloutOpen[section.key];
        if (typeof stored === 'boolean') return stored;
        const hasMine = this.pulloutSections.some((sec) => sec.key === 'mine');
        return hasMine ? section.key === 'mine' : index === 0;
    }

    togglePulloutSection(section: PulloutSection, index: number): void {
        this._pulloutOpen = { ...this._pulloutOpen, [section.key]: !this.isPulloutSectionOpen(section, index) };
        try {
            localStorage.setItem(PlannerComponent.PULLOUT_OPEN_KEY, JSON.stringify(this._pulloutOpen));
        } catch { /* storage unavailable */ }
    }

    // ── Planned tab: ⋮ → "Unplan all blocks" ──
    // Takes an action off the planner from today on (its blocks today and
    // later — earlier days are history and stay, per the spec), so it goes
    // back to Unplanned. Only blocks this user may remove go: their own, or
    // everyone's with the Planner role (the server applies the same rule).

    pulloutMenuActionId: number | null = null;

    /** The ⋮ is offered on the user's own actions, or any with the Planner role. */
    canUnplanAction(action: JobAction): boolean {
        return this._loginService.checkAuth(['Planner']) || this.isMyAction(action);
    }

    // ── Planned tab: click a card → its blocks highlighted on the calendar ──
    // Every block of that action on every planner shown keeps its colour,
    // with a ring in it; all other blocks fade back. Matching blocks carry a
    // data-hl attribute — set as they mount (onEventDidMount) and swept after
    // any change (applyHighlight) — which FullCalendar's own re-renders
    // leave alone (it manages their classes, not other attributes). Stays on
    // across weeks; off on a second click, Escape, a press on an empty part
    // of a calendar, or leaving the Planned tab / closing the panel.

    highlightActionId: number | null = null;
    // Any of its blocks in the dates shown — the rest only fade when there
    // is; otherwise the card says so.
    highlightInView = false;
    private _highlightSweepTimer: any = null;

    onPulloutCardClick(action: JobAction, e: MouseEvent): void {
        if (this.pulloutTab !== 'planned') return;
        const target = e.target as HTMLElement | null;
        // A click that has just closed something (e.g. a ⋮ menu item, whose
        // menu is gone by the time the click reaches the card) isn't a click
        // on the card itself.
        if (!target?.isConnected) return;
        // Its ⋮ menu, grip and owner avatars do their own thing.
        if (target.closest('.PlannerPullout-menuWrap, .PlannerPullout-handle, my-avatar')) return;
        this.toggleHighlight(action.jobActionId);
    }

    toggleHighlight(jobActionId: number): void {
        this.highlightActionId = this.highlightActionId === jobActionId ? null : jobActionId;
        this.applyHighlight(this.highlightActionId != null);
    }

    clearHighlight(): void {
        if (this.highlightActionId == null) return;
        this.highlightActionId = null;
        this.applyHighlight(false);
    }

    /** A press on an empty part of a calendar (not on a block) ends it. */
    onCalendarAreaMousedown(e: MouseEvent): void {
        if (this.highlightActionId == null) return;
        const target = e.target as HTMLElement | null;
        if (target?.closest('.fc-event:not(.fc-bg-event)')) return;
        this.clearHighlight();
    }

    private scheduleHighlightSweep(): void {
        if (this.highlightActionId == null || this._highlightSweepTimer) return;
        this._highlightSweepTimer = setTimeout(() => {
            this._highlightSweepTimer = null;
            if (!this._destroyed) this.applyHighlight(false);
        }, 0);
    }

    // Marks the action's blocks (and unmarks the rest) across every
    // calendar shown; on turning it on, brings the first one into view if
    // none is visible.
    private applyHighlight(scrollToFirst: boolean): void {
        const root = this.plannerBodyRef?.nativeElement;
        const id = this.highlightActionId;
        // No longer on the Planned tab's list (e.g. unplanned meanwhile).
        if (id != null && this.plannedInfoReady && !this.pulloutGroups.planned.some((a) => a.jobActionId === id)) {
            this.highlightActionId = null;
            this.applyHighlight(false);
            return;
        }
        const matches: HTMLElement[] = [];
        root?.querySelectorAll('.fc-event:not(.fc-bg-event)').forEach((node) => {
            const el = node as HTMLElement;
            const actionId = getElSeg(el)?.eventRange?.def.extendedProps?.['jobActionId'];
            if (id != null && actionId === id) {
                if (!el.hasAttribute('data-hl')) el.setAttribute('data-hl', '');
                matches.push(el);
            } else if (el.hasAttribute('data-hl')) {
                el.removeAttribute('data-hl');
            }
        });
        this.highlightInView = matches.length > 0;
        this._changeRef.markForCheck();

        if (scrollToFirst && matches.length) {
            // The earliest one (top-most, then left-most) — centred in the
            // calendar's visible area.
            const first = matches.reduce((a, b) => {
                const ra = a.getBoundingClientRect();
                const rb = b.getBoundingClientRect();
                return rb.top < ra.top || (rb.top === ra.top && rb.left < ra.left) ? b : a;
            });
            this.scrollBlockToCentre(first);
        }
    }

    /** Smoothly scrolls so a block sits in the middle of the calendar's
     *  visible area — the part below whatever is pinned above the hours
     *  (search bar; the person's name row and day header), so a block
     *  hidden just under those counts as out of view. In Day columns, also
     *  sideways if its column is off to one side. */
    private scrollBlockToCentre(el: HTMLElement): void {
        const r = el.getBoundingClientRect();
        const area = el.closest('.Planner-peopleList--scroll') as HTMLElement | null;
        const section = el.closest('.PlannerPerson') as HTMLElement | null;
        const dayHeader = section?.querySelector('.fc-scrollgrid-section-header') as HTMLElement | null;
        const dayHeaderH = dayHeader?.offsetHeight ?? 0;

        let container: HTMLElement;
        let visibleTop: number;
        let visibleBottom: number;
        let deltaX = 0;
        if (area) {
            const ar = area.getBoundingClientRect();
            const personHeader = section?.querySelector('.PlannerPerson-header') as HTMLElement | null;
            container = area;
            visibleTop = ar.top + (personHeader?.offsetHeight ?? 0) + dayHeaderH;
            visibleBottom = ar.bottom;

            // Day columns: its column off to the left or right.
            if (r.left < ar.left || r.right > ar.right) {
                deltaX = (r.left + r.right) / 2 - (ar.left + ar.right) / 2;
            }
        } else {
            const vc = this.getCalendarVisibleCentre();
            if (!vc) return;
            container = vc.container;
            visibleTop = vc.visibleTop + dayHeaderH;
            visibleBottom = vc.visibleBottom;
        }
        const deltaY = (r.top + r.bottom) / 2 - (visibleTop + visibleBottom) / 2;
        // One scroll for both directions (a second smooth scroll on the
        // same box can cancel the first).
        if (Math.abs(deltaY) > 2 || deltaX) container.scrollBy({ top: deltaY, left: deltaX, behavior: 'smooth' });
    }

    /** The panel card's ⋮: "Go to job" on either tab — unless that job's
     *  own page is the one open — and "Unplan this action" on the Planned
     *  tab (canUnplanAction). No ⋮ when neither applies. */
    canPulloutGoToJob(action: JobAction): boolean {
        return !!action.jobId && !this.isCurrentJobPage(action.jobId);
    }

    canPulloutUnplan(action: JobAction): boolean {
        return this.pulloutTab === 'planned' && this.canUnplanAction(action);
    }

    // Always: "Edit action" is on every card.
    hasPulloutMenu(_action: JobAction): boolean {
        return true;
    }

    pulloutGoToJob(action: JobAction): void {
        this.closePulloutMenu();
        this.goToJob(action.jobId);
    }

    togglePulloutMenu(action: JobAction, e: MouseEvent): void {
        e.stopPropagation();
        this.pulloutMenuActionId = this.pulloutMenuActionId === action.jobActionId ? null : action.jobActionId;
        this._changeRef.markForCheck();
    }

    closePulloutMenu(): void {
        if (this.pulloutMenuActionId == null) return;
        this.pulloutMenuActionId = null;
        this._changeRef.markForCheck();
    }

    unplanAction(action: JobAction): void {
        this.closePulloutMenu();
        const from = new Date();
        from.setHours(0, 0, 0, 0);
        const title = action.title ? `"${this.capitalizeFirst(action.title)}"` : 'this action';
        this._plannedBlockService.getFutureBlockCount([action.jobActionId], from).subscribe({
            next: (count) => {
                if (this._destroyed) return;
                if (!count) {
                    // Planned, but not on a planner this user may change.
                    this._dialog.open(AlertDialog, {
                        width: '480px',
                        maxWidth: '92vw',
                        data: {
                            title: 'Nothing to unplan',
                            message: `The planned blocks of ${title} for today and later days are on other people's planners, which you can't change.`,
                        },
                    });
                    return;
                }
                const blocks = count === 1 ? '1 planned block' : `${count} planned blocks`;
                const dialogRef = this._dialog.open(AlertDialog, {
                    width: '560px',
                    maxWidth: '92vw',
                    panelClass: 'PlannerFutureBlocksDialog', // spacing — see vendor/_material.scss
                    data: {
                        title: `Unplan ${title}?`,
                        message: `This removes its ${blocks} for today and every day after, and moves it back to Unplanned. Its blocks on past days stay on the planner.`,
                        confirm: true,
                        confirmTitle: count === 1 ? 'Unplan block' : 'Unplan blocks',
                        cancelTitle: 'Cancel',
                    },
                });
                dialogRef.afterClosed().subscribe((confirmed) => {
                    if (!confirmed) return;
                    this._plannedBlockService.deleteFutureBlocks([action.jobActionId], from).subscribe({
                        next: () => {
                            // Its blocks may be on anyone's planner shown.
                            this._plannerCache.invalidateAllBlocks(false);
                            if (this._destroyed) return;
                            this.refetchAllCalendars();
                        },
                        error: () => alert('Could not unplan this action. Please try again.'),
                    });
                });
            },
            error: () => alert('Could not unplan this action. Please try again.'),
        });
    }

    /** The logged-in user's own action — see isActionOf. */
    private isMyAction(action: JobAction): boolean {
        const me = this._loginService.userId;
        return !!me && this.isActionOf(action, me);
    }

    /** An action is a person's when it's owned by them or their team, or
     *  waiting on them (the same ways the Owner filter matches one). */
    private isActionOf(action: JobAction, userId: string): boolean {
        if ((action.assignedUsers || []).includes(userId) || action.waitingUserId === userId) return true;
        const user = this._allUsers.find((u) => u.id === userId);
        const teamId = user?.userGroup?.userGroupId ?? user?.userGroupId;
        return teamId != null && (action.assignedGroups || []).includes(teamId);
    }

    /** A pull-out action's owners, for its "Owner" row (always shown): every
     *  person and team it's assigned to — not just the people on screen —
     *  shaped for the multi-avatar the Actions table uses. Memoised per
     *  action object (read on every change detection). */
    pulloutOwners(action: JobAction): { users: { id: string; name: string; imageUrl?: string }[]; teams: string[]; count: number } {
        let owners = this._ownersMemo.get(action);
        if (!owners) {
            const users = this._userService.getUsersFromIds(action.assignedUsers || []).map((u) => ({
                id: u.id,
                name: u.fullName || `${u.firstName} ${u.surname}`,
                imageUrl: this._userService.findAvatarForUser(u.id) ?? undefined,
            }));
            const teams = this._userService.getGroupsFromIds(action.assignedGroups || []).map((g) => g.groupName);
            owners = { users, teams, count: users.length + teams.length };
            this._ownersMemo.set(action, owners);
        }
        return owners;
    }
    private _ownersMemo = new WeakMap<JobAction, { users: { id: string; name: string; imageUrl?: string }[]; teams: string[]; count: number }>();

    /** Several people shown: whose a pull-out action is, among them — the
     *  pull-out lists everyone's actions together then, so each says whose.
     *  An action can have several owners (people, or a team), and can also
     *  be on someone's list because it's waiting on them ("Waiting
     *  internal") without them owning it — the same two ways the Owner
     *  filter itself matches an action. Null with one person shown (it's
     *  plainly theirs). Memoised per list/people — read on every change
     *  detection. */
    pulloutWhose(action: JobAction): PulloutWhose | null {
        if (!this.isSeveralPeople) return null;
        if (this._whoseMemo.actions !== this.unplannedActions || this._whoseMemo.people !== this.people) {
            this._whoseMemo = { actions: this.unplannedActions, people: this.people, byAction: new Map() };
        }
        if (this._whoseMemo.byAction.has(action.jobActionId)) {
            return this._whoseMemo.byAction.get(action.jobActionId);
        }
        const owners = new Set(action.assignedUsers || []);
        const ownerPeople = this.people.filter((p) => owners.has(p.userId));
        const waitingOn = action.waitingUserId && !owners.has(action.waitingUserId)
            ? this.people.find((p) => p.userId === action.waitingUserId) ?? null
            : null;
        const teams = (action.assignedGroups || [])
            .map((id) => this._groupNames.get(id))
            .filter((name): name is string => !!name);
        // Nothing to say (e.g. teams not loaded yet) — no empty row.
        const whose = ownerPeople.length || waitingOn || teams.length
            ? { owners: ownerPeople, waitingOn, teams }
            : null;
        this._whoseMemo.byAction.set(action.jobActionId, whose);
        return whose;
    }
    private _whoseMemo: { actions: JobAction[] | null; people: PlannerPerson[] | null; byAction: Map<number, PulloutWhose | null> } =
        { actions: null, people: null, byAction: new Map() };
    // Team id → name, for teams that own actions (pulloutWhose).
    private _groupNames = new Map<number, string>();

    /** A Planned action's blocks in the When period — its first block's
     *  start, and how many there are (for "+N more"). */
    pulloutPlanned(action: JobAction): PlannedSummary | undefined {
        return this._plannedInPeriod.get(action.jobActionId);
    }

    /** The action's job colours — the same ones its blocks have on the
     *  calendar, so a pull-out item visibly belongs with its blocks. */
    pulloutJobColor(action: JobAction): PlannerColorSet {
        const key = action.jobNumber ?? 0;
        let color = this._pulloutColorCache.get(key);
        if (!color) {
            color = plannerColorForJobNumber(action.jobNumber);
            this._pulloutColorCache.set(key, color);
        }
        return color;
    }
    private _pulloutColorCache = new Map<number, PlannerColorSet>();

    // Date order by default. Once the user has reordered the list (see
    // onPulloutReorder), their saved order wins: items they've positioned
    // come first, in that order, and anything they haven't yet (e.g. a
    // newly-created action) follows in date order — the sort is stable, so
    // the date sort above it decides the order among those.
    private setUnplannedActions(actions: JobAction[]): void {
        // Actions with no scheduled date (possible with When: All) go last.
        const time = (a: JobAction) => (a.scheduleDate ? new Date(a.scheduleDate).getTime() : Number.MAX_SAFE_INTEGER);
        const byDate = [...actions].sort((a, b) => time(a) - time(b));
        const saved = this.readPulloutOrder();
        if (saved.length) {
            const rank = new Map(saved.map((id, i) => [id, i] as [number, number]));
            const rankOf = (a: JobAction) => rank.get(a.jobActionId) ?? saved.length;
            byDate.sort((a, b) => rankOf(a) - rankOf(b));
        }
        this.unplannedActions = byDate;
        this.unplannedCountChange.emit(this.unplannedActions.length);
    }

    // ─── Reordering the Unscheduled list ────────────────────────────────

    // A press on the grip handle belongs to the reorder drag only (CDK's
    // own listener is on this same item element, so it still runs) —
    // stopped here so it never bubbles up to the list, where FullCalendar's
    // Draggable listens, which would otherwise start a drag-to-calendar at
    // the same time. Presses anywhere else on the item pass through as
    // before.
    onPulloutItemPointerDown(e: Event): void {
        const target = e.target as HTMLElement | null;
        if (target?.closest('.PlannerPullout-handle')) {
            e.stopPropagation();
            return;
        }
        // A press on a draggable item that then moves is a drag onto the
        // calendar (FullCalendar's Draggable doesn't announce it) — so the
        // planners it can't go on can say so while it's dragged.
        if (target?.closest('.PlannerPullout-item.is-locked')) return;
        this.watchPulloutDrag(e);
    }

    // ── "Can't add here" overlay while dragging ──
    // Without the Planner role, a block can't go on anyone else's planner —
    // while one is being dragged (from the pull-out, or a block from a
    // calendar), those calendars show an overlay saying so.
    dragInProgress = false;
    private _pulloutDragCleanup: (() => void) | null = null;

    // The planner the pointer is over during a drag — its overlay shows the
    // message (the rest are only dimmed). Worked out from the pointer's
    // position (trackDragHover), not mouseenter/mouseleave: those fire on
    // and off mid-drag as the dragged block and the calendar redraw under
    // the pointer, which made the message flash.
    noDropHoverUserId: string | null = null;
    private _dragHoverCleanup: (() => void) | null = null;

    // What's being dragged: its action (0 if deleted / unknown), and the
    // planner it's coming from (null: the panel). For cannotDropOn.
    private _dragAction: { jobActionId: number; fromUserId: string | null; isBusy?: boolean } | null = null;

    /** While dragging: this person's planner won't take what's dragged — not
     *  one the user may change, or (with the Planner role) someone else's
     *  and the action isn't theirs (canPlanActionOn). Its own planner always
     *  takes a block back. */
    cannotDropOn(userId: string): boolean {
        if (!this.canEditPlannerOf(userId)) return true;
        const drag = this._dragAction;
        if (!drag || drag.fromUserId === userId || drag.isBusy) return false;
        return !this.canPlanActionOn(drag.jobActionId, userId);
    }

    private setDragInProgress(value: boolean): void {
        if (this.dragInProgress === value) return;
        this.dragInProgress = value;
        this.markLockedColumns(value);
        if (value) {
            this.trackDragHover();
        } else {
            this._dragHoverCleanup?.();
            this.noDropHoverUserId = null;
            this.setBlockOverPanel(false);
            this._draggedBlock = null;
            this._dragAction = null;
            this._droppedOnPanel = false;
        }
        this._changeRef.markForCheck(); // see openContextMenu
    }

    // ── While dragging: past days are locked ──
    // Their columns, on the planners what's dragged could go on (the rest
    // already say "Can't add here" — cannotDropOn), are marked for the drag
    // (data-locked). While it's over one of them, that person's locked
    // columns all get a soft veil (data-over-locked on their calendar) —
    // not other people's — and a short note follows the pointer. Marked on the columns
    // themselves (data-locked / data-locked-hover — attributes FullCalendar
    // leaves alone); see the stylesheet.
    private _lockedHoverCol: HTMLElement | null = null;

    private markLockedColumns(on: boolean): void {
        const root = this.plannerBodyRef?.nativeElement;
        if (!root) return;
        if (!on) {
            document.body.classList.remove('PlannerDrag--overLocked');
            this.lockedNoteRef?.nativeElement.classList.remove('is-visible');
            root.querySelectorAll('.fc-timegrid-col[data-locked]').forEach((col) => {
                col.removeAttribute('data-locked');
                col.removeAttribute('data-locked-hover');
            });
            root.querySelectorAll('.PlannerPerson-calendar[data-over-locked]').forEach((cal) => cal.removeAttribute('data-over-locked'));
            this._lockedHoverCol = null;
            return;
        }
        root.querySelectorAll('.PlannerPerson-calendar[data-user-id]').forEach((cal) => {
            const userId = cal.getAttribute('data-user-id');
            if (!userId || this.cannotDropOn(userId)) return;
            cal.querySelectorAll('.fc-timegrid-col[data-date]').forEach((col) => {
                const [y, m, d] = ((col as HTMLElement).dataset['date'] || '').split('-').map(Number);
                if (y && m && d && this.isPastDay(new Date(y, m - 1, d))) col.setAttribute('data-locked', '');
            });
        });
    }

    private updateLockedHover(x: number, y: number): void {
        let hovered: HTMLElement | null = null;
        this.plannerBodyRef?.nativeElement.querySelectorAll('.fc-timegrid-col[data-locked]').forEach((col) => {
            const r = (col as HTMLElement).getBoundingClientRect();
            if (x >= r.left && x < r.right && y >= r.top && y < r.bottom) hovered = col as HTMLElement;
        });
        if (hovered !== this._lockedHoverCol) {
            this._lockedHoverCol?.removeAttribute('data-locked-hover');
            (hovered as HTMLElement | null)?.setAttribute('data-locked-hover', '');
            this._lockedHoverCol = hovered;
            // The "no" cursor — page-wide for that moment, as the pointer is
            // really over the dragged copy (its own grab cursor otherwise).
            document.body.classList.toggle('PlannerDrag--overLocked', !!hovered);
            // Over a locked column: all of that person's locked columns grey
            // out together (only theirs — not other people's).
            const overCal = (hovered as HTMLElement | null)?.closest('.PlannerPerson-calendar') ?? null;
            this.plannerBodyRef?.nativeElement.querySelectorAll('.PlannerPerson-calendar[data-over-locked]').forEach((cal) => {
                if (cal !== overCal) cal.removeAttribute('data-over-locked');
            });
            overCal?.setAttribute('data-over-locked', '');
        }
        // One note for the whole planner (not one per column, which a
        // narrow column cut short): over any locked column, it follows the
        // pointer — just below the block being dragged (the copy that
        // follows the pointer would cover it otherwise), or just above it
        // when there's no room below on screen — kept within the planner's
        // width. Placed against .Planner itself, as the right-click menu is.
        const note = this.lockedNoteRef?.nativeElement;
        const root = this.plannerRootRef?.nativeElement;
        if (!note || !root) return;
        if (!hovered) {
            note.classList.remove('is-visible');
            return;
        }
        const host = root.getBoundingClientRect();
        const GAP = 24; // from the block's edge to the note's middle
        const mirror = Array.from(document.querySelectorAll('.fc-event-dragging'))
            .find((el) => (el as HTMLElement).style.position === 'fixed') as HTMLElement | undefined;
        const m = mirror?.getBoundingClientRect();
        const top = m ? m.top : y - 20;
        const bottom = m ? m.bottom : y + 20;
        let targetY = bottom + GAP;
        if (targetY > document.documentElement.clientHeight - 24) targetY = top - GAP;
        const half = note.offsetWidth / 2;
        const PAD = 8;
        const targetX = Math.max(host.left + half + PAD, Math.min(host.right - half - PAD, x));
        note.style.left = `${targetX - host.left}px`;
        note.style.top = `${targetY - host.top}px`;
        note.classList.add('is-visible');
    }

    // ── Dragging a block back onto the panel removes it ──
    // Another way to remove a block (as Delete block — the same undo
    // toast). Only blocks that can be dragged at all, i.e. on planners this
    // user may change. While one is held over the open panel, the panel
    // says so (blockOverPanel), and the block following the pointer fades
    // back; dropped there, it's removed instead of springing back.
    blockOverPanel = false;
    private _draggedBlock: { plannedBlockId: number; saving: boolean } | null = null;
    private _droppedOnPanel = false;
    private _mirrorTimer: any = null;

    private isOverPanel(x: number, y: number): boolean {
        if (!this.pulloutOpen) return false;
        const panel = this.plannerBodyRef?.nativeElement.querySelector('.Planner-pullout') as HTMLElement | null;
        if (!panel) return false;
        const r = panel.getBoundingClientRect();
        return x >= r.left && x < r.right && y >= r.top && y < r.bottom;
    }

    private setBlockOverPanel(value: boolean): void {
        if (this.blockOverPanel === value) return;
        this.blockOverPanel = value;
        // The copy FullCalendar moves with the pointer lives in <body> —
        // faded back there while it would be removed (see the global rule
        // in the stylesheet).
        document.body.classList.toggle('PlannerDrag--overPanel', value);
        this._changeRef.markForCheck();
    }

    // Released over the panel (from trackDragHover's pointer-up): the block
    // is going, so FullCalendar's slide back to where it was (it wasn't
    // dropped on a calendar — played before it reports the drag's end) is
    // hidden straight away: the copy following the pointer, and the block
    // itself in its old place.
    private onBlockReleasedOverPanel(): void {
        const block = this._draggedBlock;
        if (!block?.plannedBlockId || block.saving) return;
        this._droppedOnPanel = true;
        clearTimeout(this._mirrorTimer);
        document.body.classList.add('PlannerDrag--removed');
        this.plannerBodyRef?.nativeElement.querySelectorAll('.fc-event:not(.fc-bg-event)').forEach((node) => {
            const el = node as HTMLElement;
            if (getElSeg(el)?.eventRange?.def.extendedProps?.['plannedBlockId'] === block.plannedBlockId) {
                el.style.visibility = 'hidden';
            }
        });
    }

    // The drag's end (after FullCalendar's slide back): released over the
    // panel → removed (the same delete, and undo, as Delete block).
    private onBlockDragStop(_jsEvent: MouseEvent | TouchEvent | undefined): void {
        const block = this._draggedBlock;
        const dropped = this._droppedOnPanel;
        this._droppedOnPanel = false;
        if (!dropped || !block?.plannedBlockId || block.saving) return;
        this.deleteBlock(block.plannedBlockId);
        this._mirrorTimer = setTimeout(() => document.body.classList.remove('PlannerDrag--removed'), 50);
    }

    // While dragging: which person's calendar the pointer is over (only
    // those this user can't change count), checked at most once a frame,
    // and the view only updated when that actually changes.
    private trackDragHover(): void {
        this._dragHoverCleanup?.();
        let frame = 0;
        let x = 0;
        let y = 0;
        const update = () => {
            frame = 0;
            let hovered: string | null = null;
            const calendars = this.plannerBodyRef?.nativeElement.querySelectorAll('.PlannerPerson-calendar[data-user-id]');
            calendars?.forEach((el) => {
                const r = (el as HTMLElement).getBoundingClientRect();
                if (x >= r.left && x < r.right && y >= r.top && y < r.bottom) {
                    const userId = el.getAttribute('data-user-id');
                    if (userId && this.cannotDropOn(userId)) hovered = userId;
                }
            });
            if (hovered !== this.noDropHoverUserId) {
                this._ngZone.run(() => {
                    this.noDropHoverUserId = hovered;
                    this._changeRef.markForCheck();
                });
            }
            this.updateLockedHover(x, y);
            // A calendar block (not a panel card) taken outside the planner:
            // hidden, with the "no" cursor, until it's back (isOutsidePlanner).
            const outside = !!this._draggedBlock && this.isOutsidePlanner(x, y);
            if (outside !== this._dragOutside) {
                this._dragOutside = outside;
                document.body.classList.toggle('PlannerDrag--outside', outside);
            }
            // A calendar block (not a panel card) held over the panel.
            const overPanel = !!this._draggedBlock && this.isOverPanel(x, y);
            if (overPanel !== this.blockOverPanel) {
                this._ngZone.run(() => this.setBlockOverPanel(overPanel));
            }
        };
        const onMove = (ev: Event) => {
            const t = (ev as TouchEvent).touches?.[0];
            x = t ? t.clientX : (ev as MouseEvent).clientX;
            y = t ? t.clientY : (ev as MouseEvent).clientY;
            // Straight away (before FullCalendar's own handling of this
            // move, which checks eventAllow) — see isPointerOverPanel.
            this._dragPointer = { x, y };
            if (!frame) frame = requestAnimationFrame(update);
        };
        // A calendar block released over the panel — caught at the release
        // itself, before FullCalendar's slide back (onBlockReleasedOverPanel).
        const onUp = (ev: Event) => {
            if (!this._draggedBlock) return;
            const t = (ev as TouchEvent).changedTouches?.[0];
            const ux = t ? t.clientX : (ev as MouseEvent).clientX;
            const uy = t ? t.clientY : (ev as MouseEvent).clientY;
            // Before FullCalendar's own drop (eventDrop / eventReceive).
            this._releasedOutside = this.isOutsidePlanner(ux, uy);
            if (this.isOverPanel(ux, uy)) this.onBlockReleasedOverPanel();
        };
        this._ngZone.runOutsideAngular(() => {
            document.addEventListener('mousemove', onMove, true);
            document.addEventListener('touchmove', onMove, true);
            document.addEventListener('mouseup', onUp, true);
            document.addEventListener('touchend', onUp, true);
        });
        this._dragHoverCleanup = () => {
            document.removeEventListener('mousemove', onMove, true);
            document.removeEventListener('touchmove', onMove, true);
            document.removeEventListener('mouseup', onUp, true);
            document.removeEventListener('touchend', onUp, true);
            if (frame) cancelAnimationFrame(frame);
            this._dragOutside = false;
            this._dragPointer = null;
            document.body.classList.remove('PlannerDrag--outside');
            this._dragHoverCleanup = null;
        };
    }

    // ── A block dragged outside the planner ──
    // Blocks only ever go on the calendar (or the panel, to remove them), so
    // outside the planner — over the search bar, the site's top bar or
    // side menu — the block being dragged is hidden (it would show behind
    // those, cut off) with the "no" cursor, and let go there it goes back.
    private _dragOutside = false;
    private _releasedOutside = false;
    // Where the pointer is while a calendar block is dragged (trackDragHover).
    private _dragPointer: { x: number; y: number } | null = null;

    /** A calendar block (not a panel card) being dragged over the panel. */
    private isPointerOverPanel(): boolean {
        const p = this._dragPointer;
        return !!this._draggedBlock && !!p && this.isOverPanel(p.x, p.y);
    }

    /** Outside the planner's own area (the calendar and the panel), as far
     *  as it's on screen — below the pinned search bar and site top bar. */
    private isOutsidePlanner(x: number, y: number): boolean {
        // The panel is always part of it (dropping there removes the block),
        // even where it pokes out past the calendar.
        if (this.isOverPanel(x, y)) return false;
        const body = this.plannerBodyRef?.nativeElement;
        if (!body) return false;
        const r = body.getBoundingClientRect();
        const top = Math.max(r.top, this.stickyChromeHeight());
        const bottom = Math.min(r.bottom, document.documentElement.clientHeight);
        return x < r.left || x >= r.right || y < top || y >= bottom;
    }

    private watchPulloutDrag(e: Event): void {
        this._pulloutDragCleanup?.();
        const card = (e.target as HTMLElement | null)?.closest('.PlannerPullout-item');
        this._dragAction = { jobActionId: Number(card?.getAttribute('data-action-id')) || 0, fromUserId: null };
        const point = (ev: Event) => {
            const t = (ev as TouchEvent).touches?.[0];
            return t ? { x: t.clientX, y: t.clientY } : { x: (ev as MouseEvent).clientX, y: (ev as MouseEvent).clientY };
        };
        const from = point(e);
        const onMove = (ev: Event) => {
            if (this.dragInProgress) return;
            const at = point(ev);
            if (Math.abs(at.x - from.x) + Math.abs(at.y - from.y) > 5) {
                this._ngZone.run(() => this.setDragInProgress(true));
            }
        };
        const onEnd = () => {
            cleanup();
            this._ngZone.run(() => this.setDragInProgress(false));
        };
        const cleanup = () => {
            document.removeEventListener('mousemove', onMove, true);
            document.removeEventListener('touchmove', onMove, true);
            document.removeEventListener('mouseup', onEnd, true);
            document.removeEventListener('touchend', onEnd, true);
            document.removeEventListener('touchcancel', onEnd, true);
            this._pulloutDragCleanup = null;
        };
        this._ngZone.runOutsideAngular(() => {
            document.addEventListener('mousemove', onMove, true);
            document.addEventListener('touchmove', onMove, true);
            document.addEventListener('mouseup', onEnd, true);
            document.addEventListener('touchend', onEnd, true);
            document.addEventListener('touchcancel', onEnd, true);
        });
        this._pulloutDragCleanup = cleanup;
    }

    // Reordering happens within one group at a time (each has its own drop
    // list). The group's new order is merged into the saved order (see
    // savePulloutOrder), then the whole list is re-sorted from it.
    onPulloutReorder(event: CdkDragDrop<JobAction[]>, sectionActions: JobAction[]): void {
        if (event.previousIndex === event.currentIndex) return;
        const items = [...sectionActions];
        moveItemInArray(items, event.previousIndex, event.currentIndex);
        this.savePulloutOrder(items.map((a) => a.jobActionId));
        this.setUnplannedActions(this.unplannedActions);
    }

    // Per browser, per whose planner is showing (same as the zoom level,
    // see ZOOM_KEY) — a Planner-role user ordering someone else's list
    // doesn't reorder their own.
    private pulloutOrderKey(): string | null {
        const userId = this.subjectUserId();
        return userId ? `planner_pullout_order_${userId}` : null;
    }

    private readPulloutOrder(): number[] {
        const key = this.pulloutOrderKey();
        if (!key) return [];
        try {
            const parsed = JSON.parse(localStorage.getItem(key) || '[]');
            return Array.isArray(parsed) ? parsed.filter((id) => typeof id === 'number') : [];
        } catch {
            return [];
        }
    }

    // The list on screen can be narrowed by the page's filters (client,
    // search…), so it's merged into the saved order rather than replacing
    // it — the visible items are re-slotted into the positions they
    // already held, leaving every hidden item exactly where it was.
    private savePulloutOrder(visibleIds: number[]): void {
        const key = this.pulloutOrderKey();
        if (!key) return;
        const saved = this.readPulloutOrder();
        const savedSet = new Set(saved);
        const visibleSet = new Set(visibleIds);
        const combined = [...saved, ...visibleIds.filter((id) => !savedSet.has(id))];
        let next = 0;
        const merged = combined.map((id) => (visibleSet.has(id) ? visibleIds[next++] : id));
        try {
            localStorage.setItem(key, JSON.stringify(merged));
        } catch { /* storage full or unavailable */ }
    }

    // The grey absence overlay belongs to each person shown (their own
    // holidays and non-working days on their own calendar), not always the
    // logged-in user's.
    private loadHolidayData(): void {
        // Open sections only — a collapsed person's are loaded when they're
        // opened (togglePersonCollapsed), so the "All" view, with most
        // sections collapsed, doesn't fetch everyone's at once.
        const userIds = this.people
            .filter((person) => !this.isPersonCollapsed(person.userId))
            .map((person) => person.userId);
        this.loadHolidayDataFor(userIds);
    }

    // These people's grey absence days. Cached from an earlier visit: used
    // straight away (before the calendar's first render on a fresh visit,
    // so no re-render needed then), then quietly refreshed below — and a
    // person's calendar only re-rendered if their refreshed data actually
    // differs. Re-rendering is free either way: planned blocks come from
    // the cache too. Everyone's refreshed in one request (not two per
    // person), as a team or "All" can show many people at once.
    private loadHolidayDataFor(userIds: string[]): void {
        if (!userIds.length) return;
        userIds.forEach((userId) => {
            const absence = this.absenceFor(userId);
            const cachedHolidays = this._plannerCache.getHolidays(userId);
            if (cachedHolidays) {
                absence.holidays = cachedHolidays.holidays;
                absence.bankHolidays = cachedHolidays.bankHolidays;
            }
            const cachedNonWorking = this._plannerCache.getNonWorkingDays(userId);
            if (cachedNonWorking) {
                absence.nonWorkingDays = cachedNonWorking;
            }
            if (cachedHolidays || cachedNonWorking) {
                this.apiFor(userId)?.refetchEvents();
            }
        });

        // Already on its way for someone (e.g. the page opening, then the
        // search bar's filter arriving a moment later and asking again) —
        // that reply serves them; no second request.
        const toLoad = userIds.filter((userId) => !this._absenceLoads.has(userId));
        if (!toLoad.length) return;

        const request = this._plannedBlockService.getPlannerAbsences(toLoad).pipe(
            finalize(() => toLoad.forEach((userId) => this._absenceLoads.delete(userId))),
            shareReplay(1)
        );
        // Lets a calendar's first load wait for these (fetchCalendarEvents),
        // so its grey days arrive together with its blocks.
        // Capped, so a slow reply never holds back the blocks — they draw
        // anyway, and the grey days follow when it lands (refetch below).
        const done = request.pipe(
            timeout(PlannerComponent.ABSENCE_WAIT_MS),
            map(() => undefined),
            catchError(() => of(undefined))
        );
        toLoad.forEach((userId) => this._absenceLoads.set(userId, done));

        this.subscription.add(
            request.subscribe({
                next: (result) => {
                    const bankHolidays = result.bankHolidays || [];
                    const byUser = new Map((result.users || []).map((u) => [u.userId, u] as [string, typeof u]));
                    toLoad.forEach((userId) => {
                        const forUser = byUser.get(userId);
                        const holidays = { holidays: forUser?.holidays || [], bankHolidays };
                        const nonWorkingDays = forUser?.nonWorkingDays || [];
                        this._plannerCache.setHolidays(userId, holidays);
                        this._plannerCache.setNonWorkingDays(userId, nonWorkingDays);
                        if (this._destroyed) return;
                        // Compared with what's on screen (not the cache, which
                        // an earlier reply may already have filled without it
                        // ever being shown) — any person's own data is right
                        // to show whenever it arrives.
                        const current = this.absenceFor(userId);
                        const changed = JSON.stringify(holidays.holidays) !== JSON.stringify(current.holidays)
                            || JSON.stringify(holidays.bankHolidays) !== JSON.stringify(current.bankHolidays)
                            || JSON.stringify(nonWorkingDays) !== JSON.stringify(current.nonWorkingDays);
                        if (!changed) return;
                        current.holidays = holidays.holidays;
                        current.bankHolidays = holidays.bankHolidays;
                        current.nonWorkingDays = nonWorkingDays;
                        // A first load that's waiting for this draws it anyway.
                        if (!this._awaitingAbsences.has(userId)) this.apiFor(userId)?.refetchEvents();
                    });
                },
                // Leaves whatever was cached showing.
                error: () => {},
            })
        );
    }

    // Absence requests in flight, per person (see loadHolidayDataFor), and
    // the people whose calendar's first load is waiting for theirs.
    private _absenceLoads = new Map<string, Observable<void>>();
    private static readonly ABSENCE_WAIT_MS = 8000;
    private _awaitingAbsences = new Set<string>();

    private absenceFor(userId: string): PersonAbsence {
        let absence = this._absenceByUser.get(userId);
        if (!absence) {
            absence = { holidays: [], bankHolidays: [], nonWorkingDays: [] };
            this._absenceByUser.set(userId, absence);
        }
        return absence;
    }

    private backgroundEventsForRange(start: Date, end: Date, userId: string): EventInput[] {
        const events: EventInput[] = [];
        const cursor = new Date(start);
        cursor.setHours(0, 0, 0, 0);
        const rangeEnd = new Date(end);

        while (cursor < rangeEnd) {
            const day = this.absenceForDate(cursor, userId);
            const dayStart = new Date(cursor);
            const morningOffEnd = new Date(cursor);
            morningOffEnd.setHours(PlannerComponent.MORNING_OFF_UNTIL[0], PlannerComponent.MORNING_OFF_UNTIL[1], 0, 0);
            const afternoonOffStart = new Date(cursor);
            afternoonOffStart.setHours(PlannerComponent.AFTERNOON_OFF_FROM[0], PlannerComponent.AFTERNOON_OFF_FROM[1], 0, 0);
            const dayEnd = new Date(cursor);
            dayEnd.setDate(dayEnd.getDate() + 1);
            const shade = (kind: AbsenceKind, from: Date, to: Date, part: 'day' | 'am' | 'pm') => events.push({
                start: from,
                end: to,
                display: 'background',
                classNames: ['Planner-absence', `Planner-absence--${kind}`],
                extendedProps: { isAbsence: true, label: this.absenceTooltip(kind, part) },
            });
            if (day.morning && day.morning === day.afternoon) {
                shade(day.morning, dayStart, dayEnd, 'day');
            } else {
                if (day.morning) shade(day.morning, dayStart, morningOffEnd, 'am');
                if (day.afternoon) shade(day.afternoon, afternoonOffStart, dayEnd, 'pm');
            }
            cursor.setDate(cursor.getDate() + 1);
        }

        return events;
    }

    // Half-day absences, per the company handbook's half-day holiday
    // timings: a morning off means starting work by 13:45, an afternoon off
    // means finishing by 12:45 — so those are the times shaded as away.
    private static readonly MORNING_OFF_UNTIL: [number, number] = [13, 45];
    private static readonly AFTERNOON_OFF_FROM: [number, number] = [12, 45];

    // Who's away when, for one day: morning and afternoon separately, as a
    // half-day holiday only covers one of them (isMorning). Same reading of
    // the holiday rows as the Holidays page's grid: a booking of a day or
    // more covers the whole of every day it spans, under a day covers the
    // morning or the afternoon. A holiday wins over a bank holiday, which
    // wins over a non-working day.
    private absenceForDate(date: Date, userId: string): { morning: AbsenceKind | null; afternoon: AbsenceKind | null } {
        const { holidays, bankHolidays, nonWorkingDays } = this.absenceFor(userId);
        let morning: AbsenceKind | null = null;
        let afternoon: AbsenceKind | null = null;

        const isNonWorking = nonWorkingDays.some((nwd) => {
            const first = new Date(nwd.firstDay);
            first.setHours(0, 0, 0, 0);
            const last = nwd.lastDay ? new Date(nwd.lastDay) : null;
            if (last) last.setHours(0, 0, 0, 0);
            if (date < first || (last && date > last)) return false;

            switch (date.getDay()) {
                case 1: return nwd.monday;
                case 2: return nwd.tuesday;
                case 3: return nwd.wednesday;
                case 4: return nwd.thursday;
                case 5: return nwd.friday;
                default: return false;
            }
        });
        if (isNonWorking) morning = afternoon = 'nonworking';

        const isBankHoliday = bankHolidays.some((bh) => {
            const d = new Date(bh.date);
            return d.getFullYear() === date.getFullYear()
                && d.getMonth() === date.getMonth()
                && d.getDate() === date.getDate();
        });
        if (isBankHoliday) morning = afternoon = 'bank';

        holidays.forEach((h) => {
            const start = new Date(h.start);
            start.setHours(0, 0, 0, 0);
            const last = new Date(h.lastDay || h.start);
            last.setHours(0, 0, 0, 0);
            if (date < start || date > last) return;
            const kind = this.holidayKind(h);
            if (h.duration >= 1) {
                morning = afternoon = kind;
            } else if (h.isMorning) {
                morning = kind;
            } else {
                afternoon = kind;
            }
        });

        return { morning, afternoon };
    }

    // Sick leave and other absences are only told apart for people with
    // the Calendar role, the same as the Holidays page (the server sends
    // everyone else every absence as annual leave anyway). Without it, an
    // absence is just "Off" — rather than calling a sick day a holiday.
    private holidayKind(h: Holiday): AbsenceKind {
        if (!this._loginService.checkAuth(['Calendar'])) return 'off';
        switch (h.holidayType) {
            case HolidayType.Illness: return 'sick';
            case HolidayType.Other: return 'other';
            default: return 'holiday';
        }
    }

    private static readonly ABSENCE_NAMES: Record<AbsenceKind, string> = {
        holiday: 'Holiday',
        sick: 'Sick',
        other: 'Other absence',
        off: 'Off',
        bank: 'Bank holiday',
        nonworking: 'Non-working day',
    };
    // Shorter, for the day header's small label.
    private static readonly ABSENCE_SHORT_NAMES: Record<AbsenceKind, string> = {
        holiday: 'Holiday',
        sick: 'Sick',
        other: 'Other',
        off: 'Off',
        bank: 'Bank hol',
        nonworking: 'Non-working',
    };

    // Hover text on the shaded part of the day.
    private absenceTooltip(kind: AbsenceKind, part: 'day' | 'am' | 'pm'): string {
        const name = PlannerComponent.ABSENCE_NAMES[kind];
        return part === 'am' ? `${name} (morning)` : part === 'pm' ? `${name} (afternoon)` : name;
    }

    // The day header's small label for a day with an absence ("Holiday",
    // "Sick AM", "Off PM"…), or '' for none.
    private absenceHeaderHtml(date: Date, userId: string): string {
        const { morning, afternoon } = this.absenceForDate(date, userId);
        if (!morning && !afternoon) return '';
        const parts: { kind: AbsenceKind; text: string; title: string }[] = [];
        const short = PlannerComponent.ABSENCE_SHORT_NAMES;
        if (morning && morning === afternoon) {
            parts.push({ kind: morning, text: short[morning], title: this.absenceTooltip(morning, 'day') });
        } else {
            if (morning) parts.push({ kind: morning, text: `${short[morning]} AM`, title: this.absenceTooltip(morning, 'am') });
            if (afternoon) parts.push({ kind: afternoon, text: `${short[afternoon]} PM`, title: this.absenceTooltip(afternoon, 'pm') });
        }
        return parts
            .map((p) => `<span class="Planner-dayAbsence Planner-dayAbsence--${p.kind}" title="${p.title}">${p.text}</span>`)
            .join('');
    }

    private renderDayHeader(date: Date, userId: string): { html: string } {
        const dayAbbr = date.toLocaleDateString(undefined, { weekday: 'short' }).toUpperCase();
        const dayNum = date.getDate();
        const key = this.dateKey(date);
        const draftMinutes = (this._draftTotalsByUser.get(userId) || {})[key] || 0;
        // " planned" only shows where it fits beside the hours (it wraps onto
        // a hidden line otherwise — see .Planner-dayTotal), and never on
        // small screens (see .Planner-dayTotalSuffix).
        const totalLabel = draftMinutes
            ? `<span>${formatHours(draftMinutes)}h</span><span class="Planner-dayTotalSuffix">planned</span>`
            : '<span>0h</span>';
        const absence = this.absenceHeaderHtml(date, userId);
        // A past day (locked — see isPastDay): quieter, with a small padlock
        // beside its name, and says so on hover.
        const past = this.isPastDay(date);
        return {
            html: `<div class="Planner-dayHeader${past ? ' Planner-dayHeader--past' : ''}"${past ? ' title="This day has passed and is locked"' : ''}>`
                + `<div class="Planner-dayNum">${dayNum}</div>`
                + `<div class="Planner-dayInfo">`
                + `<div class="Planner-dayAbbr">${dayAbbr}${past ? PLANNER_DAY_LOCK_ICON : ''}</div>`
                + `<div class="Planner-dayTotal">${totalLabel}</div>`
                + `</div>`
                + `</div>`
                + (absence ? `<div class="Planner-dayAbsences">${absence}</div>` : ''),
        };
    }

    // A person's planned minutes per day (day header totals, and the week
    // total in their section header), from the events FullCalendar hands
    // eventsSet. Not looked up through apiFor: with the blocks already
    // remembered (planner cache), FullCalendar is given them as the
    // calendar is first created, before Angular has listed it
    // (_calendarList), so the lookup found nothing and every total stayed
    // at 0h, until a refresh loaded the blocks afresh (a moment later).
    private recomputeDraftTotals(userId: string, events: EventApi[]): void {
        const totals: Record<string, number> = {};
        events.forEach((ev) => {
            const isPlannedBlock = ev.extendedProps?.['plannedBlockId'] || ev.extendedProps?.['isSaving'];
            if (!isPlannedBlock || !ev.start || !ev.end) return;
            const key = this.dateKey(ev.start);
            const minutes = (ev.end.getTime() - ev.start.getTime()) / 60000;
            totals[key] = (totals[key] || 0) + minutes;
        });
        this._draftTotalsByUser.set(userId, totals);
        // A FullCalendar callback — the section header's week total needs a
        // re-check under the OnPush Actions page (see openContextMenu).
        if (this.isSeveralPeople) this._changeRef.markForCheck();
        // Also runs whenever their absences change (those are events too —
        // see backgroundEventsForRange), so the day headers' totals and
        // absence badges are brought up to date from here.
        this.refreshDayHeaders(userId);
    }

    // FullCalendar draws a day header (renderDayHeader) once, for the dates
    // on screen, and doesn't redraw it when only the events change — so
    // totals and absence badges that arrive afterwards (blocks loading, a
    // block moved, holidays loading) never reached it. This redraws each of
    // the person's day headers in place from the current data, the same
    // HTML FullCalendar itself would draw. Once per moment per person.
    private _headerRefreshQueued = new Set<string>();
    private refreshDayHeaders(userId: string): void {
        if (this._headerRefreshQueued.has(userId)) return;
        this._headerRefreshQueued.add(userId);
        setTimeout(() => {
            this._headerRefreshQueued.delete(userId);
            if (this._destroyed) return;
            const root = this.plannerBodyRef?.nativeElement
                .querySelector(`.PlannerPerson-calendar[data-user-id="${userId}"]`) as HTMLElement | null;
            root?.querySelectorAll('.fc-col-header-cell[data-date]').forEach((cell) => {
                const cushion = cell.querySelector('.fc-col-header-cell-cushion');
                const [y, m, d] = ((cell as HTMLElement).dataset['date'] || '').split('-').map(Number);
                if (!cushion || !y || !m || !d) return;
                const html = this.renderDayHeader(new Date(y, m - 1, d), userId).html;
                if (cushion.innerHTML !== html) cushion.innerHTML = html;
            });
        }, 0);
    }

    private updateToolbarLabel(start: Date): void {
        // Day view: "Fri 9 Oct · W41", and Today/Yesterday highlighted in
        // the date picker's shortcuts when they apply.
        if (this.viewMode === 'day') {
            this.weekLabel = start.toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' });
            this.weekNumber = getISOWeekNumber(start);
            this.selectedWeekStart = getMondayOf(start);
            const today = new Date();
            this.selectedQuickOption = isSameDay(start, today) ? 'today'
                : isSameDay(start, addDays(today, -1)) ? 'yesterday'
                : '';
            return;
        }
        const end = addDays(start, this.viewMode === '5days' ? 4 : 6); // 5 days: Mon–Fri
        const fmt = (d: Date) => d.toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
        this.weekLabel = `${fmt(start)} – ${fmt(end)}`;
        this.weekNumber = getISOWeekNumber(start);
        this.selectedWeekStart = start;

        const thisMonday = getMondayOf(new Date());
        const lastMonday = addDays(thisMonday, -7);
        if (isSameDay(start, thisMonday)) {
            this.selectedQuickOption = 'thisWeek';
        } else if (isSameDay(start, lastMonday)) {
            this.selectedQuickOption = 'lastWeek';
        } else {
            this.selectedQuickOption = '';
        }
    }

    private dateKey(date: Date): string {
        return `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
    }
}
